/**
 * Pokedex screenshot OCR.
 *
 * Reads a single Pokedex page screenshot from the user's photo library
 * and returns a structured `PokedexGridFrame` that downstream code can
 * persist to the server (per /home/user/workspace/pogo_screen_ingest_spec.md
 * §6). Designed for the user-scrolls-and-screenshots workflow: each frame
 * captures 5-6 rows; the caller is responsible for merging frames into a
 * full-region scan.
 *
 * Implementation notes:
 *  - Tesseract.js is lazy-loaded; this module ships as a small (sub-30 KB
 *    gzip) lazy chunk that pulls the wasm binary on first call.
 *  - Crop regions are normalized 0-1 and calibrated against 19.5:9 phones.
 *  - Active tab + filter detection uses both pixel luminance sampling AND
 *    OCR of the highlighted strip; we prefer the OCR text when the
 *    confidence > 70, fall back to luminance otherwise.
 *  - Progress bar parsing rejects pairs where total exceeds the per-region
 *    max (per spec §13) and yields `progressSeen: null` instead, so the
 *    server never persists obvious OCR garbage.
 */

// ── Types ─────────────────────────────────────────────────────────────────

export type PokedexRegion =
  | "kanto" | "johto" | "hoenn" | "sinnoh" | "unova"
  | "kalos" | "alola" | "galar" | "hisui" | "paldea"
  // GO's region-less grouping for Meltan #808 / Melmetal #809.
  | "unidentified" | "unknown";

export type PokedexFilter =
  | "seen" | "caught" | "shiny" | "lucky" | "shadow"
  | "purified" | "three_star" | "xxl" | "xxs";

export interface PokedexCellData {
  dexNumber: number | null;
  /**
   * Where the dex number sits in the card (2026-09-24): "center" is Pokémon
   * GO's blank-card layout (registered, not caught), "bottom" sits under a
   * sprite. Independent of the sprite classifier, so the self-correction uses
   * it as its blank-card evidence. Absent on replays that predate the field.
   */
  numberPosition?: string;
  /** The verdict came from the card band above the Vision number box. */
  numberAnchored?: boolean;
  /**
   * PR-E6-OCR2: the per-cell species-name OCR was removed in favour of dex
   * number + sprite chroma match. Field kept for back-compat with callers
   * that still log it; always null on freshly-extracted frames.
   */
  speciesName: string | null;
  isNew: boolean;
  rowIndex: number;
  colIndex: number;
  /**
   * Whether this cell reads as CAUGHT. PR-E6-OCR2: now driven by the
   * sprite chroma matcher (cellSpriteMatcher.matchCellToSprite). Replaces
   * the prior hue-variance silhouette classifier when a dex number was
   * read for the cell.
   */
  caught: boolean;
  /**
   * PR #194 (b17b): whether the cell reads as at-least-SEEN (number pinned to
   * the bottom of the cell, sprite present or silhouetted) vs unseen (number
   * vertically centred in a blank frame). A caught cell is always seen.
   */
  seen?: boolean;
  /**
   * PR-E6-OCR2: sprite matcher verdict for this cell, when the dex number
   * was read and a sprite match was attempted. Surfaced for the upcoming
   * apply gate (PR-E6-OCR3).
   */
  spriteVerdict?: "caught" | "missing" | "uncertain";
  /**
   * PR-E6-OCR2: sprite matcher confidence in [0, 1]. Used by the apply
   * gate to decide whether to write the cell vs queue it for review.
   */
  spriteConfidence?: number;
  /**
   * Legacy pixel-classifier sprite state for THIS cell ("empty" | "silhouette" |
   * "caught" | "none"), as logged in `[dex-scan] cell classify`. Threaded onto
   * the cell so the live write layer (`adaptDexLiveFrame`) can enforce the
   * empty/silhouette present-write veto at the SINGLE apply chokepoint — the
   * per-cell bug-A gate is bypassed on the cache-replay path, so the veto must
   * also exist where observations become writes. `undefined` on cells that had
   * no classification (upload / no-canvas), preserving back-compat.
   */
  spriteState?: SpriteState | "none";
  /**
   * Sprite-matcher reason for THIS cell's verdict (e.g. "local-presence",
   * "chroma-match", "aura-present"). Used with `spriteState` by the live write
   * veto so a presence-heuristic catch on an empty/silhouette cell can never
   * write present. `undefined` on cells with no matcher verdict.
   */
  spriteReason?: string | null;
  /**
   * Presence-classifier chroma fraction for THIS cell in [0, 1] (logged in
   * `[dex-scan] cell classify` as `spriteChromaFraction`) — the fraction of the
   * sprite region that is chromatic (coloured) rather than monochrome, from
   * `classifyCellPresence`. `undefined` on cells with no presence verdict
   * (upload / no-canvas). Distinct from `DexCellClassification.chromaFraction`,
   * the b18f empty-test fraction only populated for the no-silhouette variants.
   */
  spriteChromaFraction?: number;
  /**
   * Legacy pixel-classifier foreground pixel count for THIS cell (logged in
   * `[dex-scan] cell classify` as `foregroundPixels`). Threaded onto the cell so
   * the live write layer can require a real sprite's pixel MASS under the aura: a
   * pure-aura phantom carries only ~510-779 px (its dex glyph + glow) whereas a
   * real sprite fills the cell body (2300+ px). `undefined` on cells with no
   * classification (upload / no-canvas).
   */
  spriteForegroundPixels?: number;
  /** foregroundPixels over the sprite band area: the resolution-independent mass. */
  spriteForegroundFraction?: number;
  /**
   * Legacy pixel-classifier hue variance for THIS cell (DexCellClassification.
   * hueVariance, logged in `[dex-scan] cell classify` as `hueVariance`). Threaded
   * onto the cell (PR #533) so the live write layer's per-variant silhouette
   * write-veto floor (`HUE_VARIANCE_FLOOR`) can compare the cell's hue spread
   * against the variant's confirmed silhouette ceiling. `null`/`undefined` on
   * cells with no classification (upload / no-canvas).
   */
  spriteHueVariance?: number | null;
  /**
   * PRESENCE-CLASSIFIER BODY STATISTICS. `classifyCellPresence` already computes
   * both — the features the whole caught/silhouette/empty decision is built on —
   * but until now they stopped at the classify telemetry line and never reached
   * the cell, so the live write layer's only view of "how much sprite is there"
   * was the raw `spriteForegroundPixels` count. `bodyFrac` is that same body
   * measurement normalised by ROI area (device-independent, unlike a raw pixel
   * count) with the dex-number box masked out, and `lumStd` is the flat-fill-vs
   * -real-sprite luminance discriminator — the STRUCTURE signal that separates a
   * dim same-hue catch from a blank-cell background bleed where chroma cannot.
   * Both `undefined` on cells with no presence classification (upload / no-canvas).
   */
  spriteBodyFrac?: number;
  spriteLumStd?: number;
  /**
   * Fraction (0..1) of THIS cell's ROI covered by the Nexus PiP overlay
   * (`computePillOverlapFraction`, logged in `[dex-scan] cell classify` as
   * `pillOverlapFraction`). Threaded onto the cell so the live write layer can
   * pass it to the session's PiP-overlap guard (scan overhaul v2, Phase 4): a
   * cell whose sprite ROI is >15% PiP-blended runs the classifier on corrupted
   * pixels and defers its apply to a later frame. `undefined` when no pill was
   * detected (upload / no-canvas).
   */
  pillOverlapFraction?: number;
  /**
   * True when this cell's verdict was REPLAYED from the per-cell content cache
   * rather than classified from this frame's pixels. The cache keys on a hash of
   * the cell's number band plus its rect, so a hit means the pixels did not
   * change — the verdict is the previous one, not a second opinion.
   *
   * Anything that treats repeated reads as corroboration must ignore cached
   * ones: two identical images agreeing tells you nothing. `undefined` on paths
   * with no cache (upload / no-canvas), which callers read as fresh.
   */
  classificationCached?: boolean;
}

export interface PokedexGridFrame {
  activeRegion: PokedexRegion;
  activeFilter: PokedexFilter;
  progressSeen: number | null;
  progressTotal: number | null;
  cells: PokedexCellData[];
  confidence: number;       // 0-100, average over OCR bands
  warnings: PokedexWarning[];
  /**
   * How `activeRegion` was resolved: "label" (visible region header),
   * "tab_text", "tab_column" (brightest selected tab fallback), or
   * "unresolved". Diagnostic only — never used to apply state.
   */
  regionReason?: string;
  /** Candidate cells whose number ROI was masked by the PiP overlay. */
  pipObscuredCells?: number;
  /**
   * Candidate cells skipped because the Nexus live-scan overlay card obscured
   * their number ROI (geometry overlap or HUD-marker text). Diagnostic only —
   * a skipped cell never applies, preventing the overlay's "Added: #…" text from
   * becoming phantom dex observations.
   */
  nexusOverlayObscuredCells?: number;
  /** Candidate cells skipped because their sprite body sat in the bottom-UI band
   *  (PoGo search / ✕ / REGIONS / filter pills). Diagnostic only. */
  bottomUiObscuredCells?: number;
  /** Cells in the TOP grid row, which is never scanned — the Nexus PiP card
   *  covers it. Diagnostic only; see the bail in the cell loop. */
  topRowSkippedCells?: number;
  /**
   * How many cell number-ROIs this frame batched into its ONE native OCR call
   * (see runCellNumberOcrBatch). This is the direct measure of the per-frame
   * native call saving — pre-batch it was one call PER ROI — and it is
   * aggregated into the session `timing snapshot` rather than logged per frame,
   * so it lands in a device trace without needing DEX_SCAN_VERBOSE.
   */
  numberRoiBatchSize?: number;
  /**
   * Wall-clock ms this frame spent SUSPENDED at a main-thread yield (see
   * yieldToEventLoop). Reported separately because the yields make the frame's
   * own `totalMs` non-comparable across the 2026-08-11 change: with two frames
   * in flight, a yield lets the OTHER frame run, so the suspended time lands
   * inside this frame's elapsed measurement. `totalMs - cellLoopYieldMs` is the
   * frame's own work.
   */
  cellLoopYieldMs?: number;
  /**
   * Push 8 / v1.4.3 b8: the Pokémon GO top-panel reading + decision (region +
   * `X / Y` main count + optional variant counts). This is the trusted
   * summary the app uses to override a stale region guess and to decide
   * whether to bulk-complete a region or narrow to "find N missing". Optional
   * so older deserialized frames are unaffected.
   */
  topPanel?: TopPanelDecision;
  /**
   * PR-C Problem 1 — Signal A. OCR text lines cropped from the bottom variant-
   * tab-pill band (POKÉMON / SHINY / LUCKY / XXL / XXS / PERFECT). Populated by
   * the native broadcast layer when the b5 extension is installed; absent on
   * web and older binaries, where the variant vote falls back to the legacy
   * filter keyword + sticky last-known. Optional so older frames deserialize.
   */
  tabPillLines?: TabPillLine[];
  /**
   * PR-C Problem 1 — Signal B. Dominant tint sampled from the upper ~60% of the
   * dex grid (native layer). Optional for the same reason as `tabPillLines`.
   */
  tintSample?: TintSample;
  /**
   * Build 2 / v1.4.6 — PoGo-side variant signal read from the filter-chip row
   * directly above the 4×4 grid. This is the source-of-truth for which dex the
   * user is *scanning* (vs the Nexus page they happen to be viewing). Chip-text
   * OCR is preferred; dominant-hue is the fallback. Optional so older
   * deserialized frames are unaffected.
   */
  variantChip?: VariantChipSignal;
  /**
   * colour-narrowed pill re-read (2026-06-19) — variant matched by a WIDE,
   * colour-narrowed pill OCR re-read when the active-pill classifier failed
   * (`variantChip.source === "none"`) on a MULTI-variant colour family. Matched
   * ONLY against the page family's allowed tokens, so it can never be an
   * impossible variant. Null when the active-pill read succeeded, the family was
   * single-variant (resolved directly by the adapter), or no allowed token was
   * read. Threaded to `adaptDexLiveFrame` as `colourNarrowedVariant`.
   */
  colourNarrowedVariant?: PokedexVariant | null;
  /**
   * Vision-only trial: the VETO-ONLY page-colour family derived internally this
   * frame from the full-frame RGBA (deriveVetoOnlyFamily). Replaces the deleted
   * pre-OCR palette gate's colour tracker as the family the ROUTING-side veto
   * (resolveTrustedVariant / adapter) consults. Null when the backdrop matches
   * no dex family. Corroboration-only — never positively selects a variant.
   */
  pageColourFamily?: DexPageColourFamily | null;
  /**
   * PR-E6-OCR2.5-gmax (hardening): the grid column count the per-frame sweep
   * ACTUALLY used (`gridColumnsForVariant(frameVariant)` — 3 for G-Max, 4
   * otherwise). Exposed so the live-frame adapter derives its slot-index stride
   * from the SAME trusted column count the geometry was cropped with, instead of
   * a hardcoded 4. Without this a G-Max (3-col) frame is cropped correctly here
   * but slot-indexed as if it were 4-col downstream — every row past the first
   * picks up an off-by-(row) slot error. Optional so older deserialized frames /
   * the not-pokedex and aggregate return paths (which never ran a sweep) are
   * unaffected; the adapter treats `undefined` as the 4-col default.
   */
  gridColumns?: number;
  /**
   * Run-3 false-write hardening (2026-06-21). The concatenated header/region/
   * filter/variant-row OCR text read for THIS frame, used by the live write
   * chokepoint (`adaptDexLiveFrame`) to block frames whose global OCR sampled
   * the Nexus overlay/status HUD ("Scanning", "Hold steady", "dex entries",
   * "Added:", "NEXUS DEX"). The device run proved the geometric overlap check
   * (`nexusOverlayObscured:0`) is not sufficient on its own — the card can be
   * parked where the rect detector misses it yet its text still OCRs. Optional
   * so older deserialized frames / the upload path are unaffected.
   */
  globalOcrText?: string;
}

/**
 * Build 2 / v1.4.6 — result of sampling the PoGo filter-chip row above the
 * grid. `variant` is null when neither text nor hue resolved confidently.
 */
export interface VariantChipSignal {
  /** Which cue resolved the variant. */
  source: "chip-text" | "hue" | "none";
  /** Resolved variant, or null when inconclusive. */
  variant: PokedexVariant | null;
  /** 0..1 confidence. Callers SKIP the write below ~0.6 rather than defaulting. */
  confidence: number;
  /**
   * Fine-grained provenance of the read, preserved verbatim from the
   * activePillClassifier decision ("active-pill-text" / "active-pill-text-strong"
   * / "active-pill-neighbours" / "hue-fallback" / "none"). The coarse `source`
   * field above collapses these to "chip-text"/"hue"/"none" for the legacy apply
   * gate; this field keeps the distinction so the unified resolver can trust a
   * DIRECT active-pill-strip label read (the apply-back authority) over a coarse
   * page-tint colour veto without lowering the global veto-override bar.
   */
  activePillSource?: string;
  /**
   * True when an independent hue check agreed with the chip-text variant read.
   * Two agreeing signals are stronger evidence than one, so a hue-corroborated
   * chip-text read clears a lower authority bar in resolveTrustedVariant.
   */
  hueAgrees?: boolean;
  /**
   * True when this read came from the GEOMETRY-FAILED flat-band fallback in
   * `classifyActivePillBand` (one OCR over the whole pill row) rather than the
   * primary geometric active-pill locator. A flat-band read cannot tell WHICH
   * pill is the active one — it just sees the whole strip ("POKÉMON SHINY
   * LUCKY") and `classifyVariantChip` first-match-wins picks one. The
   * still-image upload commits a SINGLE frame after confirm, so the screenshot
   * resolver (`resolveScreenshotScanVariant`) refuses to auto-trust a
   * `bandFallback` read and requires explicit user confirmation instead.
   *
   * 2026-08-30 audit: the live path used to "tolerate" the flag on the theory
   * that multi-frame voting recovers a bad frame — but the corroboration
   * engine that doc described was deleted (Tier-1 commits on ONE frame now),
   * and classifyVariantChip floors its confidence at 0.7 and can stamp 0.99,
   * which beats even the colour-veto-override bar. The live adapter now CAPS a
   * bandFallback read's confidence below CHIP_TEXT_VETO_OVERRIDE_THRESHOLD:
   * a whole-strip guess may still win normal authority resolution, but can
   * never outrank the page-colour veto.
   */
  bandFallback?: boolean;
}

export type PokedexWarning =
  | "not_pokedex_screenshot"
  // 2026-09-18: the STORAGE BOX (a grid of caught Pokémon, "CP 282" over each)
  // read through the dex-number ROIs. Always paired with not_pokedex_screenshot.
  | "storage_box"
  | "progress_out_of_range"
  | "active_region_unknown"
  | "active_filter_unknown"
  | "low_confidence"
  // Fix B: the unified resolver returned HOLD/SKIP (app_state colour-vetoed and
  // chip-text could not supply an allowed variant). The frame committed nothing.
  | "variant_unstable"
  // 2026-10-23: the frame's width/height is outside the band the grid lattice
  // was calibrated on (lib/frameGeometry: 20:9 to 19:9 phones). An iPad (4:3)
  // or a 16:9 phone lands here. The frame is still read with the phone
  // lattice, so the warning marks reads to distrust and gives the first tablet
  // scan log the shape it needs for a calibration.
  | "uncalibrated_frame_aspect";

// Per spec §13: per-region "max possible" totals. The server validates
// using the same numbers; we duplicate them client-side so we can reject
// nonsense from OCR before it reaches the network.
const REGIONAL_MAX: Record<PokedexRegion, number> = {
  kanto: 151, johto: 100, hoenn: 135, sinnoh: 107, unova: 156,
  kalos: 72, alola: 86, galar: 89, hisui: 7, paldea: 120,
  // Meltan #808 / Melmetal #809 — GO's "Unidentified" grouping (alola stays 86
  // because these two are carved out of its range, not counted under it).
  unidentified: 2, unknown: 1025,
};

const REGION_KEYWORDS: Array<[PokedexRegion, RegExp]> = [
  ["kanto",  /\bkanto\b/i],
  ["johto",  /\bjohto\b/i],
  ["hoenn",  /\bhoenn\b/i],
  ["sinnoh", /\bsinnoh\b/i],
  ["unova",  /\bunova\b/i],
  ["kalos",  /\bkalos\b/i],
  ["alola",  /\balola\b/i],
  ["galar",  /\bgalar\b/i],
  ["hisui",  /\bhisui\b/i],
  ["paldea", /\bpaldea\b/i],
  ["unidentified", /\bunidentified\b/i],
];

// The Pokémon GO Pokédex tab strip is "All" followed by the ten regions in
// National Dex order. detectActiveColumn() returns the bright (selected) tab
// index 0..10; column 0 is the "All" tab (national, no single region). This
// mapping lets us recover a region from the highlighted tab when OCR cannot
// read the label glyphs (dark theme / stylised font) — far better than
// silently falling back to "unknown".
export const TAB_COLUMN_REGIONS: PokedexRegion[] = [
  "unknown", // 0 = "All"
  "kanto", "johto", "hoenn", "sinnoh", "unova",
  "kalos", "alola", "galar", "hisui", "paldea",
];

const FILTER_KEYWORDS: Array<[PokedexFilter, RegExp]> = [
  ["shiny",      /\bshiny\b/i],
  ["lucky",      /\blucky\b/i],
  ["shadow",     /\bshadow\b/i],
  ["purified",   /\bpurified\b/i],
  ["three_star", /\b3[\s-]?star|three[-\s]?star|100\b/i],
  ["xxl",        /\bxxl\b/i],
  ["xxs",        /\bxxs\b/i],
  ["caught",     /\bcaught\b/i],
  ["seen",       /\bseen\b|\bpok[eé]mon\b/i],
];

// ── Image helpers ─────────────────────────────────────────────────────────

function loadImage(dataUrl: string): Promise<HTMLImageElement> {
  // One decode per frame, shared by every consumer (lib/frameImageCache).
  return loadFrameImage(dataUrl);
}

function cropToDataUrl(
  img: HTMLImageElement,
  xPct: number, yPct: number, wPct: number, hPct: number,
): string {
  const x = Math.floor(img.width * xPct);
  const y = Math.floor(img.height * yPct);
  const w = Math.max(1, Math.floor(img.width * wPct));
  const h = Math.max(1, Math.floor(img.height * hPct));
  const canvas = document.createElement("canvas");
  canvas.width = w;
  canvas.height = h;
  const ctx = canvas.getContext("2d", CANVAS_READBACK);
  if (!ctx) return "";
  ctx.drawImage(img, x, y, w, h, 0, 0, w, h);
  return canvas.toDataURL("image/png");
}

// ── Fix A — short-label pill-slot OCR pre-pass ──────────────────────────────
//
// Apple Vision returns "" for the SHORT pill labels (XXL/XXS/"100%"/LUCKY): a
// naive 1/3-width band crop at FILTER_PILL_ROW_H=0.07 leaves a 2–4 char glyph
// only a few-dozen pixels tall, below Vision's reliable floor. LONG labels
// (PURIFIED/MEGA/SHADOW) read fine at the same scale. The fix is OCR-faithfulness
// only — text stays the SOLE authority (no colour selection, PILL_TOKENS regexes
// untouched): upscale each slot crop, pad it modestly so a centred short label is
// never clipped, and apply a conservative grayscale + contrast/threshold pass so
// thin glyphs survive. A long token passes through the SAME helper unaffected
// (upscaling a readable label does not regress it).

/** Linear upscale applied to each pill-slot crop before OCR (aspect preserved). */
export const PILL_SLOT_OCR_UPSCALE = 3;
/**
 * Horizontal padding added to each side of a slot crop, as a fraction of the
 * slot width, so a short centred label isn't clipped by the naive 1/3 split.
 * Clamped to the band so the crop never leaves the pill row.
 */
export const PILL_SLOT_OCR_PAD_X_FRAC = 0.08;
// Vision-only trial (trial/vision-only-no-colour): PILL_SLOT_OCR_LUMA_THRESHOLD
// and PILL_SLOT_OCR_CONTRAST were deleted along with preprocessSlotOcrRgba. The
// fixed-luma binarisation is what broke iPhone 12 pill reads under True Tone
// white-point drift; Vision reads the raw (upscaled, padded) crop directly.



/**
 * Round-7 zero-histSim local-presence demotion (device Kanto Purified scan).
 *
 * The PRIMARY classifier's `local-presence` "caught" verdict fires on edge
 * density + presence confidence ALONE — it does not require any species
 * histogram match. Telemetry from the Purified scan showed 42 cells (e.g. #024)
 * reaching `finalClassification:"caught"` via `local-presence` with
 * `spriteHistSim` EXACTLY 0 (edgeDensity ~0.047, confidence 0.9): a cell whose
 * crop matched NO species sprite histogram at all is a silhouette/outline with
 * incidental edge noise, not a real caught sprite. REAL catches are never
 * exactly 0 — the lowest genuine local-presence catches measure histSim
 * ~0.05–0.2, top-dex ones 0.6–0.95. So an exact-zero (sub-epsilon) histSim on a
 * local-presence caught verdict is a uniquely-separable false-positive class.
 *
 * The cutoff is deliberately a tiny epsilon, NOT a higher floor: a floor of even
 * 0.05 would block the real low-histSim local-presence catches that legitimately
 * apply. Only the no-species-match-at-all (`< 0.001`) case is demoted; a null
 * histSim (histogram never computed) is left untouched so paths that genuinely
 * lack the signal keep their prior behaviour.
 */
export const ZERO_HISTSIM_EPSILON = 0.001;




// Option C center-veto foreground floor (2026-07-08). The non-binary bug-A
// center-veto used to fire on ANY centred-number read, dropping genuine catches
// whose Vision number-box centroid misreads as "center" during scroll (the grid
// ROI is fixed and does not track scroll — the true fix, per-frame registration,
// is deferred). Device telemetry (eac4863): empty-cell false writes had
// foregroundPixels 70–190; the lowest REAL caught cell was 556 (median 3254).
// 400 sits well clear of both (~2× above empties, ~1.4× below the lowest real
// catch), so a centred read is only trusted as "empty" when foreground also
// corroborates it. Above the floor → signals disagree → defer to the matcher.
export const CENTER_VETO_FG_FLOOR = 400;
/**
 * The same floor as a FRACTION of the sprite band (400 px of the 886x1918
 * iPhone frame's ~41.6k-px band). The pixel count is resolution-bound: on the
 * Galaxy's 576x1280 capture the band holds 2.4x fewer pixels, so a sprite the
 * iPhone corroborates at 684 px measured 298 px there and the centred-number
 * veto rejected the catch (2026-09-25 Purified pass, #116 / #117 / #081 ...).
 */
export const CENTER_VETO_FG_FLOOR_FRACTION = 0.0096;

/**
 * bug-A gate decision (Fix #163): given a sprite matcher verdict and the number
 * geometry, decide whether the centred-number veto should reject a "caught"
 * verdict. Pulled out as a pure function so the gate logic in extractPokedexFrame
 * (which needs a DOM) is unit-testable.
 *
 *  - chromaMatchNotVerified: a "chroma-match" caught verdict only loses to
 *    centred geometry when its histogram similarity is null or below the floor.
 *    A high-histSim chroma-match is a VERIFIED real sprite and survives (#163).
 *  - localPresenceCaught: a "local-presence" caught verdict is an aura/edge
 *    presence heuristic, NOT a verified sprite-histogram match, so a centred
 *    number (the registered-but-uncaught silhouette geometry) authoritatively
 *    overrules it. This closes the empty-cell false-positive the chroma-only
 *    gate missed (device 2026-06-20). A real caught sprite shows the number at
 *    "bottom" (under the sprite), so it is never centred and never vetoed here.
 *  - recoveryCaughtRejected: the anchor-absent recovery path — a centred number
 *    is the silhouette/uncaught signature, so ANY caught verdict is rejected.
 *
 * Option C (2026-07-08): the NON-BINARY center-veto now optionally requires
 * foreground to corroborate an EMPTY cell before firing. Pass
 * `requireLowForeground: true` (as the non-binary call site does) so a centred
 * read only vetoes when `foregroundPixels <= CENTER_VETO_FG_FLOOR`. A caught
 * cell whose number misreads as "center" during scroll carries a real sprite
 * (high foreground) and must survive — when foreground is above the floor the
 * signals disagree and we defer to the matcher. When foreground is unknown we
 * are CONSERVATIVE TOWARD RECALL (do NOT veto), since the whole point of this
 * mitigation is to stop dropping real catches. Callers that omit `requireLowForeground` keep the
 * legacy geometry-only behaviour (binary path is unchanged — the call site
 * skips this gate entirely for binary variants).
 */
export function bugAGateRejectsCaught(args: {
  spriteVerdict: "caught" | "missing" | "uncertain" | undefined;
  numberSaysUncaught: boolean;
  recoveredCentredUncaught: boolean;
  requireLowForeground?: boolean;
  foregroundPixels?: number | null;
  foregroundFloor?: number;
  /** foregroundPixels over the band's area; when given it decides instead of the pixel count. */
  foregroundFraction?: number | null;
}): boolean {
  // Scan overhaul v2 (2026-07-08): NUMBER-GEOMETRY-ONLY veto. A caught sprite
  // verdict is rejected iff the dex number reads CENTRED — either the direct
  // numberPosition "center" signal (registered-but-uncaught silhouette geometry)
  // or the anchor-absent recovery equivalent. The number anchor is driven by
  // Apple Vision's digit bounding-box, so this is the one authoritative
  // never-seen signal and correctly blocks empty registered cells. The legacy
  // spriteState-empty and histSim recovery arms were removed — the cellPresence
  // chroma verdict is now authoritative for caught.
  const centredUncaught =
    args.numberSaysUncaught && args.spriteVerdict === "caught";
  const recoveryCaughtRejected =
    args.recoveredCentredUncaught && args.spriteVerdict === "caught";
  const geometryVeto = centredUncaught || recoveryCaughtRejected;
  if (!geometryVeto) return false;
  // Legacy / binary path: geometry alone vetoes (binary skips this gate at the
  // call site, and existing callers that don't opt in keep the old behaviour).
  if (!args.requireLowForeground) return true;
  // Option C non-binary foreground corroboration. Unknown foreground → recall-safe
  // (defer to matcher, do NOT veto).
  const frac = args.foregroundFraction;
  if (frac != null && Number.isFinite(frac)) return frac <= CENTER_VETO_FG_FLOOR_FRACTION;
  const fg = args.foregroundPixels;
  if (fg == null || !Number.isFinite(fg)) return false;
  const floor = args.foregroundFloor ?? CENTER_VETO_FG_FLOOR;
  return fg <= floor;
}

export interface SlotOcrCropPlan {
  /** Source rect in image pixels (already padded + clamped to band). */
  srcX: number;
  srcY: number;
  srcW: number;
  srcH: number;
  /** Destination (upscaled) dimensions in pixels. */
  outW: number;
  outH: number;
  /** The upscale factor actually used (== PILL_SLOT_OCR_UPSCALE). */
  upscale: number;
}

/**
 * Pure geometry/param computation for a single pill-slot OCR crop. Split out so
 * it is deterministically unit-testable without a DOM/Vision (Fix A's
 * regression test asserts the upscale factor + padded dimensions on a small
 * input, and that a wide/long crop scales by the same factor).
 *
 * `slotXFrac`/`slotWFrac` are the naive slot rect; padding widens it by
 * PILL_SLOT_OCR_PAD_X_FRAC of the slot width per side, clamped to [0,1] so the
 * crop stays inside the pill band.
 */
export function computeSlotOcrCrop(
  imgWidth: number,
  imgHeight: number,
  slotXFrac: number,
  slotYFrac: number,
  slotWFrac: number,
  slotHFrac: number,
): SlotOcrCropPlan {
  const padFrac = slotWFrac * PILL_SLOT_OCR_PAD_X_FRAC;
  const x0 = Math.max(0, slotXFrac - padFrac);
  const x1 = Math.min(1, slotXFrac + slotWFrac + padFrac);
  const srcX = Math.floor(imgWidth * x0);
  const srcY = Math.floor(imgHeight * slotYFrac);
  const srcW = Math.max(1, Math.floor(imgWidth * (x1 - x0)));
  const srcH = Math.max(1, Math.floor(imgHeight * slotHFrac));
  return {
    srcX,
    srcY,
    srcW,
    srcH,
    outW: Math.max(1, Math.round(srcW * PILL_SLOT_OCR_UPSCALE)),
    outH: Math.max(1, Math.round(srcH * PILL_SLOT_OCR_UPSCALE)),
    upscale: PILL_SLOT_OCR_UPSCALE,
  };
}

/**
 * Crop a single pill slot, upscale + pad it, and return a raw (non-binarised)
 * PNG data URL for Vision.
 *
 * Vision-only trial: the previous binarisation step (preprocessSlotOcrRgba) was
 * removed. Its fixed-luma threshold is exactly what broke iPhone 12 pill reads
 * under True Tone white-point drift — it binarised the pill text into the
 * background. Apple Vision reads the raw upscaled crop reliably, so we keep only
 * the geometry (upscale + pad) and hand Vision unmodified pixels. Falls back to
 * the naive `cropToDataUrl` only when no 2D context is available (no-canvas
 * environments), so OCR never silently gets nothing.
 */
function cropSlotForOcr(
  img: HTMLImageElement,
  slotXFrac: number, slotYFrac: number, slotWFrac: number, slotHFrac: number,
): string {
  const plan = computeSlotOcrCrop(
    img.width, img.height, slotXFrac, slotYFrac, slotWFrac, slotHFrac,
  );
  const canvas = document.createElement("canvas");
  canvas.width = plan.outW;
  canvas.height = plan.outH;
  const ctx = canvas.getContext("2d", CANVAS_READBACK);
  if (!ctx) {
    return cropToDataUrl(img, slotXFrac, slotYFrac, slotWFrac, slotHFrac);
  }
  ctx.imageSmoothingEnabled = true;
  ctx.drawImage(
    img,
    plan.srcX, plan.srcY, plan.srcW, plan.srcH,
    0, 0, plan.outW, plan.outH,
  );
  // No binarisation: return the raw upscaled crop straight to Vision.
  return canvas.toDataURL("image/png");
}

/**
 * Flat-cell thresholds for the caught veto (see the FLAT-CELL VETO branch).
 *
 * Calibrated against the 2026-07-18 device trace:
 *   false #91 (empty, false-caught) — hueVariance 0,      luminanceVariance 0
 *   genuine catches                 — hueVariance 0.245+, luminanceVariance 0.0058+
 * The floors sit an order of magnitude below the weakest genuine catch, so this
 * can only fire on a cell that is literally featureless. Deliberately strict:
 * a false NEGATIVE here costs a re-scan, a false POSITIVE corrupts the dex.
 */
const FLAT_CELL_HUE_VARIANCE_MAX = 0.01;
const FLAT_CELL_LUM_VARIANCE_MAX = 0.0005;

/**
 * True when the pixel classifier sees a featureless cell: it called the cell
 * empty AND found essentially no hue or luminance variation. Nothing that
 * contains a sprite can be flat, so a caught verdict on such a cell is false.
 */
function isFlatEmptyCell(
  cls: { spriteState?: string; hueVariance: number; luminanceVariance: number } | null | undefined,
): boolean {
  if (!cls) return false;
  if (cls.spriteState !== "empty") return false;
  return (
    cls.hueVariance <= FLAT_CELL_HUE_VARIANCE_MAX &&
    cls.luminanceVariance <= FLAT_CELL_LUM_VARIANCE_MAX
  );
}

function avgLuminance(
  img: HTMLImageElement,
  xPct: number, yPct: number, wPct: number, hPct: number,
): number {
  const x = Math.floor(img.width * xPct);
  const y = Math.floor(img.height * yPct);
  const w = Math.max(1, Math.floor(img.width * wPct));
  const h = Math.max(1, Math.floor(img.height * hPct));
  const canvas = document.createElement("canvas");
  canvas.width = w; canvas.height = h;
  const ctx = canvas.getContext("2d", CANVAS_READBACK);
  if (!ctx) return 0;
  ctx.drawImage(img, x, y, w, h, 0, 0, w, h);
  const data = ctx.getImageData(0, 0, w, h).data;
  let sum = 0;
  for (let i = 0; i < data.length; i += 4) {
    sum += 0.2126 * data[i] + 0.7152 * data[i + 1] + 0.0722 * data[i + 2];
  }
  return sum / (w * h);
}

/**
 * Dominant hue (0-360) + saturation (0-1) over a normalized crop. Returns
 * hue=null for an achromatic / empty region. Coarse average is enough for the
 * chip-row hue fallback — we only need to tell orange/pink from gold from
 * pearl/silver from mint.
 */
function dominantHue(
  img: HTMLImageElement,
  xPct: number, yPct: number, wPct: number, hPct: number,
): { hue: number | null; saturation: number } {
  const x = Math.floor(img.width * xPct);
  const y = Math.floor(img.height * yPct);
  const w = Math.max(1, Math.floor(img.width * wPct));
  const h = Math.max(1, Math.floor(img.height * hPct));
  const canvas = document.createElement("canvas");
  canvas.width = w; canvas.height = h;
  const ctx = canvas.getContext("2d", CANVAS_READBACK);
  if (!ctx) return { hue: null, saturation: 0 };
  ctx.drawImage(img, x, y, w, h, 0, 0, w, h);
  const data = ctx.getImageData(0, 0, w, h).data;
  // Accumulate hue as a unit vector so the circular mean is correct, and
  // average saturation over chromatic pixels only.
  let sx = 0, sy = 0, satSum = 0, chroma = 0;
  for (let i = 0; i < data.length; i += 4) {
    const r = data[i] / 255, g = data[i + 1] / 255, b = data[i + 2] / 255;
    const max = Math.max(r, g, b), min = Math.min(r, g, b);
    const d = max - min;
    if (d < 0.06) continue; // near-grey: skip, contributes no hue
    let hh: number;
    if (max === r) hh = ((g - b) / d) % 6;
    else if (max === g) hh = (b - r) / d + 2;
    else hh = (r - g) / d + 4;
    hh *= 60; if (hh < 0) hh += 360;
    const rad = (hh * Math.PI) / 180;
    sx += Math.cos(rad); sy += Math.sin(rad);
    satSum += max <= 0 ? 0 : d / max;
    chroma += 1;
  }
  if (chroma === 0) return { hue: null, saturation: 0 };
  let hue = (Math.atan2(sy, sx) * 180) / Math.PI;
  if (hue < 0) hue += 360;
  return { hue, saturation: satSum / chroma };
}

/**
 * b17o: number-anchor-first per-cell classifier.
 *
 * The dex number is the ANCHOR. For each cell we read its number position FIRST,
 * then — only when the number is pinned to the bottom — inspect the sprite:
 *
 *   step 1  locate the dex number (DexNumberPosition centroid).
 *   step 2  classify its vertical band:
 *             center → NEVER-SEEN (blank frame, number centred). Done.
 *             absent → no usable signal; skip the cell.
 *             bottom → proceed to the sprite test.
 *   step 3  the sprite region sits ABOVE the number. analyzeSpriteHues decides:
 *             empty       → SEEN-only (number pinned, no sprite).
 *             silhouette  → SEEN-only (single-hue tinted outline).
 *             multi-hue   → CAUGHT.
 *   step 4  per-variant rule:
 *             silhouette variants (Main/Shiny/Mega/G-Max/Shadow/Purified) run the
 *               full hue-variance test above.
 *             empty-vs-full variants (Lucky/Perfect/XXL/XXS) have NO greyscale
 *               state in Pokémon GO — a cell is either empty or a full-colour
 *               sprite — so the silhouette branch is skipped: any non-empty
 *               sprite is CAUGHT.
 *
 * Pokémon GO's three visual cell states map to the {caught, seen} pair the
 * downstream adapter consumes:
 *   CAUGHT            → { caught: true,  seen: true  } → "present"
 *   SEEN (silhouette) → { caught: false, seen: true  } → "missing"
 *   NEVER-SEEN        → { caught: false, seen: false } → "unknown"
 *   skip (no number)  → null (cell is not emitted at all)
 */

/**
 * Variants Pokémon GO renders with a greyscale/tinted SILHOUETTE for the
 * available-but-uncaught state. For these the hue-variance silhouette test runs.
 */
const SILHOUETTE_VARIANTS: ReadonlySet<PokedexVariant> = new Set<PokedexVariant>([
  "pokemon", "shiny", "mega", "gmax", "shadow", "purified",
]);

/**
 * Variants with NO silhouette state — a cell is either empty or a full-colour
 * sprite (XXL/XXS/Perfect/Lucky). The silhouette branch is skipped for these so
 * a low-hue-variance full sprite is never mis-read as a silhouette.
 */
export function variantHasSilhouette(variant: PokedexVariant): boolean {
  return SILHOUETTE_VARIANTS.has(variant);
}

export type SpriteState = "empty" | "silhouette" | "caught";

export interface DexCellClassification {
  caught: boolean;
  seen: boolean;
  numberPosition: NumberVerticalPosition;
  spriteState: SpriteState | "none";
  hueVariance: number;
  luminanceVariance: number;
  foregroundPixels: number;
  /**
   * foregroundPixels over the sprite band's own pixel area (2026-09-08): the
   * resolution-independent form. Broadcast frames arrive at the phone's own
   * size (886x1918 on the owner's iPhone, fewer pixels on a 16:9 device), so
   * every apply floor reads this, never the pixel count.
   */
  foregroundFraction: number;
  /**
   * b18f: chroma-foreground fraction from the background-adaptive empty test.
   * Only populated for the no-silhouette variants (XXL/XXS/Lucky/Perfect);
   * undefined elsewhere. Surfaced into the classify telemetry so the next scan
   * can verify the light-sprite recovery.
   */
  chromaFraction?: number;
}

/**
 * Why a cell produced no classification, so the scan log can tell apart a
 * cell with no number anchor (pos-absent) from a missing 2D canvas context
 * (ctx-null) from the genuinely no-canvas (non-DOM) path.
 */
export type ClassificationSkipReason =
  | "ctx-null"
  | "pos-absent"
  | "nexus_overlay_overlap";

export interface DexCellSkip {
  skipReason: ClassificationSkipReason;
}

export function isCellSkip(
  result: DexCellClassification | DexCellSkip,
): result is DexCellSkip {
  return (result as DexCellSkip).skipReason !== undefined;
}

/**
 * Run the number-anchor-first classifier over a WHOLE-cell RGBA crop. Pure, so
 * it unit-tests in Node (sharp → RGBA) and runs verbatim in the browser (canvas
 * getImageData). Returns a { skipReason } marker when the cell should be
 * skipped because it has no number anchor.
 */
export function classifyDexCell(
  data: Uint8ClampedArray | Uint8Array,
  width: number,
  height: number,
  variant: PokedexVariant,
  pillMask: PillRegion | null = null,
): DexCellClassification | DexCellSkip {
  const numberPos = dexCellNumberPositionFromRgba(data, width, height, variant, pillMask);
  const position = classifyNumberVerticalPosition(numberPos);

  if (position === "absent") return { skipReason: "pos-absent" }; // no anchor — skip.

  if (position === "center") {
    // Number centred in a blank frame → NEVER-SEEN by the centroid anchor alone.
    // Sprite is not inspected here; the caller may still promote this cell to
    // SEEN if OCR read a valid dex number (see decideCellEmit / b17v Fix 2).
    return {
      caught: false,
      seen: false,
      numberPosition: position,
      spriteState: "none",
      hueVariance: 0,
      luminanceVariance: 0,
      foregroundPixels: 0,
      foregroundFraction: 0,
    };
  }

  // position === "bottom": inspect the sprite region ABOVE the number.
  return classifyCellSprite(data, width, height, variant, position, pillMask);
}

/**
 * Inspect the sprite region ABOVE the number band and build the full
 * classification. The number sits in the bottom band, so crop the upper ~70%
 * of the cell. Shared by the "bottom" path of classifyDexCell and the b17v
 * read-promotion path (decideCellEmit), so both classify a sprite identically.
 */
function classifyCellSprite(
  data: Uint8ClampedArray | Uint8Array,
  width: number,
  height: number,
  variant: PokedexVariant,
  numberPosition: NumberVerticalPosition,
  pillMask: PillRegion | null = null,
): DexCellClassification {
  const spriteH = Math.max(1, Math.floor(height * 0.7));
  const region = sliceTopRegion(data, width, height, spriteH);
  // b18-pill: pass the cell-local pill mask into the analyzers so they skip
  // pixels inside the capsule region.
  // Round-9 Fix A: purified sprites are washed-out and clear few foreground
  // pixels, so the default 5% empty-coverage floor mislabels real catches as
  // empty. Halve the floor for purified ONLY; every other variant keeps the
  // default. Scoped here at the single call site so non-purified is byte-for-byte
  // unchanged (analyzeSpriteHues defaults emptyPixelThreshold to 0.05 when unset).
  const hueOpts =
    variant === "purified"
      ? { emptyPixelThreshold: PURIFIED_FOREGROUND_PIXEL_FLOOR }
      : {};
  const hue = analyzeSpriteHues(region, width, spriteH, hueOpts, pillMask);
  const hasSilhouette = variantHasSilhouette(variant);

  // b18f: for the no-silhouette variants (XXL/XXS/Lucky/Perfect) the
  // luminance-based emptiness test in analyzeSpriteHues drops light sprites
  // against the light page background (the XXL false-negative class). Take a
  // second, background-adaptive chroma opinion and only keep the "empty" verdict
  // when both agree. The silhouette variants keep the pure luminance path.
  const chroma = hasSilhouette
    ? null
    : analyzeSpriteChroma(region, width, spriteH, { variant }, pillMask);

  let spriteState: SpriteState;
  if (hue.isEmpty && (chroma == null || chroma.isEmpty)) {
    spriteState = "empty";
  } else if (hasSilhouette && hue.isSilhouette) {
    spriteState = "silhouette";
  } else {
    // Either a multi-hue sprite, or an empty-vs-full variant with any sprite
    // content (silhouette branch skipped), or a light sprite the chroma test
    // recovered → caught.
    spriteState = "caught";
  }

  return {
    caught: spriteState === "caught",
    seen: true, // a number anchor that reached the sprite test reads at least SEEN.
    numberPosition,
    spriteState,
    hueVariance: hue.hueVariance,
    luminanceVariance: hue.luminanceVariance,
    foregroundPixels: hue.foregroundPixels,
    foregroundFraction: hue.foregroundPixels / Math.max(1, width * spriteH),
    chromaFraction: chroma?.chromaFraction,
  };
}

/**
 * b17v Fix 2: decide a cell's final {seen, caught} emit, promoting a centroid
 * "center" verdict to at least SEEN when OCR read a valid dex number.
 *
 * The centroid anchor (even after Fix 1) can still misjudge a caught cell as
 * "center" / never-seen. But if the digit OCR returned a real dex id, a Pokémon
 * entry demonstrably occupies this slot, so it is at minimum SEEN. We then run
 * the sprite test (the same one the "bottom" path uses) to decide caught vs
 * seen-only, rather than emitting the blank never-seen cell.
 *
 * Pure over an already-cropped RGBA buffer so it unit-tests without a canvas.
 * Returns null when no promotion applies (caller keeps the original verdict).
 */
export interface CellEmitPromotion {
  classification: DexCellClassification;
  spriteCaught: boolean;
}

export function promoteCenterCellOnRead(
  classification: DexCellClassification,
  dexNumber: number | null,
  data: Uint8ClampedArray | Uint8Array,
  width: number,
  height: number,
  variant: PokedexVariant,
  pillMask: PillRegion | null = null,
): CellEmitPromotion | null {
  if (classification.numberPosition !== "center") return null;
  if (dexNumber == null) return null;
  const promoted = classifyCellSprite(data, width, height, variant, "center", pillMask);
  return { classification: promoted, spriteCaught: promoted.caught };
}

/**
 * Live-scan write-back fix — recover a classification when the pixel number-
 * anchor reported ABSENT (`pos-absent`) but OCR positively read a valid dex id.
 *
 * The number-anchor detector (`dexCellNumberPositionFromRgba`) scores a cell as
 * `absent` when too few glyph pixels clear the variant's colour predicate —
 * which is exactly what happens on the LIVE broadcast/PiP path, where ReplayKit
 * downscaling + JPEG compression desaturate the rose/silver "#xxxx" ink below
 * the predicate's threshold. The STILL-CAPTURE (upload) path feeds the same
 * classifier full-resolution cell crops, so its glyphs clear the predicate and
 * the anchor resolves to center/bottom. The device log proved the gap: 92% of
 * `cell classify` events were `numberPosition:"no-canvas"` / `pos-absent` even
 * though the tight-ROI Tesseract read (`dexNumberRead`) returned a real id on
 * the same cells. With `cls == null`, the cell loop fell through to a
 * conservative `caught:false, seen:true` ("missing") verdict, so every read
 * died before the apply layer and `numbersRead`/`applied` stayed 0.
 *
 * A positive OCR read is independent proof that a numbered Pokédex entry
 * occupies the slot, so the cell is at minimum SEEN. We then run the SAME sprite
 * test the "bottom" anchor path uses (`classifyCellSprite`) over the cell crop
 * to decide caught vs seen-only — recovering a real `numberPosition` instead of
 * `no-canvas`. This is the canvas-context handoff the live path was dropping:
 * the crop is already in hand (the sprite chroma matcher uses it), we just
 * weren't classifying it when the anchor scored absent.
 *
 * Returns null (caller keeps the conservative verdict) when there is no read or
 * no crop — the genuinely non-DOM/upload path with no pixel buffer is untouched.
 * Pure over an already-cropped RGBA buffer so it unit-tests without a canvas.
 */
export function classifyReadCellWhenAnchorAbsent(
  dexNumber: number | null,
  data: Uint8ClampedArray | Uint8Array,
  width: number,
  height: number,
  variant: PokedexVariant,
  pillMask: PillRegion | null = null,
): CellEmitPromotion | null {
  if (dexNumber == null) return null;
  if (width < 2 || height < 2) return null;
  // A read number means the slot is occupied; run the sprite test to decide
  // caught vs seen-only. This recovers numberPosition so the cell stops
  // reporting no-canvas/pos-absent.
  //
  // False-positive gate (re-applies the bug-A centred-number discriminator the
  // bottom-anchor path uses). The original recovery FORCED a "bottom" anchor,
  // which made the downstream bug-A gate (keyed on numberPosition === "center")
  // a no-op for recovered cells — so an empty/silhouette dex cell whose number
  // is CENTRED (the registered-but-uncaught signature) could be promoted to
  // CAUGHT on a bare chroma/silhouette match. This is exactly the #007/#029 XXL
  // false positive.
  //
  // We re-measure the number position over the crop instead of assuming it. The
  // anchor scored ABSENT under the variant's colour predicate (why we are here),
  // but the centroid integration is a softer signal that can still resolve the
  // number's vertical band on the same crop. We only promote to CAUGHT when the
  // number geometry is NOT centred — a CENTRED number is authoritative evidence
  // of NOT-caught and blocks the recovery, never CAUGHT. A bottom/absent number
  // keeps the genuine full-sprite recovery (the #439 win: Shiny/XXL real entries
  // still apply).
  const numberPos = dexCellNumberPositionFromRgba(data, width, height, variant, pillMask);
  const measured = classifyNumberVerticalPosition(numberPos);
  // The recovered anchor reported for telemetry: trust the measured band when it
  // resolved, else fall back to "bottom" (the slot is occupied per the OCR read).
  const anchor: NumberVerticalPosition = measured === "absent" ? "bottom" : measured;
  const recovered = classifyCellSprite(data, width, height, variant, anchor, pillMask);
  if (measured === "center" && recovered.caught) {
    // Centred number with no sprite is the registered-but-uncaught silhouette:
    // NOT-seen and not-caught. Never promote to caught on recovery, and route
    // this identically to the bug-A center case (seen:false) so both paths agree.
    const seenOnly: DexCellClassification = {
      ...recovered,
      caught: false,
      seen: false,
      numberPosition: "center",
    };
    return { classification: seenOnly, spriteCaught: false };
  }
  return { classification: recovered, spriteCaught: recovered.caught };
}

/** Copy the top `regionHeight` rows of an RGBA buffer into a fresh buffer. */
function sliceTopRegion(
  data: Uint8ClampedArray | Uint8Array,
  width: number,
  height: number,
  regionHeight: number,
): Uint8ClampedArray {
  const h = Math.min(regionHeight, height);
  const out = new Uint8ClampedArray(width * h * 4);
  const bytes = width * h * 4;
  for (let i = 0; i < bytes; i++) out[i] = data[i];
  return out;
}

/** Intrinsic pixel dims of an image, preferring naturalWidth/Height when set. */
function imgDims(img: HTMLImageElement): { w: number; h: number } {
  return {
    w: img.naturalWidth || img.width,
    h: img.naturalHeight || img.height,
  };
}

/**
 * b18-pill: translate a frame-space PillRegion into the coordinate space of a
 * cell crop that starts at (cellX, cellY) in the frame with size (cellW x cellH).
 * Returns null when the pill does not intersect the cell at all.
 */
function framePillToCellPill(
  pill: PillRegion,
  cellX: number,
  cellY: number,
  cellW: number,
  cellH: number,
): PillRegion | null {
  const localX = pill.x - cellX;
  const localY = pill.y - cellY;
  const x0 = Math.max(0, localX);
  const y0 = Math.max(0, localY);
  const x1 = Math.min(cellW - 1, localX + pill.w - 1);
  const y1 = Math.min(cellH - 1, localY + pill.h - 1);
  if (x1 < x0 || y1 < y0) return null;
  return { x: x0, y: y0, w: x1 - x0 + 1, h: y1 - y0 + 1 };
}

/**
 * Fraction of cell area (0-1) covered by the pill mask.
 * Returns 0 when there is no pill or no overlap.
 */
function computePillOverlapFraction(pill: PillRegion | null, cellW: number, cellH: number): number {
  if (!pill) return 0;
  const area = cellW * cellH;
  if (area === 0) return 0;
  return Math.min(1, (pill.w * pill.h) / area);
}

/**
 * DOM wrapper: crop the cell to its own canvas and run classifyDexCell. Returns
 * a { skipReason } marker when the 2D context is unavailable or the cell is
 * skipped; callers that get neither a classification nor a skip are in the
 * genuinely no-canvas (non-DOM) path. On success also returns the cell crop
 * (RGBA + dims) so the caller can re-run the sprite test for the b17v read
 * promotion without cropping the cell a second time.
 */
interface CellClassifyResult {
  result: DexCellClassification | DexCellSkip;
  crop?: { data: Uint8ClampedArray; width: number; height: number };
}

function classifyCellAt(
  img: HTMLImageElement,
  xPct: number, yPct: number, wPct: number, hPct: number,
  variant: PokedexVariant,
  framePill: PillRegion | null = null,
): CellClassifyResult {
  const { w: iw, h: ih } = imgDims(img);
  const x = Math.floor(iw * xPct);
  const y = Math.floor(ih * yPct);
  const w = Math.max(1, Math.floor(iw * wPct));
  const h = Math.max(1, Math.floor(ih * hPct));
  const canvas = document.createElement("canvas");
  canvas.width = w; canvas.height = h;
  const ctx = canvas.getContext("2d", CANVAS_READBACK);
  if (!ctx) return { result: { skipReason: "ctx-null" } };
  ctx.drawImage(img, x, y, w, h, 0, 0, w, h);
  const data = ctx.getImageData(0, 0, w, h).data;
  // b18-pill: translate frame-space pill into cell-local coordinates.
  const cellPill = framePill ? framePillToCellPill(framePill, x, y, w, h) : null;
  return { result: classifyDexCell(data, w, h, variant, cellPill), crop: { data, width: w, height: h } };
}

/**
 * Build 2 / v1.4.6 — classify the PoGo dex variant from a hue sample of the
 * filter-chip / theme region. Returns null when the hue doesn't clearly match
 * a known theme so the caller can SKIP rather than mislabel.
 *
 * Theme cues (PoGo Pokédex filter chips):
 *   - lucky   → orange/pink   (hue ~330-360 or ~0-45)
 *   - shiny   → gold/yellow   (hue ~46-62, high saturation)
 *   - perfect → pearl/silver  (near-achromatic / very low saturation)
 *   - xxs/xxl → mint/teal     (hue ~150-200)
 */
function variantFromChipHue(hue: number | null, saturation: number): PokedexVariant | null {
  // 2026-08-30 (inflated-Perfect audit): "no hue / low saturation" used to
  // positively select "perfect" here. That inverted the codebase-wide rule
  // that colour may veto but never positively pick a variant — an unreadable
  // or washed-out chip band (no hue at all!) classified as the Perfect dex.
  // Absence of colour is absence of evidence: return null and let the caller
  // skip the frame.
  if (hue == null || saturation < 0.12) return null;
  const h = ((hue % 360) + 360) % 360;
  if ((h >= 330 || h <= 45) && saturation >= 0.3) return "lucky";   // orange/pink
  if (h >= 46 && h <= 62 && saturation >= 0.4) return "shiny";       // gold
  if (h >= 150 && h <= 200 && saturation >= 0.2) return "xxl";       // mint/teal
  return null;
}

const CHIP_VARIANT_KEYWORDS: Array<[PokedexVariant, RegExp]> = [
  ["lucky",    /\blucky\b/i],
  ["shiny",    /\bshiny\b/i],
  // 2026-08-30 (inflated-Perfect heal, fourth lexicon): the pill reads
  // "★ 100%", so the % is mandatory and 3-star wording never classifies. A
  // bare "100" here matched dex numbers / progress counts in the band OCR and,
  // via classifyVariantChip's 0.99-confidence stamp, beat every veto.
  ["perfect",  /\bperfect\b|\b100\s*%/i],
  ["xxs",      /\bxxs\b/i],
  ["xxl",      /\bxxl\b/i],
  // Mega/G-Max are real curated PoGo pills with on-screen text labels. Adding
  // their tokens lets the legacy chip-text path positively name them (mirroring
  // shadow/purified) so a pink/rose Mega/G-Max backdrop resolves by TEXT and is
  // never left to the hue fallback below, which maps pink into lucky's band.
  ["gmax",     /\bg[-\s]?max\b|\bgigantamax\b/i],
  ["mega",     /\bmega\b/i],
  ["shadow",   /\bshadow\b/i],
  ["purified", /\bpurified\b/i],
  // Base/main dex pill. Placed LAST so a more-specific variant keyword on a
  // multi-pill strip is still counted; the multi-keyword gate in
  // classifyActivePillBand disambiguates regardless of list order.
  ["pokemon",  /\bpok[eé]?mon\b/i],
];

/**
 * Find every DISTINCT variant whose keyword regex matches `text`. Unlike the
 * legacy first-match-wins loop in classifyVariantChip, this collects ALL hits
 * so the caller can tell the unambiguous single-pill case (accept the text)
 * apart from the multi-pill "POKEMON SHINY LUCKY" strip (delegate to the
 * geometric active-pill locator). Order of the returned list follows
 * CHIP_VARIANT_KEYWORDS. Exported for tests.
 */
export function matchVariantKeywords(text: string): PokedexVariant[] {
  const seen = new Set<PokedexVariant>();
  const out: PokedexVariant[] = [];
  for (const [variant, re] of CHIP_VARIANT_KEYWORDS) {
    if (re.test(text) && !seen.has(variant)) {
      seen.add(variant);
      out.push(variant);
    }
  }
  return out;
}

/**
 * Sole-variant TEXT recovery (Purified-on-cyan deadlock, device trace
 * 2026-06-23).
 *
 * THE BUG. On a held-still official **Purified** dex page, the pre-OCR colour
 * tracker classified the backdrop as `dex_cyan` (the Purified mint wash reads
 * B-dominant on this device, indistinguishable from cyan by tint — the P6
 * cyan/mint boundary deliberately keeps a B-dominant wash as cyan). The
 * geometric active-pill read then returned `source:"none"` (capsule not located,
 * peakHue~98), and the colour-narrowed re-read matched nothing because cyan's
 * allowed token set is {pokemon, xxl, xxs} — it can NEVER contain "purified".
 * So every frame held at `mode:"unknown"` forever, even though the PoGo filter
 * pill plainly reads "PURIFIED" in the capsule band on every frame.
 *
 * THE RECOVERY. When the geometric pill read fails on a dex page, OCR the
 * capsule band and look for a UNIQUE, unambiguous **sole-variant** keyword
 * (`purified` / `shadow` — the curated dexes whose colour family admits exactly
 * one variant, so a single keyword names the dex outright). When found, synthesize
 * a high-confidence `chip-text` signal for it. That signal disagrees with the
 * `pokemon` app_state default and clears CHIP_TEXT_VETO_OVERRIDE_THRESHOLD (0.95),
 * so the unified resolver's veto-override accepts it OVER the boundary-misread
 * colour family — the exact mechanism the resolver header documents for
 * "purified page → dex_cyan" boundary misreads.
 *
 * SAFEGUARDS AGAINST THE WRONG DEX:
 *   - Fires ONLY when geometry failed (`source:"none"`); a real geometric pin is
 *     never overridden.
 *   - Requires EXACTLY ONE distinct variant keyword in the band. A real cyan page
 *     reads "POKEMON"/"XXL"/"XXS" (cyan tokens), not "purified", so it never
 *     synthesizes a cross-family variant; any contamination that adds a second
 *     keyword (e.g. a stray "100") collapses the match to HOLD.
 *   - Restricted to {purified, shadow}: distinctive words that do not occur in the
 *     live-scan overlay ("Scanning…" / "Hold steady" / "NEXUS DEX") or in cell
 *     numbers, so the noisy capsule band cannot spoof them. ("perfect" is excluded
 *     — its 100/3-star tokens collide with dex counts.)
 *   - Requires a high OCR confidence (TEXT_RECOVERY_MIN_OCR_CONF, 0-100 scale).
 *
 * Returns the recovered variant, or null when no unique allowed keyword is read.
 * Pure; exported for tests.
 */
export const TEXT_RECOVERY_VARIANTS: readonly PokedexVariant[] = ["purified", "shadow"];
/** Min runOcr confidence (0-100) for the sole-variant text recovery to fire. */
export const TEXT_RECOVERY_MIN_OCR_CONF = 90;
/**
 * Min runOcr confidence (0-100) for the Apple Vision FALLBACK recovery to fire.
 *
 * Vision confidence is rescaled onto a different curve than Tesseract
 * (see rescaleVisionConfidence: native 0.55 -> 70, native 1.00 -> 100). The
 * Tesseract-tuned 90 gate would require native Vision conf >= 0.85, which is an
 * unnecessarily tight bar for the LONG, high-contrast {purified, shadow} labels
 * Vision reads very reliably. A Vision read that clears the engine's own
 * APPLE_VISION_MIN_CONFIDENCE (native 0.55 -> rescaled 70) is already a trusted
 * read for these distinctive words, so the fallback gate sits at 70. The same
 * single-unambiguous-keyword lexicon safeguard (matchVariantKeywords) still
 * guards against a wrong-dex synthesis.
 */
export const TEXT_RECOVERY_VISION_MIN_OCR_CONF = 70;

export function recoverSoleVariantFromBandText(
  text: string,
  ocrConf: number,
  /**
   * Min OCR confidence (0-100) required to fire. Defaults to the Tesseract gate
   * (TEXT_RECOVERY_MIN_OCR_CONF). The Apple Vision fallback passes the lower
   * Vision-calibrated gate (TEXT_RECOVERY_VISION_MIN_OCR_CONF) because Vision
   * confidence lives on a different (rescaled) curve.
   */
  minOcrConf: number = TEXT_RECOVERY_MIN_OCR_CONF,
): PokedexVariant | null {
  if (ocrConf < minOcrConf) return null;
  const matches = matchVariantKeywords(text);
  if (matches.length !== 1) return null;
  const variant = matches[0];
  return TEXT_RECOVERY_VARIANTS.includes(variant) ? variant : null;
}

/**
 * R2 (2026-06-20): the robust GEOMETRIC active-pill path is now PRIMARY for
 * ALL frames where a pill region is locatable, not only multi-keyword strips.
 *
 * Previously this delegated to the geometric locator only when the flat-band OCR
 * read MORE THAN ONE keyword (`matches.length > 1`); single-keyword and
 * zero-keyword frames went through the brittle flat-band `classifyVariantChip`
 * (one OCR over the whole band) which failed ~93% of device frames on the
 * Perfect-page log and hardcoded `peakScore:0`, making device traces dishonest.
 *
 * New flow:
 *   1. Rasterise the band ONCE and run `locateActivePill` (cheap geometry).
 *   2. Pill region LOCATABLE (score ≥ MIN_PEAK_SCORE) → the geometric per-slot
 *      path (per-slot OCR → pin by slot position, with Fix-C no-hue-selection
 *      and the Perfect-vs-Purified disambiguation) is authoritative. This now
 *      covers single-keyword AND zero-keyword frames too. The real
 *      `location.score` is surfaced as `peakScore` and the geometric `source`
 *      (`active-pill-text` / `active-pill-text-strong` / `none`) flows through,
 *      so traces are honest. A locatable pill that pins NO variant returns an
 *      honest `none` (HOLD) — never a hue guess.
 *   3. Pill region NOT locatable (score < MIN_PEAK_SCORE: PiP overlap, scroll
 *      transition, anchor off the capsule) → fall back to the flat-band
 *      single-OCR `classifyVariantChip` so a clean single keyword can still be
 *      read when geometry can't find the capsule. This is the FALLBACK only.
 *
 * All `classifyActivePill` safeties (MIN_PEAK_SCORE gate, Perfect-vs-Purified,
 * Fix C no-hue-selection, confidence tiers) are preserved exactly — they live in
 * `classifyActivePill`, which the geometric path calls unchanged.
 */
export async function classifyActivePillBand(
  img: HTMLImageElement,
  bandYFrac: number,
  bandHFrac: number,
  /** Page-colour family for the colour-impossibility veto inside classifyActivePill. */
  pageColourFamily?: string | null,
  /**
   * perf/scan-efficiency: optional per-session pill-OCR pHash cache. When
   * supplied, the pill ROI is perceptually hashed and, if it matches the last
   * frame's ROI within the Hamming threshold, the cached Vision result is
   * returned WITHOUT re-running Vision. When omitted (one-off screenshot OCR,
   * tests) the classifier runs Vision every frame exactly as before.
   */
  cache?: PillOcrCache,
): Promise<{ chip: VariantChipSignal; diag: ActivePillClassification }> {
  // perf/scan-efficiency: pHash short-circuit. Compute a perceptual hash of the
  // pill ROI (the same band that would be fed to Vision) and, on a cache hit,
  // return the previously-computed Vision result. The pill band is screen-fixed
  // while scrolling, so consecutive frames hash near-identically and skip Vision
  // entirely. The cache stores the Vision result, not a heuristic, so a hit is
  // byte-identical to re-running Vision on the unchanged pill.
  let pillPHash: bigint | null = null;
  if (cache) {
    const roi = pillRoiImageData(img, bandYFrac, bandHFrac);
    if (roi) {
      pillPHash = computePillPHash(roi);
      const hitDistance = cache.distanceToCurrent(pillPHash);
      const hit = cache.lookup(pillPHash);
      if (hit) {
        // eslint-disable-next-line no-console
        if (DEX_SCAN_VERBOSE)
          console.log("[dex-scan] pill-ocr cache hit", {
            phash: pillPHash.toString(16),
            framesSinceLastMiss: cache.stats().hits,
            hammingDistance: hitDistance,
          });
        return hit;
      }
    }
  }

  const result = await runActivePillBandVision(img, bandYFrac, bandHFrac, pageColourFamily);

  if (cache && pillPHash !== null) {
    const previousPhash = cache.currentPhash();
    // Distance to the entry that is about to be REPLACED (the previous miss's
    // ROI), for telemetry — computed before store() overwrites it.
    const missDistance = cache.distanceToCurrent(pillPHash);
    cache.store(pillPHash, result);
    // eslint-disable-next-line no-console
    if (DEX_SCAN_VERBOSE)
      console.log("[dex-scan] pill-ocr cache miss", {
        phash: pillPHash.toString(16),
        previousPhash: previousPhash === null ? null : previousPhash.toString(16),
        hammingDistance: missDistance,
      });
  }

  return result;
}

/**
 * Rasterise the pill ROI (the band that would be sent to Vision) to ImageData
 * for perceptual hashing. Mirrors the crop geometry used by the Vision path
 * (`cropToDataUrl(img, 0, bandYFrac, 1, bandHFrac)` / the geometric locator's
 * band raster). Returns null when a 2D context is unavailable (non-DOM test
 * environments) so the caller simply skips the cache and runs Vision.
 */
function pillRoiImageData(
  img: HTMLImageElement,
  bandYFrac: number,
  bandHFrac: number,
): ImageData | null {
  const w = img.width;
  const y = Math.floor(img.height * bandYFrac);
  const h = Math.max(1, Math.floor(img.height * bandHFrac));
  if (w <= 0 || h <= 0) return null;
  const canvas = document.createElement("canvas");
  canvas.width = w;
  canvas.height = h;
  const ctx = canvas.getContext("2d", CANVAS_READBACK);
  if (!ctx) return null;
  ctx.drawImage(img, 0, y, w, h, 0, 0, w, h);
  return ctx.getImageData(0, 0, w, h);
}

/**
 * The original classifyActivePillBand body — the actual Vision-backed pill-band
 * classifier. Extracted verbatim so the public classifyActivePillBand can wrap
 * it with the pHash cache short-circuit without touching OCR correctness.
 */
async function runActivePillBandVision(
  img: HTMLImageElement,
  bandYFrac: number,
  bandHFrac: number,
  pageColourFamily?: string | null,
): Promise<{ chip: VariantChipSignal; diag: ActivePillClassification }> {
  // 1. Geometry first: rasterise the band and try to locate the active pill.
  const location = locateBandActivePill(img, bandYFrac, bandHFrac);

  // 2. Locatable → geometric per-slot path is PRIMARY (all keyword counts).
  if (location && location.score >= MIN_PEAK_SCORE) {
    return geometricActivePillBand(img, bandYFrac, bandHFrac, location, pageColourFamily);
  }

  // 3. Not locatable → flat-band single-OCR FALLBACK. One read over the whole
  //    band; classifyVariantChip floors a clean single keyword to ≥0.7 so it
  //    still clears the apply gate, and its hue path stays the last resort
  //    before source:"none". peakScore carries the real (sub-threshold) location
  //    score when we had one, not a hardcoded 0, so device traces stay honest.
  const pillCrop = cropToDataUrl(img, 0.0, bandYFrac, 1.0, bandHFrac);
  const pillText = await runOcr(pillCrop, { precropped: true });
  const { hue, saturation } = dominantHue(img, 0.0, bandYFrac, 1.0, bandHFrac);
  if (DEX_SCAN_VERBOSE)
    console.log("[dex-scan] pill-ocr vision", {
      slotIndex: -1, // flat-band fallback: whole strip, no single active slot
      text: pillText.text,
      conf: pillText.conf,
      matched: null,
      cropDims: { band: "flat-fallback", bandYFrac, bandHFrac },
    });
  // Tag this as a geometry-FAILED flat-band read. The geometric locator could
  // not find the active capsule, so whatever `classifyVariantChip` returns is a
  // best-effort guess over the WHOLE pill strip — it cannot know which pill is
  // active. The screenshot upload resolver refuses to auto-write a `bandFallback`
  // read (it asks the user to confirm) so a non-active pill keyword (e.g. the
  // always-present leftmost "POKÉMON") can never silently become the saved Dex.
  const baseChip = classifyVariantChip(pillText.text, pillText.conf, hue, saturation);
  // 2026-08-30 audit: cap a geometry-failed whole-strip guess BELOW the
  // colour-veto-override bar. classifyVariantChip floors confidence at 0.7 and
  // a clean Vision read stamps 0.99, which used to let a first-match-wins
  // guess over the whole pill strip beat the page-colour veto (the
  // "multi-frame voting recovers it" rationale described a deleted engine -
  // Tier-1 commits on one frame now). A capped read may still win normal
  // authority resolution; it can never outrank the veto.
  const chip: VariantChipSignal = {
    ...baseChip,
    confidence: Math.min(
      baseChip.confidence,
      VARIANT_CONFIDENCE.CHIP_TEXT_VETO_OVERRIDE_THRESHOLD - 0.01,
    ),
    bandFallback: true,
  };
  return {
    chip,
    diag: {
      source: chip.source === "chip-text"
        ? "active-pill-text"
        : chip.source === "hue"
          ? "hue-fallback"
          : "none",
      variant: chip.variant,
      confidence: chip.confidence,
      slot: null,
      peakScore: location ? location.score : 0,
      peakHue: location && location.hue != null
        ? Math.round(location.hue)
        : hue == null
          ? null
          : Math.round(hue),
      hueAgrees: false,
    },
  };
}

/**
 * R2 helper: rasterise the supplied pill band and run the geometric active-pill
 * locator on it, returning the raw `ActivePillLocation` (or null when the band
 * can't be rasterised in this environment). Pure of OCR — the caller decides
 * whether the score clears MIN_PEAK_SCORE before paying for per-slot OCR.
 */
function locateBandActivePill(
  img: HTMLImageElement,
  bandYFrac: number,
  bandHFrac: number,
): ActivePillLocation | null {
  const bandW = img.width;
  const bandY = Math.floor(img.height * bandYFrac);
  const bandH = Math.max(1, Math.floor(img.height * bandHFrac));
  const canvas = document.createElement("canvas");
  canvas.width = bandW;
  canvas.height = bandH;
  const ctx = canvas.getContext("2d", CANVAS_READBACK);
  if (!ctx) return null;
  ctx.drawImage(img, 0, bandY, bandW, bandH, 0, 0, bandW, bandH);
  const rgba = ctx.getImageData(0, 0, bandW, bandH).data;
  return locateActivePill(rgba, bandW, bandH);
}

/**
 * PR-E7 geometric active-pill locator — used now ONLY as the multi-pill
 * disambiguation fallback (see classifyActivePillBand). Finds the *active* pill
 * by darker-fill geometry, OCRs each of the three on-screen slots independently,
 * and pins the active variant by slot position against the canonical PoGo pill
 * order. Theme hue at the peak is a sanity cross-check, not the sole signal.
 * When the active pill can't be located confidently (PiP overlap, scroll
 * transition), the result is `{ source: "none", variant: null, confidence: 0 }`
 * and the apply gate skips the frame.
 */
async function geometricActivePillBand(
  img: HTMLImageElement,
  bandYFrac: number,
  bandHFrac: number,
  precomputedLocation?: ActivePillLocation,
  pageColourFamily?: string | null,
): Promise<{ chip: VariantChipSignal; diag: ActivePillClassification }> {
  // 1. Locate the active pill (saturated dark capsule) inside the band. R2: the
  //    caller (classifyActivePillBand) may already have located it to decide on
  //    the geometric-primary route; reuse that to avoid a second raster + scan.
  let location = precomputedLocation;
  if (!location) {
    const located = locateBandActivePill(img, bandYFrac, bandHFrac);
    if (!located) {
      return {
        chip: { source: "none", variant: null, confidence: 0 },
        diag: {
          source: "none", variant: null, confidence: 0, slot: null,
          peakScore: 0, peakHue: null, hueAgrees: false,
        },
      };
    }
    location = located;
  }

  // 3. Untrusted peak → return early with a `none` signal. The downstream
  //    apply gate will skip the frame.
  if (location.score < MIN_PEAK_SCORE) {
    const result = classifyActivePill(location, { left: "", middle: "", right: "" }, pageColourFamily);
    return {
      chip: { source: "none", variant: null, confidence: 0 },
      diag: result,
    };
  }

  // 4. OCR each of the three on-screen slots from data URLs of their
  //    horizontal sub-bands. Slot widths are 1/3 of the band each. We could
  //    crop to the *located* pill's width to be tighter, but slot-thirds
  //    keep neighbour text fully inside the right slot crop, which is what
  //    the pinning algorithm needs to count neighbour agreement.
  //
  //    Vision-only trial: each slot is upscaled (PILL_SLOT_OCR_UPSCALE) and
  //    padded (PILL_SLOT_OCR_PAD_X_FRAC) via cropSlotForOcr so SHORT labels
  //    (XXL/XXS/100%/LUCKY) carry enough pixels for Vision. The binarisation
  //    step was removed — Vision reads the raw upscaled crop.
  const slotCrops = [
    cropSlotForOcr(img, 0.0, bandYFrac, 1 / 3, bandHFrac),
    cropSlotForOcr(img, 1 / 3, bandYFrac, 1 / 3, bandHFrac),
    cropSlotForOcr(img, 2 / 3, bandYFrac, 1 / 3, bandHFrac),
  ];
  const [leftOcr, middleOcr, rightOcr] = await Promise.all(
    slotCrops.map((u) => runOcr(u, { precropped: true })),
  );
  if (DEX_SCAN_VERBOSE) {
    [leftOcr, middleOcr, rightOcr].forEach((o, slotIndex) =>
      console.log("[dex-scan] pill-ocr vision", {
        slotIndex,
        text: o.text,
        conf: o.conf,
        matched: null, // classification happens below in classifyActivePill
        cropDims: { band: "slot-third", bandYFrac, bandHFrac },
      }),
    );
  }

  // 5. Classify.
  const result = classifyActivePill(location, {
    left: leftOcr.text,
    middle: middleOcr.text,
    right: rightOcr.text,
  }, pageColourFamily);

  // 6. Adapt to the VariantChipSignal shape so the rest of pokedexOcr.ts +
  //    dexLiveFrameAdapter.ts keep working unchanged. The source label
  //    deliberately reuses the legacy strings: "chip-text" for any active-pill
  //    text resolution (strong or weak), "hue" for the hue-only fallback, and
  //    "none" for the skip-frame case. Confidence flows through verbatim so
  //    the apply gate's 0.6/0.9 thresholds keep their meaning.
  let chipSource: VariantChipSignal["source"];
  switch (result.source) {
    case "active-pill-text-strong":
    case "active-pill-text":
    case "active-pill-neighbours":
      chipSource = "chip-text";
      break;
    case "hue-fallback":
      chipSource = "hue";
      break;
    default:
      chipSource = "none";
  }
  return {
    chip: {
      source: chipSource,
      activePillSource: result.source,
      variant: result.variant,
      confidence: result.confidence,
      hueAgrees: result.hueAgrees,
    },
    diag: result,
  };
}

// Vision-only trial: readColourNarrowedPill AND colourNarrowedBand were deleted.
// They implemented a Tesseract-primary narrow-lexicon second-chance re-read for
// multi-variant families. With Vision reading the full pill on a raw (non-
// binarised) crop, that redundant recovery path is gone. The colour veto still
// runs at the classifier layer via COLOUR_FAMILY_ALLOWED_VARIANTS.

/**
 * Build 2 / v1.4.6 — resolve which PoGo dex the user is SCANNING from the chip
 * row above the grid. Chip-text OCR is the primary cue; dominant-hue is the
 * fallback. The result drives `apply observations` routing — never the Nexus
 * app's locally-selected filter.
 *
 * DEPRECATED at the production call site as of PR-E7: the live frame pipeline
 * now uses `classifyActivePillBand` above (active-pill geometry + per-slot OCR +
 * neighbour pinning). This function is kept exported for backward compatibility
 * with existing unit tests and as a documented reference for the old contract.
 * It must NOT be used in new code paths.
 *
 * Exported for tests.
 */
export function classifyVariantChip(
  chipText: string,
  chipTextConfidence: number,
  hue: number | null,
  saturation: number,
): VariantChipSignal {
  // 1. Chip text wins when it confidently names a variant.
  for (const [variant, re] of CHIP_VARIANT_KEYWORDS) {
    if (re.test(chipText)) {
      // OCR conf (0-100) scaled to 0..1, floored so a clean keyword still
      // clears the 0.6 apply gate even when the engine under-reports.
      const conf = Math.max(0.7, Math.min(0.99, chipTextConfidence / 100));
      return { source: "chip-text", variant, confidence: conf };
    }
  }
  // 2. Hue fallback.
  const hueVariant = variantFromChipHue(hue, saturation);
  if (hueVariant) {
    // Hue is a weaker cue than text; perfect-by-low-saturation is the least
    // certain (an off-white main theme can also read low-sat), so keep it just
    // under the apply gate unless saturation strongly indicates a colour theme.
    const strong = hueVariant !== "perfect" && saturation >= 0.35;
    return { source: "hue", variant: hueVariant, confidence: strong ? 0.65 : 0.5 };
  }
  return { source: "none", variant: null, confidence: 0 };
}

/**
 * Resolve the variant the per-cell pixel classifier should run under.
 *
 * THIS is the resolver that tags the COMMITTED cell variant (`frameVariant`,
 * logged in `cell classify`, threaded into every matchCellToSprite /
 * isBinaryVariant call). It used to be a hand-maintained COPY of the adapter's
 * `resolveTrustedVariant` — and #444 lowered the adapter to 0.80 while this copy
 * stayed at 0.9 with no veto, which IS the XXL-on-cyan zero-commit bug (every
 * cell ran the wrong shiny sprite test → applied:0). Both now delegate to the
 * ONE unified resolver (`variantResolution.resolveVariant`), so the committed
 * cell variant and the routed variant can never diverge again.
 *
 * Fix B: a HOLD/SKIP outcome from the unified resolver (variant null) means
 * "commit NOTHING this frame". The old `?? appStateVariant` silently substituted
 * the STALE app_state variant (e.g. a frozen "shiny" filter from a prior page),
 * which then ran the wrong sprite fingerprint and dropped/miscommitted cells.
 * We now PROPAGATE the null: `extractPokedexFrame` skips the whole cell sweep
 * when this returns null, so no cells classify and nothing commits. The
 * legitimate sticky path is handled INSIDE resolveVariant (app_state is accepted
 * when it isn't vetoed); only the outer stale-substitution was the bug.
 *
 * Pure and exported for tests. Returns null to signal HOLD/SKIP.
 */
/**
 * Confidence stamped on a colour-narrowed pill re-read when it is fed to the
 * unified resolver as a synthetic chip-text read on the per-cell COMMIT path.
 * Mirrors the routing-side `COLOUR_NARROWED_PILL_CONFIDENCE` (0.9) in
 * dexLiveFrameAdapter so both resolvers weigh the same recovered signal
 * identically. Above CHIP_TEXT_OVER_MAIN_THRESHOLD (0.7) so a recovered XXS read
 * wins over a stale "pokemon" pill, below CHIP_TEXT_VETO_OVERRIDE_THRESHOLD
 * (0.95) so it still defers to the colour-impossibility veto.
 */
const CELL_COMMIT_COLOUR_NARROWED_CONFIDENCE = 0.9;

export function resolveFrameVariant(
  opts: Pick<ExtractPokedexFrameOptions, "variant">,
  variantChip?: VariantChipSignal,
  // Page-colour family for THIS frame (e.g. "dex_cyan"), from the hook's pre-OCR
  // colour tracker. VETO/CORROBORATION ONLY in the unified resolver. Undefined /
  // non-dex families make the veto inert (the upload path is unchanged).
  pageColourFamily?: string | null,
  // WIDE colour-narrowed pill re-read for THIS frame (a family-FILTERED pill OCR
  // match), or null when none matched / not attempted. Consulted by the unified
  // resolver ONLY for a multi-variant colour family when app_state is vetoed and
  // the active-pill read could not supply an allowed variant (the G-Max dex_pink
  // hold repro). Re-validated against the colour veto inside resolveVariant.
  colourNarrowedVariant?: PokedexVariant | null,
): PokedexVariant | null {
  const appStateVariant: PokedexVariant =
    opts.variant && ALL_VARIANTS.includes(opts.variant) ? opts.variant : "pokemon";

  // Only a chip-TEXT read with a valid variant feeds the unified resolver as the
  // primary positive selector; a hue-source chip never selects (corroboration
  // only), so it is intentionally dropped here.
  let chipText: ChipTextRead | null =
    variantChip &&
    variantChip.source === "chip-text" &&
    variantChip.variant != null &&
    ALL_VARIANTS.includes(variantChip.variant)
      ? {
          variant: variantChip.variant,
          confidence: variantChip.confidence,
          hueAgrees: variantChip.hueAgrees,
          source: variantChip.activePillSource,
        }
      : null;

  // colour-narrowed pill re-read PARITY (XXS missed-apply fix, telemetry
  // 2026-06-22). When the active-pill read FAILED (variantChip.source !==
  // "chip-text") but the WIDE family-filtered colour-narrowed re-read recovered a
  // variant, the ROUTING side (useDexLiveScan → resolveTrustedVariant) feeds that
  // recovered variant to resolveVariant AS a chip-text read, so the frame ROUTES
  // and WRITES under it. The per-cell COMMIT resolver must do the SAME, or it
  // silently classifies every cell under the stale app_state variant: on a cyan
  // XXS page with a frozen "pokemon" pill (which is NOT colour-vetoed on cyan),
  // the dedicated `colourNarrowedVariant` resolveVariant input is never consulted
  // (it only fires under appStateVetoed), so cells ran the "pokemon" sprite
  // fingerprint and real XXS sprites were dropped as chroma-mismatch / read as
  // silhouette. Synthesising the chip-text read here keeps the committed cell
  // variant equal to the routed one — the cross-resolver invariant this module
  // exists to guarantee. The colour-narrowed match is already family-filtered to
  // allowed tokens and resolveVariant re-validates it against the colour veto, so
  // an impossible variant can never be committed.
  if (chipText == null && colourNarrowedVariant != null) {
    chipText = {
      variant: colourNarrowedVariant,
      confidence: CELL_COMMIT_COLOUR_NARROWED_CONFIDENCE,
    };
  }

  const resolution = resolveVariant({
    appStateVariant,
    chipText,
    pageColourFamily,
    colourNarrowedVariant,
  });
  // HOLD (resolution.variant == null) propagates as null — NEVER fall back to the
  // stale app_state. The caller drops the frame.
  return resolution.variant;
}

// ── OCR ───────────────────────────────────────────────────────────────────

// PR-G abstraction: route through ocrEngine (Apple Vision on iOS, ML Kit
// on Android, Tesseract on web).
import { loadFrameImage } from "./frameImageCache";
import { filterToWhitelist, recognizeDataUrl, recognizeRegions, type NumberBox, type OcrTextBlock } from "./ocrEngine";
import { DEX_SCAN_VERBOSE } from "./dexScanLog";
import { frameAspectWarning } from "./frameGeometry";
import {
  cellRectFrac,
  numberRoiFrac,
  acceptCellNumberRoi,
  exclusionZones,
  dexGridRows,
  gridColumnsForVariant,
  GRID_COLUMNS,
  GRID_ROWS,
  type FracRect,
} from "./dexOcrRoi";
import { dexLayoutForFrame, type DexScreenLayout } from "./dexLayout";
import {
  detectGridBottomFrac,
  detectFilterPillRowFrac,
  ANCHOR_TRUST_CONFIDENCE,
  rowEnergyProfile,
} from "./dexAnchorDetector";
import {
  containsNexusOverlayMarker,
  detectNexusOverlayRect,
  overlayOverlapFraction,
  padOverlayRect,
  MAX_TOLERATED_OVERLAY_OVERLAP,
  MAX_SPRITE_BODY_OVERLAY_OVERLAP,
  type NexusOverlayRect,
} from "./nexusOverlayGuard";
import {
  parseTopPanel,
  decideTopPanelAction,
  type TopPanelDecision,
} from "./topPanelOcr";
import type { TabPillLine, TintSample } from "./variantSignals";
import type { PokedexVariant } from "./pogoPokedexVariantClassifier";
import {
  dexCellNumberPositionFromRgba,
  classifyNumberVerticalPosition,
  analyzeSpriteHues,
  analyzeSpriteChroma,
  detectFastScrollPill,
  hasAuraMarker,
  PURIFIED_FOREGROUND_PIXEL_FLOOR,
  type NumberVerticalPosition,
  type PillRegion,
} from "./ocrPixelOps";
import {
  ALL_VARIANTS,
  isDexPageColourFamily,
  soleVariantForColourFamily,
  deriveVetoOnlyFamily,
  type DexPageColourFamily,
} from "./pogoPokedexVariantClassifier";
import { resolveVariant, VARIANT_CONFIDENCE, type ChipTextRead } from "./variantResolution";
import { dexIdToRegion } from "./dexIdToRegion";
import { classifyCellPresence, isBinaryVariant } from "./cellPresence";
import {
  estimateGridPhase,
  phaseSampleFromBox,
  type GridPhaseSample,
  estimateGridPhaseFromEdges,
} from "./dexGridPhase";

/**
 * Grid phase application (2026-09-08). A measured lattice error below this (in
 * cell heights) is glyph-box jitter, not a scroll offset; above it the lattice
 * is rebuilt shifted before the cells are read.
 */
export const PHASE_APPLY_MIN_CELLS = 0.05;
/**
 * Single-state tie-break. When every number on the frame sits at one height
 * the estimator cannot tell "centred numbers, lattice a little high" from
 * "bottom-pinned numbers, lattice a little low" (the two explanations sit
 * 0.35 cells apart), so it applies nothing and the frame stays misread. The
 * sprites break the tie: a cell whose number is bottom-pinned HAS a sprite.
 * With at least this many samples, at most this spread between them, and a
 * clear sprite majority either way, the matching anchor is used.
 */
export const PHASE_TIEBREAK_MIN_SAMPLES = 4;
export const PHASE_TIEBREAK_MAX_SPREAD = 0.2;
export const PHASE_TIEBREAK_SPRITE_MAJORITY = 0.6;
const PHASE_ANCHOR_CENTRE_REL = 0.5;
const PHASE_ANCHOR_BOTTOM_REL = 0.85;
/** A default-lattice prefetch with fewer samples than this is "blind". */
export const PHASE_BLIND_MAX_SAMPLES = 2;
/** ...but only when it actually asked for enough bands to have read something. */
export const PHASE_BLIND_MIN_ROIS = 6;
/** Where the default lattice's blind zone is: half a cell off. */
export const PHASE_PROBE_OFFSET_CELLS = 0.5;
/** Samples the half-cell probe must read before its lattice is trusted. */
export const PHASE_PROBE_MIN_SAMPLES = 4;
import {
  classifyActivePill,
  locateActivePill,
  MIN_PEAK_SCORE,
  type ActivePillClassification,
  type ActivePillLocation,
} from "./activePillClassifier";
import { detectQuickScrollPill } from "./pillDetect";
import { computePillPHash, type PillOcrCache } from "./pillOcrCache";
import { CANVAS_READBACK } from "./canvasReadback";

// b13c.4: gate the one-time `[dex-scan] tile geometry` log so it fires once per
// page-load instead of on every frame (the live loop calls extractPokedexFrame
// ~1 Hz). Reset between scans is unnecessary — the geometry is build-static.
let loggedTileGeometry = false;

/** Test-only escape hatch to re-arm the one-time tile-geometry log. */
export function __resetTileGeometryLog(): void {
  loggedTileGeometry = false;
}

// ---------------------------------------------------------------------------
// Y6 perf: cell-content hash cache + skip-on-stable.
//
// Heavy-dex live scanning re-runs Tesseract OCR (~30-80ms/cell) and the pixel
// classifier on every cell every frame, even when a cell is unchanged across
// many consecutive frames. We hash the bottom (dex-number) band of each cell
// and, when the hash matches the previous frame for that cellIdx, return the
// cached classification + OCR read and skip both heavy steps entirely. After
// STABILITY_THRESHOLD identical-hash frames a cell is "stable"; we still hash
// it (cheap) but trust the cached result. A forced re-classify every
// FORCE_REFRESH_INTERVAL frames bounds how long a stuck cache can go stale.
//
// Cell-content cache tuning - revisit after device testing.
const CELL_HASH_BAND_TOP_FRAC = 0.7;     // hash from cell-y 70% to bottom (number band)
const CELL_HASH_BAND_BOTTOM_FRAC = 1.0;
const CELL_HASH_STRIDE = 4;              // sample every 4th pixel for speed
const STABILITY_THRESHOLD = 5;           // frames of identical hash before locking
const FORCE_REFRESH_INTERVAL = 30;       // force re-classify every N frames (anti-staleness)
/**
 * Numbers the previous frame read (-1 before the first). A frame that read
 * NOTHING must not seed the per-cell cache for the next one: on a held screen
 * the band hashes do not change, so a blind first read (a lattice half a cell
 * off) was served back from the cache frame after frame and the screen never
 * got a second look (2026-09-08 device log: 287 of 399 OCR frames asked for
 * zero ROIs). The cache is dropped at the start of the frame after a blind one.
 */
let extractLastNumbersRead = -1;

/**
 * The full per-cell result the cache stores and replays on a hit. Carries the
 * OCR reads, the pixel classifier output, and the cell rect (frac coords) used
 * to detect a cellIdx remap (geometry shifted under the same index).
 */
interface CachedCellResult {
  dexNumber: number | null;
  speciesName: string | null;
  classification: DexCellClassification | null;
  classificationSkipReason: ClassificationSkipReason | null;
  numberPosition: NumberVerticalPosition | "no-canvas";
  numberAnchored?: boolean;
  spriteState: SpriteState | "none";
  hueVariance: number | null;
  luminanceVariance: number | null;
  foregroundPixels: number | null;

  foregroundFraction: number | null;
  chromaFraction: number | null;
  finalClassification: "caught" | "not_caught" | "skipped";
  caught: boolean;
  seen: boolean;
  isNew: boolean;
  dexConf: number;
  speciesConf: number;
  /** PR-E6-OCR2: sprite chroma matcher fields, replayed on a cache hit. */
  spriteVerdict?: "caught" | "missing" | "uncertain";
  spriteConfidence?: number;
  /** Sprite-matcher reason, replayed on a cache hit so the live write veto
   *  sees the same signal a fresh classify would (the cache-replay push must
   *  carry every field the apply layer gates on). */
  spriteReason?: string | null;
  /** Presence-classifier chroma fraction, replayed on a cache hit so the live
   *  write layer sees the same colour-fraction a fresh classify would. */
  spriteChromaFraction?: number | null;
  /** Presence-classifier body statistics, replayed on a cache hit for the same
   *  reason as the fields above — see PokedexCellData.spriteBodyFrac. */
  spriteBodyFrac?: number | null;
  spriteLumStd?: number | null;
  rect: { x: number; y: number; w: number; h: number };
}

interface CellCacheEntry {
  hash: number;
  hitCount: number;
  lastResult: CachedCellResult;
}

// Keyed by cellIdx. Module-level so it survives across the ~1Hz per-frame
// extractPokedexFrame calls within one scan. MUST be cleared when a new scan
// session starts (see resetCellContentCache, called from useDexLiveScan) and
// when the frame variant changes (handled inside extractPokedexFrame, since a
// "perfect" cell hash must not be replayed for a "lucky" read at the same
// cellIdx).
const cellContentCache = new Map<number, CellCacheEntry>();
let cellCacheVariant: PokedexVariant | null = null;
let extractFrameCounter = 0;

/**
 * Clear the cell-content cache. Called on scan-session start so a prior
 * session's per-cell hashes cannot be replayed against a fresh scan.
 */
export function resetCellContentCache(): void {
  cellContentCache.clear();
  cellCacheVariant = null;
  extractFrameCounter = 0;
}

/**
 * FNV-1a hash over a strided sample of the cell's number band (bottom 30% by
 * default). Pure integer math: drawn into a small canvas, then every Nth pixel
 * (stride CELL_HASH_STRIDE) folds its R/G/B bytes into the hash. Returns null
 * when no 2D context is available (SSR / non-DOM) so callers can skip the cache
 * and run the original path. A 0 result is a valid hash (empty/black band).
 */
function numberBandHash(
  img: HTMLImageElement,
  cellXFrac: number,
  cellYFrac: number,
  cellWFrac: number,
  cellHFrac: number,
): number | null {
  const { w: iw, h: ih } = imgDims(img);
  const bandYFrac = cellYFrac + cellHFrac * CELL_HASH_BAND_TOP_FRAC;
  const bandHFrac = cellHFrac * (CELL_HASH_BAND_BOTTOM_FRAC - CELL_HASH_BAND_TOP_FRAC);
  const x = Math.floor(iw * cellXFrac);
  const y = Math.floor(ih * bandYFrac);
  const w = Math.max(1, Math.floor(iw * cellWFrac));
  const h = Math.max(1, Math.floor(ih * bandHFrac));
  const canvas = document.createElement("canvas");
  canvas.width = w;
  canvas.height = h;
  const ctx = canvas.getContext("2d", CANVAS_READBACK);
  if (!ctx) return null;
  ctx.drawImage(img, x, y, w, h, 0, 0, w, h);
  const data = ctx.getImageData(0, 0, w, h).data;
  // FNV-1a over a strided sample. Step is stride * 4 bytes (RGBA) so we touch
  // every CELL_HASH_STRIDE-th pixel. `>>> 0` keeps the accumulator an unsigned
  // 32-bit int after each multiply.
  let hash = 0x811c9dc5;
  const step = CELL_HASH_STRIDE * 4;
  for (let i = 0; i + 2 < data.length; i += step) {
    hash ^= data[i];
    hash = (hash * 0x01000193) >>> 0;
    hash ^= data[i + 1];
    hash = (hash * 0x01000193) >>> 0;
    hash ^= data[i + 2];
    hash = (hash * 0x01000193) >>> 0;
  }
  return hash >>> 0;
}

/**
 * Test-only OCR override. When set, `runOcr` returns the override's result for
 * the given crop instead of invoking the real Tesseract/Vision recognizer. This
 * is the seam the R2 routing test uses to feed deterministic per-slot text into
 * `classifyActivePillBand` without a real OCR engine. Production never sets it.
 */
type OcrRunner = (dataUrl: string, opts?: { whitelist?: string; precropped?: boolean }) => Promise<{ text: string; conf: number; box?: NumberBox; rawText?: string }>;
let ocrRunnerOverride: OcrRunner | null = null;
export function __setOcrRunnerForTests(runner: OcrRunner | null): void {
  ocrRunnerOverride = runner;
}

async function runOcr(
  dataUrl: string,
  opts?: { whitelist?: string; precropped?: boolean },
): Promise<{ text: string; conf: number; box?: NumberBox }> {
  if (ocrRunnerOverride) return ocrRunnerOverride(dataUrl, opts);
  if (!dataUrl) return { text: "", conf: 0 };
  return recognizeDataUrl(dataUrl, opts);
}

/** Digit whitelist for the per-cell dex-number read. */
const DEX_NUMBER_WHITELIST = "#0123456789";

/**
 * SCAN-SPEED (2026-07-21). Read one cell's number band DIRECTLY off the frame.
 *
 * The old call was `runOcr(cropToDataUrl(img, roi))`, which cost, per cell:
 *   1. a PNG encode of the cell crop (`cropToDataUrl`),
 *   2. a PNG decode of that crop back into an <img> (`recognizeDataUrl`), and
 *   3. a SECOND encode — the batch engine's `fullFrameToDataUrl` of the crop —
 * before the bridge call ever happened. Nine of those per frame is where the
 * device log's 1066ms mean OCR time went, against a 1000ms capture interval:
 * the pipeline never caught up and shed 238 frames as `duplicate_in_flight`.
 *
 * `recognizeRegions` already takes the frame plus normalised ROI rects — the
 * shape DexVisionOcr was built for (one decode, one `perform`, ROIs as
 * coordinates). Handing it the ROI we already computed removes all three costs;
 * the frame is encoded once per frame and memoised in ocrEngine.
 *
 * BOX SEMANTICS ARE PRESERVED. The native plugin returns the digit box
 * ROI-relative (see DexVisionOcr.swift), which is exactly what
 * `numberPositionFromBox` expects, and is what the old identity-region-on-a-crop
 * call produced too. One deliberate difference: the old path ran the identity
 * region through `clampRegionBelowStatusBar`, shaving 6% off the TOP OF THE
 * NUMBER BAND itself (the clamp is meant for whole-frame reads). A true cell ROI
 * sits far below the status bar, so the clamp is now the no-op it was always
 * meant to be — the box is measured against the full band, matching the
 * convention `NUMBER_BOX_BOTTOM_MIN` was documented against.
 */
async function runCellNumberOcr(
  img: HTMLImageElement,
  roi: FracRect,
): Promise<CellNumberRead> {
  // Test seam: the override consumes a data URL, so only pay for the crop when
  // a test has actually installed one.
  if (ocrRunnerOverride) {
    return ocrRunnerOverride(
      cropToDataUrl(img, roi.x, roi.y, roi.w, roi.h),
      { whitelist: DEX_NUMBER_WHITELIST },
    );
  }
  const [raw] = await recognizeRegions(img, [roi], { whitelist: DEX_NUMBER_WHITELIST });
  if (!raw) return { text: "", conf: 0 };
  // `recognizeRegions` normalises confidence to 0-1; per-cell code wants 0-100.
  return {
    text: raw.text,
    conf: Math.round((raw.confidence ?? 0) * 100),
    box: raw.box,
    rawText: rawBlocksText(raw.blocks),
  };
}

/** One cell's number read, keyed back to the cell that asked for it. */
type CellNumberRead = {
  text: string;
  conf: number;
  box?: NumberBox;
  /**
   * Every observation's text in the ROI BEFORE the digit whitelist, joined.
   * The whitelist is what turns the storage box's "CP 282" into a dex number
   * 282; this is where the "CP" survives to say so (2026-09-18).
   */
  rawText?: string;
};

/**
 * MAIN-THREAD YIELD BUDGET (2026-08-11). Longest the cell loop may hold the JS
 * thread before handing it back. ~16ms is one 60Hz frame, so the WebView always
 * gets a paint slot and the PiP bridge / React commits are never starved for
 * longer than one frame plus the cell in progress.
 */
const CELL_LOOP_YIELD_MS = 16;

/**
 * Hand the main thread back so the WebView can paint.
 *
 * WHY THIS EXISTS. The number-ROI batch below was a throughput win that made
 * RESPONSIVENESS worse, and the 2026-08-11 device trace shows both halves:
 * frameMeanMs fell 748 -> 552 while the scan started feeling "clunky", with
 * "visible pauses when it was thinking". The cause is that the per-cell
 * `await runCellNumberOcr` used to be the cell loop's ONLY suspension point, and
 * it fired up to 12 times a frame. Each await was a real async bridge round trip,
 * so between every cell the WebView got to paint, the PiP status push got to
 * flush and React got to commit. Batching the reads deleted all twelve: with the
 * read served from a Map, `prefetchedReads.get(idx) ?? await …` never evaluates
 * the await, and the whole loop — twelve `classifyCellAt` passes, each
 * rasterising a cell to its own canvas and walking its pixels — collapsed into
 * ONE uninterrupted synchronous block. Less total work, delivered in a way that
 * janks: exactly what "stalled often" describes.
 *
 * A MACROTASK is required, not a microtask: `await Promise.resolve()` drains in
 * the same task and never yields to the compositor. MessageChannel is the
 * cheapest real macrotask (setTimeout is clamped to ~4ms, which we would pay on
 * every yield), with setTimeout kept as the fallback where it is unavailable.
 */
function yieldToEventLoop(): Promise<void> {
  return new Promise<void>((resolve) => {
    if (typeof MessageChannel === "function") {
      const ch = new MessageChannel();
      ch.port1.onmessage = () => {
        ch.port1.close();
        resolve();
      };
      ch.port2.postMessage(0);
      return;
    }
    setTimeout(resolve, 0);
  });
}

/**
 * Time-sliced yielder: hands the thread back only once the caller has actually
 * held it for CELL_LOOP_YIELD_MS, so a cheap frame (every cell a cache hit) pays
 * nothing and an expensive one is chopped into ~16ms slices.
 */
function createMainThreadSlicer(): {
  yieldIfBusy: () => Promise<void>;
  /** Wall-clock ms this frame spent suspended at a yield. */
  yieldedMs: () => number;
} {
  const now = () =>
    typeof performance !== "undefined" ? performance.now() : Date.now();
  let lastYield = now();
  let yielded = 0;
  return {
    async yieldIfBusy(): Promise<void> {
      if (now() - lastYield < CELL_LOOP_YIELD_MS) return;
      const before = now();
      await yieldToEventLoop();
      lastYield = now();
      yielded += lastYield - before;
    },
    yieldedMs: () => Math.round(yielded),
  };
}

/**
 * SCAN-SPEED (2026-08-11). Read EVERY cell's number band in ONE native call.
 *
 * `runCellNumberOcr` above already removed the per-cell encode/decode round
 * trip, but it still issued one `recognizeRegions` call per cell — and the cell
 * loop awaited them one at a time. On a 4-col page that is up to 16 native calls
 * per frame, each of which pays a full base64 handoff, a `CGImage` decode and its
 * own `VNImageRequestHandler.perform`, all queued through `ocrEngine`'s 2-slot
 * semaphore. The 2026-08-10 device trace measured `ocrMeanMs 746.7` against a
 * `frameMeanMs` of 748.1 — OCR is essentially the entire frame budget — with
 * `busyMs` (778,751) exceeding `sessionWallMs` (574,092), i.e. frames overlapping
 * and queueing behind each other.
 *
 * `DexVisionOcr.recognizeDigits` was built for exactly this: it takes the frame
 * plus an ARRAY of ROIs, decodes once, and runs one `VNRecognizeTextRequest` per
 * ROI inside a single `VNImageRequestHandler.perform([...])`. Handing it all the
 * cell ROIs at once collapses N decodes + N performs + N semaphore acquisitions
 * into one of each. Nothing about the reads themselves changes: same ROI rects,
 * same whitelist, same clamping, same ROI-relative box semantics.
 *
 * The `ocrRunnerOverride` seam is preserved per-ROI so the existing suites keep
 * exercising the loop (and its ordering/cache interaction) unchanged.
 */
async function runCellNumberOcrBatch(
  img: HTMLImageElement,
  rois: FracRect[],
): Promise<CellNumberRead[]> {
  if (rois.length === 0) return [];
  if (ocrRunnerOverride) {
    // Sequential, exactly as the pre-batch loop called it, so a test override
    // that counts calls or returns per-ROI fixtures behaves identically.
    const out: CellNumberRead[] = [];
    for (const roi of rois) out.push(await runCellNumberOcr(img, roi));
    return out;
  }
  const raws = await recognizeRegions(img, rois, { whitelist: DEX_NUMBER_WHITELIST });
  return rois.map((_, i) => {
    const raw = raws[i];
    if (!raw) return { text: "", conf: 0 };
    return {
      text: raw.text,
      conf: Math.round((raw.confidence ?? 0) * 100),
      box: raw.box,
      rawText: rawBlocksText(raw.blocks),
    };
  });
}

/** The unfiltered observation texts of a ROI, or undefined when the engine gave none. */
function rawBlocksText(blocks: OcrTextBlock[] | undefined): string | undefined {
  if (!blocks || blocks.length === 0) return undefined;
  return blocks.map((b) => (b.text || "").trim()).filter(Boolean).join(" ");
}

/** A cell whose number band reads as GO's "CP 282" label: the storage box, not a dex cell. */
const STORAGE_BOX_CELL_RE = /\bCP\s*\d/i;
/** Cells reading as CP labels before the frame is the storage box. */
export const STORAGE_BOX_MIN_CELLS = 3;
/**
 * Does a cell's UNFILTERED number-band text say "CP <number>"? A dex cell
 * prints "#0282"; only the storage box prints a CP over every sprite.
 */
export function isStorageBoxCellText(rawText: string | undefined | null): boolean {
  return rawText != null && STORAGE_BOX_CELL_RE.test(rawText);
}

/**
 * The purely GEOMETRIC reasons a cell's number band is not worth OCR'ing this
 * frame, in the order the cell loop applies them. Extracted so the number-ROI
 * PREFETCH and the cell loop itself decide from one implementation instead of
 * two copies that could drift — the prefetch must select exactly the cells the
 * loop will go on to read, or a cell would fall back to its own single-ROI call.
 *
 * Pure: no counters, no logging, no cache access. The loop still owns every
 * counter and `[dex-scan] cell classify` log, keyed off `reason`.
 */
type CellGeometryGateReason =
  | "top_row"
  | "bottom_ui_band"
  | "roi_rejected"
  | "number_overlay_overlap"
  | "sprite_overlay_overlap";

interface CellGeometryGate {
  accept: boolean;
  reason: CellGeometryGateReason | null;
  /** The number-band ROI, once the cell is far enough through to have one. */
  roi: FracRect | null;
  /** Populated for `roi_rejected` so the loop can split pip-obscured out. */
  roiDecision: ReturnType<typeof acceptCellNumberRoi> | null;
  /** Populated for `sprite_overlay_overlap` so the loop can log which surface. */
  spriteOverPip: number;
  spriteOverCard: number;
}

/**
 * Row 0 is skipped only while it can still sit under the header / the Nexus
 * HUD card (bottom ~0.21 of the frame on device). A lattice registered to the
 * card outlines can put a fully visible row there (2026-09-24: #065-#068 at
 * y 0.28 after a four-row page scroll), and that row never reaches rows 1-3.
 */
export const TOP_ROW_CLEAR_Y = 0.25;

export function gateCellNumberRoi(args: {
  row: number;
  cell: FracRect;
  zones: ReturnType<typeof exclusionZones>;
  bottomUiBand: { y: number } | null;
  nexusOverlayRect: NexusOverlayRect | null;
  pipRect: FracRect | null;
  /** The quick-scroll pill, fenced like the HUD card: its pale body is not a sprite. */
  scrollPillRect?: FracRect | null;
  /** Screen layout (lib/dexLayout); the phone values apply when omitted. */
  topRowClearY?: number;
  layout?: DexScreenLayout;
}): CellGeometryGate {
  const { row, cell, zones, bottomUiBand, nexusOverlayRect, pipRect, scrollPillRect } = args;
  const miss = (reason: CellGeometryGateReason, over?: Partial<CellGeometryGate>): CellGeometryGate => ({
    accept: false, reason, roi: null, roiDecision: null, spriteOverPip: 0, spriteOverCard: 0, ...over,
  });

  if (row === 0 && cell.y < (args.topRowClearY ?? TOP_ROW_CLEAR_Y)) return miss("top_row");

  if (bottomUiBand) {
    const sbTop = cell.y;
    const sbBot = cell.y + cell.h * 0.62;
    const cover =
      Math.max(0, Math.min(sbBot, 1) - Math.max(sbTop, bottomUiBand.y)) /
      Math.max(1e-6, sbBot - sbTop);
    if (cover > MAX_TOLERATED_OVERLAY_OVERLAP) return miss("bottom_ui_band");
  }

  const roi = numberRoiFrac(cell);

  const roiDecision = acceptCellNumberRoi(roi, zones, args.layout);
  if (!roiDecision.accept) return miss("roi_rejected", { roi, roiDecision });

  if (
    nexusOverlayRect &&
    overlayOverlapFraction(roi, nexusOverlayRect) > MAX_TOLERATED_OVERLAY_OVERLAP
  ) {
    return miss("number_overlay_overlap", { roi });
  }

  const spriteBodyRoi: FracRect = { x: cell.x, y: cell.y, w: cell.w, h: cell.h * 0.62 };
  const spriteOverPip = pipRect
    ? overlayOverlapFraction(spriteBodyRoi, padOverlayRect(pipRect))
    : 0;
  const spriteOverCard = nexusOverlayRect
    ? overlayOverlapFraction(spriteBodyRoi, padOverlayRect(nexusOverlayRect))
    : 0;
  const spriteOverPill = scrollPillRect
    ? overlayOverlapFraction(spriteBodyRoi, padOverlayRect(scrollPillRect))
    : 0;
  if (Math.max(spriteOverPip, spriteOverCard, spriteOverPill) > MAX_SPRITE_BODY_OVERLAY_OVERLAP) {
    return miss("sprite_overlay_overlap", { roi, spriteOverPip, spriteOverCard });
  }

  return { accept: true, reason: null, roi, roiDecision, spriteOverPip, spriteOverCard };
}

/**
 * VISION NUMBER-BOX ANCHOR (2026-06-28).
 *
 * The trustworthy replacement for the pixel-centroid number-position heuristic
 * (`dexCellNumberPositionFromRgba`). Apple Vision returns the recognised digit
 * glyphs' actual bounding box; its vertical centroid is a DIRECT geometric
 * measurement of where the number sits, with none of the glyph-detection
 * guesswork the pixel centroid relied on (which dragged caught numbers UP into
 * a false "center" read and applied 0 cells on-device).
 *
 * COORDINATE MAP. The box is normalised RELATIVE TO THE NUMBER-BAND ROI
 * (`numberRoiFrac`), which spans the lower 62% of the cell (cell-y 0.34..0.96).
 * PoGo prints the number either CENTRED in the cell (uncaught, cell-y ~0.5) or
 * pinned to the BOTTOM (caught, cell-y ~0.85). Mapped into ROI-relative space:
 *   - uncaught: (0.5  - 0.34) / 0.62 ≈ 0.26  (upper part of the ROI)
 *   - caught:   (0.85 - 0.34) / 0.62 ≈ 0.82  (lower part of the ROI)
 * A split at ROI-relative 0.5 separates them with a wide margin. Returns the
 * geometric position, or null when no usable box is present (caller then falls
 * back to the pixel-centroid path, preserving Tesseract/ML-Kit behaviour).
 * Pure; exported for tests.
 */
// Stage-1 anchor: a CAUGHT binary/three-state cell renders the dex number in
// the bottom band of the ROI; an uncaught cell floats it nearer the middle.
// Device ground-truth showed confirmed-caught numbers land at ROI-relative
// center >= 0.90, while genuine center reads sit well below. We lean heavier on
// OCR position by demanding the box-center fall in the BOTTOM 25% of the ROI to
// count as "bottom"; otherwise "center". Tighter than the old 0.5 split so a
// stray observation can no longer nudge a true-bottom read across the line.
export const NUMBER_BOX_BOTTOM_MIN = 0.75;
// (2026-08-30) The Stage-1 "binaryEmptyBelowUncaught" cross-check that lived
// here was DELETED as never-wired dead code. It keyed on
// cls.foregroundPixels, but classifyDexCell's center short-circuit returns
// foregroundPixels 0 for every centred read — including the real caught cells
// Vision misjudges as "center" — so wiring it would have re-created the
// 1.7.0(1) recall disaster (728/771 real catches rejected). The empty-binary
// class it targeted is handled where the signal is reliable instead:
// cellPresence's binary-flat-interior edge veto + the unconditional number
// mask (see classifyCellPresence).
export function numberPositionFromBox(
  box: NumberBox | undefined,
): NumberVerticalPosition | null {
  if (!box) return null;
  if (!Number.isFinite(box.y) || !Number.isFinite(box.h)) return null;
  const roiRelativeCentroid = box.y + box.h / 2;
  if (!Number.isFinite(roiRelativeCentroid)) return null;
  return roiRelativeCentroid >= NUMBER_BOX_BOTTOM_MIN ? "bottom" : "center";
}

// ── Parsers ───────────────────────────────────────────────────────────────

/** Exported for tests. */
export function classifyRegion(text: string): PokedexRegion {
  for (const [region, re] of REGION_KEYWORDS) {
    if (re.test(text)) return region;
  }
  return "unknown";
}

/**
 * GATED region-from-trusted-numbers resolver (device 2026-06-28).
 *
 * WHY: `classifyRegion` derives the region from OCRd header chrome, which is the
 * LEAST reliable signal in the pipeline — on device the Nexus overlay, True Tone
 * warming and theme tints garble the header into e.g.
 * "UNOVA dot Open a Pokemon GO dex page NEXUS DEX", so a Kanto page that read
 * dex IDs 1..15 at 100pct confidence resolved to region:"unknown" and every
 * frame was dropped at the stability gate -> applied:0. The dex NUMBER read is
 * the only 100pct-confidence signal we have and maps deterministically to one
 * region via dexIdToRegion. So trusted numbers, not chrome, are the region
 * authority — matching dexIdToRegion.ts own design intent and the
 * dexPipelineRunner path, removing the long-standing two-resolver drift.
 *
 * GATE — false-positive guard the user asked for: one stray misread digit must
 * never name the region. We accept a number-derived region ONLY when:
 *   - at least MIN_REGION_VOTES (2) read IDs map to a real (non-unknown) region,
 *   - that region holds a STRICT MAJORITY of all real-region votes, and
 *   - no rival region is within REGION_MARGIN (1) vote of the winner.
 * A torn / mid-scroll / scattered-misread frame fails the gate and returns
 * "unknown", so the caller falls back to chrome rather than guessing.
 *
 * Pure and exported for unit tests (see pokedexOcr.regionFromIds.test.ts).
 */
export const MIN_REGION_VOTES = 2;
export const REGION_MARGIN = 1;
export function regionFromReadIds(ids: ReadonlyArray<number | null>): PokedexRegion {
  const votes = new Map<PokedexRegion, number>();
  let total = 0;
  for (const id of ids) {
    if (id == null) continue;
    const r = dexIdToRegion(id);
    // "unidentified" (Meltan/Melmetal 808-809) is not a UI region tab; treat it
    // as no vote so it can never name a regional dex page.
    if (r === "unknown" || r === "unidentified") continue;
    const region = r as PokedexRegion;
    votes.set(region, (votes.get(region) ?? 0) + 1);
    total += 1;
  }
  if (total < MIN_REGION_VOTES) return "unknown";
  let winner: PokedexRegion = "unknown";
  let top = 0;
  let runnerUp = 0;
  votes.forEach((count, region) => {
    if (count > top) {
      runnerUp = top;
      top = count;
      winner = region;
    } else if (count > runnerUp) {
      runnerUp = count;
    }
  });
  // Strict majority of the real-region votes AND a clear margin over any rival.
  if (top * 2 <= total) return "unknown";
  if (top - runnerUp < REGION_MARGIN) return "unknown";
  return winner;
}

/** Exported for tests. */
export function classifyFilter(text: string): PokedexFilter {
  for (const [filter, re] of FILTER_KEYWORDS) {
    if (re.test(text)) return filter;
  }
  return "seen";
}

/**
 * Did the filter band's text actually name a Pokédex filter (2026-09-23)?
 * `classifyFilter` falls back to "seen", and "seen" is itself a keyword, so
 * `FILTER_KEYWORDS.some(f => f === activeFilter)` was true for EVERY frame:
 * the `active_filter_unknown` warning never fired, `filterKnown` was always
 * true, and the 2026-09-22 Pokédex-chrome gate passed the map, an Amazon
 * checkout and a wild encounter as Pokédex pages (iOS 1.9.5 report). Exported
 * for tests.
 */
export function filterTextNamesFilter(text: string): boolean {
  return FILTER_KEYWORDS.some(([, re]) => re.test(text || ""));
}

/** Exported for tests. */
export function parseProgress(text: string): { seen: number | null; total: number | null } {
  if (!text) return { seen: null, total: null };
  const m = text.match(/(\d{1,4})\s*\/\s*(\d{1,4})/);
  if (!m) return { seen: null, total: null };
  return { seen: parseInt(m[1], 10), total: parseInt(m[2], 10) };
}

interface DexNumberCandidate {
  value: number;
  digits: number;
  zeroPadded: boolean;
  index: number;
}

/**
 * A single tight-ROI cell read normally yields one numeric run ("0128"), but a
 * noisy frame can capture an overlay/bullet/list token alongside the real id —
 * the device log's `{"text":"11- 0128","lines":["11-","0128"]}` is the canonical
 * case: the "11-" is a list-marker artefact and "0128" is the true cell id. The
 * pre-fix parser took the FIRST numeric run (`/#?(\d{1,4})/`) and wrote dex #11
 * into the #125-#140 row.
 *
 * PoGo renders every grid cell id zero-padded to 4 digits ("#0128"), so the
 * genuine cell id is the candidate that most looks like that render. We rank all
 * in-range numeric runs by: most digits first (a 4-digit "0128" beats a 2-digit
 * "11"), then zero-padded over not, then earliest position. When only one valid
 * candidate exists — a real low id read as "0011" / "#011" / "11" on an early
 * Kanto page — it is returned unchanged, so legitimate low numbers still parse.
 */
function betterDexCandidate(
  a: DexNumberCandidate,
  b: DexNumberCandidate,
): boolean {
  if (a.digits !== b.digits) return a.digits > b.digits;
  if (a.zeroPadded !== b.zeroPadded) return a.zeroPadded;
  return a.index < b.index;
}

/** A whole Pokédex label: four consecutive digits. */
const FOUR_DIGIT_LABEL_RE = /\d{4}/;

/** Exported for tests. */
export function parseDexNumber(text: string): number | null {
  if (!text) return null;
  let best: DexNumberCandidate | null = null;
  let upsideDown: number | null = null;
  const re = /\d+/g;
  let m: RegExpExecArray | null;
  while ((m = re.exec(text)) !== null) {
    const raw = m[0];
    const value = parseInt(raw, 10);
    if (!Number.isFinite(value) || value < 1 || value > 1100) {
      // An out-of-range run may be Vision reading the label UPSIDE DOWN
      // ("0066" -> "9900", "0069" -> "6900", "0006" -> "9000"): see
      // upsideDownDexRun. Kept only as a fallback, never over a valid read.
      if (upsideDown == null) upsideDown = upsideDownDexRun(raw);
      continue;
    }
    const cand: DexNumberCandidate = {
      value,
      digits: raw.length,
      zeroPadded: raw.length >= 2 && raw[0] === "0",
      index: m.index,
    };
    if (best == null || betterDexCandidate(cand, best)) best = cand;
  }
  return best ? best.value : upsideDown;
}

/**
 * Apple Vision reads a zero-padded dex label made only of 180-degree-symmetric
 * glyphs (0 1 6 8 9) upside down on some crops: "0066" arrives as "9900",
 * "0069" as "6900", "0006" as "9000", "0099" as "6600" (2026-09-24 Perfect
 * Kanto pass: #066 never produced an observation). Rotate the run back
 * (reverse, swap 6/9) and accept it only when the result is a zero-padded
 * 4-digit dex label, which an upright read of these glyphs can never be.
 */
const ROTATE_180_DIGIT: Record<string, string> = { "0": "0", "1": "1", "6": "9", "8": "8", "9": "6" };
function upsideDownDexRun(raw: string): number | null {
  if (raw.length !== 4) return null;
  let out = "";
  for (let k = raw.length - 1; k >= 0; k--) {
    const d = ROTATE_180_DIGIT[raw[k]];
    if (d == null) return null;
    out += d;
  }
  if (out[0] !== "0") return null;
  const v = parseInt(out, 10);
  return v >= 1 && v <= 1100 ? v : null;
}

/** The 180-degree twin of a zero-padded 4-digit label, when that is a label too. */
export function rotatedLabelTwin(text: string): number | null {
  const m = text.match(/\d{4}/);
  if (!m) return null;
  const raw = m[0];
  if (raw[0] !== "0" || !/^[01689]{4}$/.test(raw)) return null;
  const twin = upsideDownDexRun(raw);
  return twin != null && twin !== parseInt(raw, 10) ? twin : null;
}

export interface FrameLabelContext {
  /** Median of the frame's unambiguous reads, or null with fewer than two. */
  median: number | null;
  count: number;
}

/** A read this far from the frame's median is not on this page. */
export const FRAME_LABEL_MAX_DISTANCE = 40;

export function buildFrameLabelContext(texts: ReadonlyArray<string>): FrameLabelContext {
  const sure: number[] = [];
  for (const t of texts) {
    if (!FOUR_DIGIT_LABEL_RE.test(t)) continue;
    const v = parseDexNumber(t);
    if (v == null || rotatedLabelTwin(t) != null) continue;
    sure.push(v);
  }
  sure.sort((a, b) => a - b);
  return { median: sure.length >= 2 ? sure[Math.floor(sure.length / 2)] : null, count: sure.length };
}

/**
 * A cell's dex number read against its frame: an ambiguous label takes the
 * reading nearer the frame's median; any read farther than
 * FRAME_LABEL_MAX_DISTANCE from it is dropped. With no median (a frame of
 * fewer than two sure reads) the read stands as parsed.
 */
export function resolveFrameDexNumber(text: string, ctx: FrameLabelContext): number | null {
  const v = parseDexNumber(text);
  if (v == null || ctx.median == null) return v;
  const twin = rotatedLabelTwin(text);
  if (twin != null) return Math.abs(twin - ctx.median) < Math.abs(v - ctx.median) ? twin : v;
  return Math.abs(v - ctx.median) > FRAME_LABEL_MAX_DISTANCE ? null : v;
}

/** Exported for tests. */
export function cleanSpeciesName(raw: string): string {
  return raw.replace(/\s+/g, " ").trim();
}

/**
 * Validate a parsed `{seen, total}` pair against the per-region maximum.
 * Returns true when the values are plausible Dex progress for `region`.
 *
 * Exported so the screen classifier and ingest pipeline can use the same
 * gate (and so we get a test surface) instead of duplicating the
 * REGIONAL_MAX table.
 */
export function isProgressInRange(
  region: PokedexRegion,
  total: number | null,
): boolean {
  if (total === null) return false;
  const max = REGIONAL_MAX[region];
  return total > 0 && total <= max + 5;
}

// ── Active tab detection ──────────────────────────────────────────────────

// The tab-strip centre, the pill band and the grid anchors now live per screen
// shape in lib/dexLayout (PHONE_LAYOUT carries the historic values).
const FILTER_BAR_Y = 0.09;  // center of filter chip strip
const TAB_COUNT = 11;       // All + 10 regions, equally spaced

/**
 * Build 3 / v1.4.6 b3 — geometry of the PoGo Pokédex *filter-pill row*.
 *
 * The LUCKY / SHINY / XXL / XXS / PURIFIED filter pills live in a horizontal
 * row at the BOTTOM of the screen, directly above the region/variant carousel
 * — NOT in the top header. Build 2 mistakenly sampled the top band (y≈0.06),
 * which on PoGo's Pokédex is the orange-on-cream "KANTO" region label; the
 * orange label hue collided with Lucky's orange and the cream background read
 * as Perfect, producing the misclassifications in the device log. We now read
 * the bottom pill row by position so the region label can never be mistaken
 * for an active filter pill.
 *
 * Calibrated against 19.5:9 reference screenshots: the active pill sits in the
 * lower ~10% of the frame, just above the very bottom carousel/nav band.
 */

function detectActiveColumn(img: HTMLImageElement, yPct: number): number {
  let best = 0;
  let bestLum = -1;
  for (let i = 0; i < TAB_COUNT; i++) {
    const x = (i + 0.5) / TAB_COUNT;
    const lum = avgLuminance(img, x - 0.04, yPct - 0.015, 0.08, 0.03);
    if (lum > bestLum) {
      bestLum = lum;
      best = i;
    }
  }
  return best;
}

// ── Main entry point ──────────────────────────────────────────────────────

export interface ExtractPokedexFrameOptions {
  /**
   * Whether the in-app Picture-in-Picture overlay is up for this frame. When
   * true, the PiP rectangle is masked out of the number-ROI candidate set so
   * OCR never reads the "Reading 4 × 4 grid" bubble / its dex count as a cell
   * number. Defaults to false (no PiP mask) so the upload path is unaffected.
   */
  pipActive?: boolean;
  /**
   * b17o: the active Dex variant for THIS frame, supplied by the caller's
   * variant resolution (app-state pill, hardened by resolveTrustedVariant /
   * CHIP_TEXT_AUTHORITY_THRESHOLD in dexLiveFrameAdapter). It selects which
   * per-cell sprite test runs: the hue-variance silhouette test for
   * Main/Shiny/Mega/G-Max/Shadow/Purified, or empty-vs-full only for
   * Lucky/Perfect/XXL/XXS. Defaults to "pokemon" so the upload path (no live
   * variant) keeps the silhouette test it had before.
   */
  variant?: PokedexVariant;
  /**
   * perf/scan-efficiency: optional per-session pill-OCR pHash cache, threaded
   * from DexLiveScanSession. When present, the active-pill Vision OCR is skipped
   * on frames whose pill ROI perceptually matches the previous frame's. Omitted
   * on one-off screenshot OCR (upload path, tests) so Vision runs every time.
   */
  pillOcrCache?: PillOcrCache;
  // Vision-only trial: the `pageColourFamily` option was removed. It used to be
  // supplied by the deleted pre-OCR palette gate / colour tracker in the live
  // hook. The veto-only family is now derived internally per frame from the
  // full-frame RGBA via deriveVetoOnlyFamily(), so callers no longer thread it.
}

/** Card band above the number box, in rows of the lattice pitch. */
export const CARD_BAND_ABOVE_NUMBER_ROWS = 0.34;
/** Gap between that band and the number's top, in rows. */
export const CARD_BAND_NUMBER_GAP_ROWS = 0.02;
/** Width of the anchored band as a fraction of the cell (the card is ~0.89). */
export const CARD_BAND_WIDTH_FRAC = 0.8;
/** How far above the number the card's top outline is looked for, in rows. */
export const CARD_OUTLINE_SEARCH_ROWS = 0.85;
/** A card outline row: the luminance step covers the card's width with one sign. */
export const CARD_OUTLINE_MIN_COVERAGE = 0.9;
export const CARD_OUTLINE_MIN_SIGN_AGREEMENT = 0.8;
const CARD_OUTLINE_MIN_STEP = 6;

/**
 * The y (px) of the card's top outline above a number, or null. Walks the
 * middle 60% of the card's columns (clear of the rounded corners) upward from
 * the number and returns the first row whose vertical luminance step is both
 * wide (covers the columns) and coherent (one sign): the outline of the card
 * the number sits on, 0.72-0.79 rows above a bottom-pinned number and
 * 0.41-0.47 rows above a centred one on the device frames. A sprite row never
 * measures that way (partial width, mixed signs). The HUD card's bottom edge
 * or a region header above a partial top row clips the band the same way.
 */
function cardTopOutlineAbove(
  frame: ImageData,
  x0: number,
  x1: number,
  yFrom: number,
  yTo: number,
): number | null {
  const FW = frame.width;
  const d = frame.data;
  const inset = Math.floor((x1 - x0) * 0.2);
  const cx0 = x0 + inset;
  const cx1 = x1 - inset;
  const n = cx1 - cx0;
  if (n < 8) return null;
  const lum = (i: number) => 0.2126 * d[i] + 0.7152 * d[i + 1] + 0.0722 * d[i + 2];
  for (let y = yFrom - 1; y > Math.max(1, yTo); y--) {
    let covered = 0;
    let signed = 0;
    let abs = 0;
    for (let x = cx0; x < cx1; x++) {
      const g = lum((y * FW + x) * 4) - lum(((y - 1) * FW + x) * 4);
      const a = Math.abs(g);
      if (a > CARD_OUTLINE_MIN_STEP) covered++;
      signed += g;
      abs += a;
    }
    if (covered / n >= CARD_OUTLINE_MIN_COVERAGE && abs > 0 && Math.abs(signed) / abs >= CARD_OUTLINE_MIN_SIGN_AGREEMENT) {
      return y;
    }
  }
  return null;
}

/**
 * RGBA of the card band directly above a Vision number box: the cell's full
 * width, CARD_BAND_ABOVE_NUMBER_ROWS tall, ending CARD_BAND_NUMBER_GAP_ROWS
 * above the digits. Null when the band would be degenerate (frame edge).
 */
function cardBandAboveNumber(
  frame: ImageData,
  cell: FracRect,
  roi: FracRect,
  box: NumberBox,
): { data: Uint8ClampedArray; width: number; height: number; y0Frac: number; hFrac: number } | null {
  const FW = frame.width;
  const FH = frame.height;
  const numberTop = roi.y + box.y * roi.h;
  const bottom = numberTop - CARD_BAND_NUMBER_GAP_ROWS * cell.h;
  // The band is centred on the NUMBER (GO centres it on the card) and runs
  // CARD_BAND_WIDTH_FRAC of the cell wide, so the lattice's lateral error never
  // puts the page gutter or the neighbour card's corner in the band: on the
  // 20:9 Galaxy the cells for columns 2/3 sit 10-15 px left of their cards
  // and that darker gutter step read as a sprite body on blank Shadow cards
  // (#379/#383, 2026-09-25 harness). The number centre is clamped to the
  // middle of the cell so a clipped Vision box cannot drag the band out.
  const numberMid = roi.x + (box.x + box.w / 2) * roi.w;
  const mid = Math.min(cell.x + cell.w * 0.65, Math.max(cell.x + cell.w * 0.35, numberMid));
  const half = (cell.w * CARD_BAND_WIDTH_FRAC) / 2;
  const x0 = Math.max(0, Math.floor((mid - half) * FW));
  const x1 = Math.min(FW, Math.floor((mid + half) * FW));
  const y1 = Math.min(FH, Math.floor(bottom * FH));
  // The band runs up to the card's own top outline when it can be seen, so a
  // sprite drawn high on the card (Crobat, Articuno, Gastly, Magnemite) is
  // inside it and a blank card's band never reaches the card above. Without
  // an outline in reach the fixed height stands.
  const outline = cardTopOutlineAbove(frame, x0, x1, y1, Math.max(0, Math.floor((bottom - CARD_OUTLINE_SEARCH_ROWS * cell.h) * FH)));
  const top = outline != null ? (outline + 2) / FH : bottom - CARD_BAND_ABOVE_NUMBER_ROWS * cell.h;
  const y0 = Math.max(0, Math.floor(top * FH));
  const width = x1 - x0;
  const height = y1 - y0;
  if (width < 8 || height < 8) return null;
  const data = new Uint8ClampedArray(width * height * 4);
  for (let y = 0; y < height; y++) {
    const src = ((y0 + y) * FW + x0) * 4;
    data.set(frame.data.subarray(src, src + width * 4), y * width * 4);
  }
  return { data, width, height, y0Frac: y0 / FH, hFrac: height / FH };
}

export async function extractPokedexFrame(
  imageDataUrl: string,
  opts: ExtractPokedexFrameOptions = {},
): Promise<PokedexGridFrame> {
  const img = await loadImage(imageDataUrl);
  const warnings: PokedexWarning[] = [];
  // Cells whose number ROI carried GO's "CP" label (see STORAGE_BOX_CELL_RE).
  let cpLabelledCells = 0;

  if (img.width > img.height) {
    return {
      activeRegion: "unknown",
      activeFilter: "seen",
      progressSeen: null, progressTotal: null,
      cells: [],
      confidence: 0,
      warnings: ["not_pokedex_screenshot"],
    };
  }

  // A screen shape the lattice was never measured on (an iPad, a 16:9 phone)
  // is still read with the phone lattice, but says so: see lib/frameGeometry.
  const aspectWarning = frameAspectWarning(img.width, img.height);
  if (aspectWarning) warnings.push(aspectWarning);
  // Which screen the grid is laid out on (lib/dexLayout): the phone lattice,
  // or the 4:3 tablet lattice measured on the owner's iPad (2026-10-23).
  const layout: DexScreenLayout = dexLayoutForFrame(img.width, img.height);

  // PR4: device-relative ROI anchors. Build the full-frame RGBA buffer ONCE
  // here (it is also reused below for the fast-scroll pill detector, so this is
  // not extra capture cost) and detect the real grid bottom + filter-pill row
  // for THIS device's geometry. Fixed fractions (GRID_BOTTOM=0.79,
  // FILTER_PILL_ROW_Y=0.85) were calibrated to one 19.5:9 phone and drift on
  // iPad / 16:9 / Dynamic-Island devices. b18 correction, below: only the pill
  // row follows the detector; the grid lattice itself stays fixed. When a
  // detector is not confident (e.g. PiP occludes the pill row, or a non-grid
  // frame) we fall back to the fixed constant so no device can regress.
  let frameRgba: ImageData | null = null;
  {
    const aCanvas = document.createElement("canvas");
    aCanvas.width = img.naturalWidth || img.width;
    aCanvas.height = img.naturalHeight || img.height;
    const aCtx = aCanvas.getContext("2d", CANVAS_READBACK);
    if (aCtx) {
      aCtx.drawImage(img, 0, 0);
      frameRgba = aCtx.getImageData(0, 0, aCanvas.width, aCanvas.height);
    }
  }
  // Vision-only trial: derive the VETO-ONLY page-colour family for THIS frame
  // from the full-frame RGBA (deriveVetoOnlyFamily samples 6 body-background
  // zones + inlined palette rules). This replaces the deleted pre-OCR palette
  // gate's colour tracker that used to feed opts.pageColourFamily. Threaded into
  // the active-pill classifier + resolveFrameVariant as a corroborating veto
  // ONLY (a Vision-read variant impossible for the frame's dominant hue is
  // rejected); it never positively selects a variant. Null when the backdrop
  // matches no known dex family, which makes the veto inert.
  const pageColourFamily: DexPageColourFamily | null = frameRgba
    ? deriveVetoOnlyFamily(frameRgba)
    : null;
  // The Nexus HUD card (pale mint since 2026-08-20) found once per frame: it
  // masks the grid-bottom profile here and is both the PiP rect and the
  // overlay rect below, so every occlusion gate keys off one rectangle.
  // detectPipSlabRect looked for the old dark card and returned null since.
  const hudRect: NexusOverlayRect | null = frameRgba && !!opts.pipActive
    ? detectNexusOverlayRect(frameRgba.data, frameRgba.width, frameRgba.height)
    : null;
  let gridBottomAnchor = layout.gridBottom;
  let pillRowY = layout.pillRowY;
  if (frameRgba) {
    const gb = detectGridBottomFrac(
      frameRgba.data,
      frameRgba.width,
      frameRgba.height,
      0.18,
      0.82,
      !!opts.pipActive,
      hudRect,
      layout.gridPitchSearch,
    );
    if (gb.confidence >= ANCHOR_TRUST_CONFIDENCE) {
      // CORRECTED b18: the detector landmark (the high-contrast band it locks
      // onto) is the TOP of the floating filter-pills/REGIONS row, NOT the grid
      // bottom. The dex grid scrolls CONTINUOUSLY BEHIND those buttons, so the
      // true grid bottom is BELOW the detected pill row (the fixed GRID_BOTTOM
      // page geometry is 4 rows spanning ~0.205->0.79). Treating gb.frac as the
      // grid bottom truncated row 4 and mis-sliced every cell. We therefore keep
      // the fixed GRID_BOTTOM authoritative for grid geometry and use the
      // detector ONLY to locate the pill row for the active-pill classifier.
      const pr = detectFilterPillRowFrac(
        frameRgba.data, frameRgba.width, frameRgba.height, gb.frac, layout.pillSearch,
      );
      if (pr.confidence >= ANCHOR_TRUST_CONFIDENCE) {
        // detectFilterPillRowFrac returns the band CENTRE; classifyActivePillBand
        // takes a band TOP + height, so shift up by half the band height.
        pillRowY = Math.max(0, pr.frac - layout.pillRowH / 2);
      }
    }
    // eslint-disable-next-line no-console
    if (DEX_SCAN_VERBOSE) console.log("[dex-scan] roi anchors", {
      gridBottom: Number(gridBottomAnchor.toFixed(3)),
      gridBottomConf: Number(gb.confidence.toFixed(2)),
      gridBottomFixed: layout.gridBottom,
      pillRowY: Number(pillRowY.toFixed(3)),
      pillRowYFixed: layout.pillRowY,
    });
  }
  // #1b: corner-agnostic PiP mask. The PiP is a native iOS window the user
  // drags anywhere (domain rule keeps it off the variant-tag corners, so it
  // typically sits bottom-left or bottom-right). When PiP is active we detect
  // its dark-card rect from THIS frame and feed it to the number-ROI mask so
  // the PiP's floating "#001"/"Added" text can never be OCR'd as a cell number
  // wherever it sits. Reuses the frameRgba buffer; null detection falls back to
  // the fixed top-right rect (no regression).
  let pipRect: FracRect | null = null;
  if (frameRgba && !!opts.pipActive) {
    pipRect = hudRect;
    if (pipRect) {
      // eslint-disable-next-line no-console
      if (DEX_SCAN_VERBOSE) console.log("[dex-scan] pip slab rect", {
        x: Number(pipRect.x.toFixed(3)),
        y: Number(pipRect.y.toFixed(3)),
        w: Number(pipRect.w.toFixed(3)),
        h: Number(pipRect.h.toFixed(3)),
      });
    }
  }
  // Nexus overlay guard (2026-06-21 false-positive fix): during a live scan the
  // app paints its OWN green status card ("● Scanning… / N dex entries updated /
  // Added: #077 #025 …") over the captured PoGo screen. When a cell number ROI
  // lands on that card the OCR reads our own HUD text and parses #077/#025/… as
  // grid dex numbers (the real-device phantom catches 77/25/143/74/817/818/…).
  // Detect the green card's rect once per frame so the cell loop can SKIP any ROI
  // it materially overlaps. Only attempt detection while PiP/live scan is active
  // (the overlay only exists then); null when the card is not over the grid so a
  // clean frame never regresses.
  let nexusOverlayRect: NexusOverlayRect | null = null;
  if (frameRgba && !!opts.pipActive) {
    nexusOverlayRect = hudRect;
    if (nexusOverlayRect) {
      // eslint-disable-next-line no-console
      if (DEX_SCAN_VERBOSE) console.log("[dex-scan] nexus overlay rect", {
        x: Number(nexusOverlayRect.x.toFixed(3)),
        y: Number(nexusOverlayRect.y.toFixed(3)),
        w: Number(nexusOverlayRect.w.toFixed(3)),
        h: Number(nexusOverlayRect.h.toFixed(3)),
      });
    }
  }

  // Exclusion zones now track the detected grid bottom (selector band re-based)
  // and the detected PiP rect when present.
  const zones = exclusionZones(!!opts.pipActive, gridBottomAnchor, pipRect, layout);

  // Region label: in Pokémon GO the active region name ("KANTO") is printed
  // top-LEFT inside the header card (~y 0.07-0.13), NOT in a tab strip. Read
  // that crop FIRST and prefer it — it is the user-visible source of truth.
  // The tab strip + filter row are read for variant/back-compat.
  // Four of the five header reads ride ONE batch call (2026-09-22, Galaxy
  // S21+ scan speed): each used to be a PNG crop encode -> decode -> JPEG
  // re-encode -> its own bridge round trip, queued two at a time. The batch
  // path hands the frame's own JPEG bytes over once with the ROI rects. The
  // status-bar strip (y < 0.06) stays on the per-crop path because the batch
  // engine clamps that band away. The test seam (ocrRunnerOverride) keeps
  // the per-crop path so fixtures keep reading.
  const headerRois = [
    layout.headerRois.regionLabel, // region label, top-left of the card
    layout.headerRois.filterRow, // filter row
    layout.headerRois.count, // header COUNT card (never a cell number)
    layout.headerRois.statsRow, // Push 8: expanded stats row
  ];
  const tabStrip = layout.headerRois.tabStrip;
  const tabTextPromise = runOcr(cropToDataUrl(img, tabStrip.x, tabStrip.y, tabStrip.w, tabStrip.h));
  let labelText: { text: string; conf: number };
  let filterText: { text: string; conf: number };
  let progressText: { text: string; conf: number };
  let variantRowText: { text: string; conf: number };
  if (ocrRunnerOverride) {
    [labelText, filterText, progressText, variantRowText] = await Promise.all([
      runOcr(cropToDataUrl(img, headerRois[0].x, headerRois[0].y, headerRois[0].w, headerRois[0].h)),
      runOcr(cropToDataUrl(img, headerRois[1].x, headerRois[1].y, headerRois[1].w, headerRois[1].h)),
      runOcr(cropToDataUrl(img, headerRois[2].x, headerRois[2].y, headerRois[2].w, headerRois[2].h), { whitelist: "0123456789 /" }),
      runOcr(cropToDataUrl(img, headerRois[3].x, headerRois[3].y, headerRois[3].w, headerRois[3].h)),
    ]);
  } else {
    const batch = await recognizeRegions(img, headerRois, { multiline: true }).catch(() => null);
    const pick = (i: number, whitelist?: string) => {
      const r = batch?.[i];
      const text = r ? (whitelist ? filterToWhitelist(r.text, whitelist) : r.text) : "";
      return { text, conf: r?.confidence ?? 0 };
    };
    labelText = pick(0);
    filterText = pick(1);
    progressText = pick(2, "0123456789 /");
    variantRowText = pick(3);
  }
  const tabText = await tabTextPromise;

  // Region resolution order, honouring the brief:
  //   1. The visible region label crop (KANTO …).
  //   2. The full tab-bar OCR text.
  //   3. The brightest selected tab column (dark-theme glyph fallback).
  // If none resolve, report "unknown" with the reason — we do NOT invent
  // Hisui/Paldea from a stale guess.
  let activeRegion = classifyRegion(labelText.text);
  let regionReason = "label";
  if (activeRegion === "unknown") {
    activeRegion = classifyRegion(tabText.text);
    if (activeRegion !== "unknown") regionReason = "tab_text";
  }
  if (activeRegion === "unknown") {
    const col = layout.tabBarY == null ? -1 : detectActiveColumn(img, layout.tabBarY);
    const colRegion = TAB_COLUMN_REGIONS[col] ?? "unknown";
    if (colRegion !== "unknown") {
      activeRegion = colRegion;
      regionReason = "tab_column";
    } else {
      regionReason = "unresolved";
      warnings.push("active_region_unknown");
    }
  }
  // Push 8 / v1.4.3 b8: parse the trusted top panel from the combined header
  // text (region label + main count + expanded variant row). When it
  // confidently names a region, it OVERRIDES the tab/label region guess —
  // this is what stops a stale HISUI/Paldea tab from sticking when the visible
  // panel clearly says KANTO.
  const topPanelText = `${labelText.text} ${progressText.text} ${variantRowText.text}`;
  const topPanel = decideTopPanelAction(parseTopPanel(topPanelText));
  if (topPanel.overrideRegion && topPanel.region !== "unknown") {
    activeRegion = topPanel.region;
    regionReason = "top_panel";
    // The override resolves the region; drop the "unknown" warning if present.
    const idx = warnings.indexOf("active_region_unknown");
    if (idx >= 0) warnings.splice(idx, 1);
  }

  const activeFilter = classifyFilter(filterText.text);
  if (!filterTextNamesFilter(filterText.text)) {
    warnings.push("active_filter_unknown");
  }

  // PR-E7 — PoGo-side variant detection from the BOTTOM filter-pill row, using
  // the *active-pill* classifier. The legacy `classifyVariantChip` saw the
  // entire band's OCR ("POKEMON SHINY LUCKY") and its first-match-wins regex
  // list always picked LUCKY, writing 53 main-dex cells under variant:"lucky"
  // on the 2026-06-15 device test. The new pipeline locates the active pill by
  // its darker-fill geometry, OCRs each on-screen slot independently, and pins
  // the active variant by its slot position against the canonical pill order.
  // Theme hue at the peak is a sanity cross-check; the apply gate skips frames
  // where the active pill can't be located confidently.
  const { chip: variantChip, diag: activePillDiag } = await classifyActivePillBand(
    // PR4: use the per-frame detected pill-row Y (falls back to the fixed
    // FILTER_PILL_ROW_Y when detection was not confident).
    img, pillRowY, layout.pillRowH,
    // Issue-2 colour veto: a stale gmax overlay read must not promote over a
    // colour-consistent xxs pin on a cyan (XXS/XXL/Pokémon) page. Vision-only
    // trial: family is now the internally-derived veto-only sample.
    pageColourFamily,
    // perf/scan-efficiency: per-session pill-OCR pHash cache (skips Vision on an
    // unchanged pill ROI). Undefined on the upload/screenshot path → Vision runs.
    opts.pillOcrCache,
  );
  // eslint-disable-next-line no-console
  if (DEX_SCAN_VERBOSE) console.log("[dex-scan] variant detect", {
    source: variantChip.source,
    variant: variantChip.variant,
    confidence: Number(variantChip.confidence.toFixed(2)),
    activePillSource: activePillDiag.source,
    slot: activePillDiag.slot,
    peakScore: Number(activePillDiag.peakScore.toFixed(3)),
    peakHue: activePillDiag.peakHue == null ? null : Math.round(activePillDiag.peakHue),
    hueAgrees: activePillDiag.hueAgrees,
  });
  // Variant classifier conflict telemetry (2026-06-22, multi-dex #477). When a
  // directly-read chip-text token disagreed with the geometry/neighbour pin and a
  // precedence guard re-routed the result (e.g. a clean G-MAX slot read over an
  // xxs slot-geometry pin on the pink G-Max page), surface WHY here so the next
  // device trace shows the resolution rather than only the final variant.
  if (activePillDiag.conflict) {
    // eslint-disable-next-line no-console
    console.log("[dex-scan] variant classifier conflict", {
      rawText: activePillDiag.conflict.rawText,
      pinned: activePillDiag.conflict.pinned,
      resolution: activePillDiag.conflict.resolution,
      resolvedVariant: variantChip.variant,
      slot: activePillDiag.slot,
      peakHue: activePillDiag.peakHue == null ? null : Math.round(activePillDiag.peakHue),
    });
  }

  // Vision-only trial: the Tesseract-primary / Vision-fallback "variant text
  // recovery" block (sole-variant PURIFIED/SHADOW re-read) AND the separate
  // colour-narrowed pill re-read were removed. With Vision reading the full pill
  // on a raw (non-binarised) upscaled crop, these narrow-lexicon second-chance
  // recoveries are redundant. The colour veto still runs at the classifier layer
  // (COLOUR_FAMILY_ALLOWED_VARIANTS) via the derived pageColourFamily below, and
  // single-variant families still resolve directly via soleVariantForColourFamily.
  const colourNarrowedVariant: PokedexVariant | null = null;

  const { seen, total } = parseProgress(progressText.text);
  let progressSeen = seen;
  let progressTotal = total;
  // Back-fill the legacy progress fields from the top-panel reading when the
  // digit-only crop missed them but the panel parsed a plausible count.
  if (progressTotal === null && topPanel.mainTotal != null && topPanel.mainCaught != null) {
    progressSeen = topPanel.mainCaught;
    progressTotal = topPanel.mainTotal;
  }
  if (progressTotal !== null && activeRegion !== "unknown") {
    const max = REGIONAL_MAX[activeRegion];
    if (progressTotal > max + 5) {
      warnings.push("progress_out_of_range");
      progressSeen = null;
      progressTotal = null;
    }
  }

  // b17o/b17t: variant that selects which per-cell sprite + number-pixel test
  // runs. A confident on-screen chip-text read wins over the app-state pill
  // (resolveFrameVariant), since the pill can disagree with what the camera
  // shows; otherwise we fall back to opts.variant, then "pokemon" (upload path).
  // Resolved BEFORE the grid rows (2026-08-31): the 3-column G-Max page needs
  // its column count to derive the taller 3-row lattice.
  const resolvedFrameVariant: PokedexVariant | null = resolveFrameVariant(
    opts,
    variantChip,
    pageColourFamily,
    colourNarrowedVariant,
  );

  // Fix B: HOLD/SKIP. When the unified resolver returns null (app_state vetoed
  // by the page colour and chip-text could not supply an allowed variant) we
  // commit NOTHING this frame — no cell sweep, no app_state substitution. The
  // old `?? appStateVariant` ran every cell under a stale variant's sprite test
  // and dropped/miscommitted them. variantChip still flows through for the
  // diagnostic panel; the empty cells make committed == 0 / applied == 0.
  if (resolvedFrameVariant == null) {
    // eslint-disable-next-line no-console
    if (DEX_SCAN_VERBOSE) console.log("[dex-scan] frame hold", {
      reason: "variant_hold",
      appState: opts.variant ?? null,
      chipSource: variantChip.source,
      chipVariant: variantChip.variant,
      pageColourFamily: pageColourFamily ?? null,
    });
    warnings.push("variant_unstable");
    return {
      activeRegion,
      activeFilter,
      progressSeen,
      progressTotal,
      cells: [],
      confidence: 0,
      warnings,
      regionReason,
      pipObscuredCells: 0,
      topPanel,
      variantChip,
      colourNarrowedVariant,
      pageColourFamily,
    };
  }
  const frameVariant: PokedexVariant = resolvedFrameVariant;
  // PR-E6-OCR2.5-gmax: G-Max renders a 3-column grid (wider sprites). Every
  // other variant is 4-column. Resolve once per frame and thread through every
  // geometry call so cellRectFrac / acceptedNumberRois sweep the right tiles.
  const gridColumns = gridColumnsForVariant(frameVariant);

  // Grid sweep. The Pokémon GO Pokédex is 4 columns wide (3 on G-Max) and the
  // dex number is rendered in the BOTTOM-CENTRE of each cell. Geometry + the
  // number ROI + the exclusion bands (status bar, header count, bottom
  // selector, PiP overlay) all live in dexOcrRoi so the same rules are unit-
  // tested against the reference screenshots. Cells whose number ROI lands in
  // an excluded band are skipped (and PiP-obscured ones counted separately),
  // which is what stops the header "150" and the SHINY/LUCKY labels from ever
  // becoming dex numbers.
  // b13c.5: bottom-anchored rows. Pokémon GO's header animates (large ↔ small
  // on scroll), so there is no static row-0 top. We anchor the grid bottom to
  // the stable filter-pills row and walk up by the row pitch (see dexGridRows).
  // Normally 4 rows (3 on the taller-rowed G-Max page); can drop further only
  // when the grid is scrolled up under an expanded header (a derived row's top
  // would overlap the header).
  // PR4: anchored to the per-frame detected grid bottom (falls back to the
  // fixed GRID_BOTTOM when detection was not confident).
  let gridRows = dexGridRows(gridBottomAnchor, gridColumns, 0, layout);

  // BOTTOM-UI BAND (2026-07-14). Pokémon GO's fixed bottom controls — the search
  // magnifier, the clear ✕, the REGIONS button and the POKÉMON/SHINY/LUCKY filter
  // pills — overlay the bottom of the dex grid. As the grid scrolls under them, a
  // cell passing beneath a button gets its sprite ROI filled with flat button
  // pixels, so the classifier reads "sprite-present" and FALSE-writes it as caught
  // (device 2026-07-14: shiny #12/#14/#30/#32/#44… all read a textureless fill
  // with foreground). No pixel feature separates a button-clip from a real sprite
  // (proven on-device), and the button is STATIONARY while content scrolls — so
  // the only robust, screen-INDEPENDENT guard is positional: skip any cell whose
  // sprite body sits in this band. It is anchored to the DETECTED grid bottom
  // (NOT a fixed screen %), so it self-scales per device/aspect-ratio. A skipped
  // cell is re-read cleanly once it scrolls up into the middle — no permanent
  // loss (that is why a genuine catch under a button, e.g. #52, still commits from
  // a clean frame). Starts ~0.7 of a row above the grid bottom; if a stray false
  // write survives, widen BOTTOM_UI_BAND_ROWS via the on-device scan-log export.
  const bottomRowSpec = gridRows[gridRows.length - 1];
  // 2026-07-19 (iPhone 12 device pass): anchor the bottom-UI band to the DETECTED
  // control strip, not a fixed 0.7-row offset from the fixed grid bottom. The old
  // offset put the band top at gridBottom-0.7row ≈ 0.688, which on this device
  // skipped a FULLY-VISIBLE bottom grid row (~0.62–0.79) sitting in the clean gap
  // ABOVE the controls — a quarter of the grid lost every frame (telemetry: only
  // cellIdx 0–11 ever classified).
  //
  // The hazard is the whole floating control cluster: the Search / ✕ / REGIONS
  // buttons AND the POKÉMON/SHINY/LUCKY pills. The pill-row detector gives us the
  // PILLS (pillRowY ≈ 0.86); the buttons float ~0.75 of a row ABOVE the pills
  // (button tops ≈ 0.78). So the band top sits 0.75 of a row above pillRowY to
  // cover the BUTTONS too — landing at ≈0.74–0.77 across the detected pillRowY
  // range, threading the clean gap between the bottom scanner row's sprite ROI
  // (fixed at ~0.644–0.735 — with MAX_TOLERATED_OVERLAY_OVERLAP=0.18 it stays read
  // as long as the band top is ≥ 0.719) and the button tops (~0.78, which the band
  // must cover). The bottom scanner row's sprite ROI never scrolls into the
  // controls (the rows are fixed screen positions), so reading it puts no cell
  // under a button, while any sprite that DOES reach the strip is still skipped.
  // Self-scales per device via the detector; pillRowY falls back to
  // FILTER_PILL_ROW_Y when detection is not confident.
  // 2026-09-25: current GO draws the round buttons BELOW the pills (the
  // pill-row detector's window already knows this), so the fence only has to
  // start just above the pills' top edge. pillRowY is the classifier band top
  // (centre - 0.035), a hair above the capsules; 0.1 of a row clears the
  // outline. At 0.75 the fence reached 0.69-0.74 and skipped the whole bottom
  // card row on the Galaxy (0228/0229 real catches, harness 2026-09-25).
  const BOTTOM_UI_BAND_MARGIN_ROWS = 0.1;
  const bottomUiBand: FracRect | null = bottomRowSpec
    ? (() => {
        const rowH = bottomRowSpec.bottom - bottomRowSpec.top;
        const y = Math.max(0, pillRowY - BOTTOM_UI_BAND_MARGIN_ROWS * rowH);
        return { x: 0, y, w: 1, h: Math.max(0, 1 - y) };
      })()
    : null;

  // (The variant + gridColumns resolution moved ABOVE the grid-row derivation,
  // 2026-08-31: the 3-column G-Max page tiles 3 TALLER rows, so the row
  // lattice needs the column count — see dexGridRows.)

  // b13c.4: one-time-per-session tile-geometry log. The build-193 trace only
  // ever showed 3–5 cells per frame, never 16, with no way to tell whether the
  // loop was enumerating fewer than 16 ROIs or the 16 ROIs were landing off the
  // numbers. This logs the resolved pixel geometry (frame size + per-tile size +
  // grid shape) ONCE per page-load so the next test confirms cols × N rows are
  // being swept, and the tile dimensions are sane (tileW ≈ imgW/cols).
  if (!loggedTileGeometry) {
    loggedTileGeometry = true;
    const imgW = img.width;
    const imgH = img.height;
    const sampleCell = cellRectFrac(gridRows[0], 0, gridColumns, layout);
    // eslint-disable-next-line no-console
    if (DEX_SCAN_VERBOSE) console.log("[dex-scan] tile geometry", {
      imgW,
      imgH,
      tileW: Math.round(sampleCell.w * imgW),
      tileH: Math.round(sampleCell.h * imgH),
      cols: gridColumns,
      defaultCols: GRID_COLUMNS,
      rows: gridRows.length,
      maxRows: GRID_ROWS,
      totalCells: gridColumns * gridRows.length,
      variant: frameVariant,
      gridBottomPx: Math.round((gridRows[gridRows.length - 1]?.bottom ?? 0) * imgH),
    });
  }

  if (
    frameVariant !== opts.variant &&
    variantChip.source === "chip-text" &&
    frameVariant === variantChip.variant
  ) {
    // eslint-disable-next-line no-console
    console.log("[dex-scan] variant override applied", {
      from: opts.variant,
      to: variantChip.variant,
      source: variantChip.source,
      confidence: Number(variantChip.confidence.toFixed(2)),
    });
  }

  const cells: PokedexCellData[] = [];
  let confSum = 0;
  let confCount = 0;
  let pipObscuredCells = 0;
  let nexusOverlayObscuredCells = 0;
  let bottomUiObscuredCells = 0;
  let topRowSkippedCells = 0;
  let cellIdx = -1;
  // GRID PHASE (2026-07-22, measurement pass). Where each cell's dex number
  // actually sits inside the cell rect the fixed lattice assigned it. The lattice
  // walks up from a CONSTANT grid bottom while the Pokédex scrolls continuously,
  // so this is the direct measurement of its registration error — the error that
  // put grid row 1 at 47% precision against 99.2% for rows 2-4 on the labelled
  // shiny pass. Measurement only this pass: nothing below consumes the estimate.
  const phaseSamples: GridPhaseSample[] = [];

  // Y6 perf: advance the frame counter and drop the cache if the frame variant
  // changed. A cached "perfect" hash must never be replayed for a "lucky" read
  // at the same cellIdx, so a variant switch invalidates every entry.
  const frameNumber = extractFrameCounter++;
  if (cellCacheVariant !== frameVariant) {
    cellContentCache.clear();
    cellCacheVariant = frameVariant;
  }
  const cacheClearedAfterBlind = extractLastNumbersRead === 0 && cellContentCache.size > 0;
  if (cacheClearedAfterBlind) cellContentCache.clear();
  let cacheHits = 0;
  let cacheMisses = 0;
  let forcedRefreshes = 0;

  // b18-pill: detect the fast-scroll capsule once per frame using the
  // full-frame pixel buffer. classifyCellAt translates this into per-cell
  // coordinates for the three analyzers.
  // PR4: reuse the `frameRgba` buffer already built for the ROI anchor
  // detection above instead of re-rasterising the frame a second time.
  // The quick-scroll pill (2026-09-25): a pale rounded pill with two chevrons
  // at the right edge while the list scrolls, tinted by the page. Its body
  // read as a caught sprite on the Galaxy Lucky pass (#454, #458, #468-470
  // written, then retracted). detectFastScrollPill looked for BLUE chevrons
  // and fired on blue sprites instead (81cc639c at 886, c2c31e66 / 67bcb549 /
  // 2be91c24 at 576), holding those right-column cells as "pill-overlap".
  let framePill: PillRegion | null = null;
  if (frameRgba) {
    const pill = detectQuickScrollPill(frameRgba.data, frameRgba.width, frameRgba.height);
    framePill = pill
      ? {
          x: Math.floor(pill.x * frameRgba.width),
          y: Math.floor(pill.y * frameRgba.height),
          w: Math.ceil(pill.w * frameRgba.width),
          h: Math.ceil(pill.h * frameRgba.height),
        }
      : null;
  }
  const scrollPillRect: FracRect | null = framePill && frameRgba
    ? { x: framePill.x / frameRgba.width, y: framePill.y / frameRgba.height, w: framePill.w / frameRgba.width, h: framePill.h / frameRgba.height }
    : null;
  if (framePill) {
    // eslint-disable-next-line no-console
    if (DEX_SCAN_VERBOSE) console.log("[dex-scan] pill detected", {
      x: framePill.x, y: framePill.y, w: framePill.w, h: framePill.h,
    });
  }

  // ── NUMBER-ROI PREFETCH (scan-speed 2026-08-11) ────────────────────────────
  //
  // Decide up front WHICH cells will need a number read this frame, and read
  // them all in ONE native call — see runCellNumberOcrBatch for the measurement
  // that motivated it. This pass runs the SAME `gateCellNumberRoi` the loop below
  // runs, and the SAME band-hash/cache test, so the selected set is exactly the
  // loop's cache-miss set: no cell is read that the loop would have skipped, and
  // none is missed (a divergence would only cost that cell its own single-ROI
  // call, never a wrong read).
  //
  // It is deliberately READ-ONLY over `cellContentCache`: the loop still owns
  // every mutation (hit counts, remap deletes, entry writes) and every counter,
  // so cache accounting and telemetry are byte-for-byte what they were.
  // Keeps neither pass below hogging the main thread — see yieldToEventLoop.
  const slicer = createMainThreadSlicer();
  const yieldIfBusy = slicer.yieldIfBusy;
  // Number-ROI prefetch for one candidate lattice: gate + hash every cell, batch
  // the uncached bands through one Vision perform, and measure where each read
  // number sits in its assumed cell (the grid-phase sample). Band hashes are
  // reused by the loop rather than recomputed, so the pass costs no extra hashing.
  const prefetchFor = async (rows: typeof gridRows) => {
    const rois: FracRect[] = [];
    const cellIdx: number[] = [];
    const cells: Array<{ cell: FracRect; roi: FracRect; row: number }> = [];
    const sampleCells: FracRect[] = [];
    const hashes = new Map<number, ReturnType<typeof numberBandHash>>();
    const forceRefreshFrame = frameNumber % FORCE_REFRESH_INTERVAL === 0;
    let scanIdx = -1;
    for (const rowSpec of rows) {
      for (let col = 0; col < gridColumns; col++) {
        scanIdx += 1;
        const cell = cellRectFrac(rowSpec, col, gridColumns, layout);
        const geom = gateCellNumberRoi({
          topRowClearY: layout.topRowClearY,
          layout,
          row: rowSpec.row, cell, zones, bottomUiBand, nexusOverlayRect, pipRect, scrollPillRect,
        });
        if (!geom.accept || geom.roi == null) continue;
        await yieldIfBusy();
        const bandHash = numberBandHash(img, cell.x, cell.y, cell.w, cell.h);
        hashes.set(scanIdx, bandHash);
        const entry = bandHash != null ? cellContentCache.get(scanIdx) : undefined;
        const rectMatches = entry != null
          && entry.lastResult.rect.x === cell.x
          && entry.lastResult.rect.y === cell.y
          && entry.lastResult.rect.w === cell.w
          && entry.lastResult.rect.h === cell.h;
        const willHitCache = entry != null
          && rectMatches
          && !forceRefreshFrame
          && entry.hash === bandHash;
        if (willHitCache) continue;
        cellIdx.push(scanIdx);
        rois.push(geom.roi);
        cells.push({ cell, roi: geom.roi, row: rowSpec.row });
      }
    }
    const reads = new Map<number, CellNumberRead>();
    const samples: GridPhaseSample[] = [];
    const batch = await runCellNumberOcrBatch(img, rois);
    cellIdx.forEach((idx, i) => {
      const read = batch[i];
      if (!read) return;
      reads.set(idx, read);
      const s = phaseSampleFromBox(read.box, cells[i].roi, cells[i].cell, cells[i].row);
      if (s) {
        samples.push(s);
        sampleCells.push(cells[i].cell);
      }
    });
    // eslint-disable-next-line no-console
    if (DEX_SCAN_VERBOSE) console.log("[dex-scan] number-roi batch", {
      rois: rois.length,
      reads: reads.size,
      gridColumns,
    });
    return { rois, reads, hashes, samples, sampleCells };
  };
  // Sprite tie-break for a single-state frame (see PHASE_TIEBREAK_*): the
  // fraction of the sampled cells that carry a sprite at the assumed lattice.
  // A lattice a fraction of a cell off still leaves most of a sprite inside
  // the cell's sprite ROI, so the read is robust to the very error it resolves.
  const resolveSingleStatePhase = (
    samples: GridPhaseSample[],
    sampleCells: FracRect[],
  ): number | null => {
    if (samples.length < PHASE_TIEBREAK_MIN_SAMPLES || sampleCells.length !== samples.length) return null;
    const ys = samples.map((s) => s.cellRelY).sort((a, b) => a - b);
    if (ys[ys.length - 1] - ys[0] > PHASE_TIEBREAK_MAX_SPREAD) return null;
    const median = ys[Math.floor(ys.length / 2)];
    let withSprite = 0;
    for (const cell of sampleCells) {
      const { result } = classifyCellAt(img, cell.x, cell.y, cell.w, cell.h, frameVariant, framePill);
      if (isCellSkip(result)) continue;
      if (result.caught || result.spriteState === "caught" || result.spriteState === "silhouette") withSprite += 1;
    }
    const frac = withSprite / sampleCells.length;
    let offset: number;
    if (frac >= PHASE_TIEBREAK_SPRITE_MAJORITY) offset = median - PHASE_ANCHOR_BOTTOM_REL;
    else if (frac <= 1 - PHASE_TIEBREAK_SPRITE_MAJORITY) offset = median - PHASE_ANCHOR_CENTRE_REL;
    else return null;
    if (offset > 0.5) offset -= 1;
    if (offset < -0.5) offset += 1;
    return offset;
  };
  // EDGE-BASED PHASE FIRST (2026-09-08): the card outlines give the lattice
  // phase for any frame with a grid on it, before a single number is read, so
  // the first prefetch already runs on registered cells. The number-box
  // estimate below stays as the fallback for a frame whose outlines are
  // drowned (a fast scroll smear, a dialog over the grid).
  let appliedPhase: { offsetCells: number; source: "edges" | "edges+numbers" | "default" | "sprite-tiebreak" | "probe"; samples: number } | null = null;
  let edgeDiag: { offsetCells: number; ambiguous: boolean; margin: number } | null = null;
  const rowHeightFrac = gridRows.length > 0 ? gridRows[0].bottom - gridRows[0].top : 0;
  if (frameRgba) {
    const energy = rowEnergyProfile(frameRgba.data, frameRgba.width, frameRgba.height);
    const edge = estimateGridPhaseFromEdges(energy, frameRgba.height, gridRows, rowHeightFrac);
    edgeDiag = edge;
    if (!edge.ambiguous && Math.abs(edge.offsetCells) >= PHASE_APPLY_MIN_CELLS) {
      gridRows = dexGridRows(gridBottomAnchor, gridColumns, edge.offsetCells, layout);
      appliedPhase = { offsetCells: edge.offsetCells, source: "edges", samples: 0 };
    }
    // eslint-disable-next-line no-console
    if (DEX_SCAN_VERBOSE) console.log("[dex-scan] edge phase", edge);
  }
  let prefetch = await prefetchFor(gridRows);
  // The numbers check the outlines (2026-09-08 device log): the edge comb
  // landed a sixth of a cell off on device frames, leaving the numbers halfway
  // between the two anchors and the cells unread. When the reads on the
  // edge-shifted lattice still measure a clear residual, shift again by it.
  if (appliedPhase?.source === "edges") {
    const check = estimateGridPhase(prefetch.samples);
    const rel = !check.ambiguous
      ? check.offsetCells
      : resolveSingleStatePhase(prefetch.samples, prefetch.sampleCells);
    if (rel != null && Math.abs(rel) >= PHASE_APPLY_MIN_CELLS) {
      let total = appliedPhase.offsetCells + rel;
      if (total > 0.5) total -= 1;
      if (total < -0.5) total += 1;
      gridRows = dexGridRows(gridBottomAnchor, gridColumns, total, layout);
      prefetch = await prefetchFor(gridRows);
      appliedPhase = { offsetCells: Number(total.toFixed(3)), source: "edges+numbers", samples: check.samples };
    }
  }

  // GRID PHASE, APPLIED (2026-09-08 Perfect-dex device report). The lattice
  // used to be fixed: the phase was measured every frame (the "grid phase" log
  // below) and never used. A grid scrolled half a cell off the lattice puts a
  // CENTRED (uncaught) number outside every number band and cuts every sprite
  // ROI across two cards, so the frame reads blank. Pages full of catches
  // survive it by luck, their bottom-pinned numbers still landing in the band;
  // a sparse page like the 100% dex goes blind (716 frames, 130 with any
  // read, 16 of ~40 visible catches registered). So: estimate the phase from
  // the prefetch reads, and when the lattice is clearly off, rebuild it
  // shifted and prefetch again on the registered cells. When the default
  // lattice reads nothing at all, probe once at a half-cell shift, which is
  // exactly where its blind zone is, and register from that.
  if (appliedPhase == null) {
    const first = estimateGridPhase(prefetch.samples);
    let offset = 0;
    let source: "edges" | "edges+numbers" | "default" | "sprite-tiebreak" | "probe" = "default";
    let samples = first.samples;
    const tieBroken = first.ambiguous ? resolveSingleStatePhase(prefetch.samples, prefetch.sampleCells) : null;
    if (!first.ambiguous && Math.abs(first.offsetCells) >= PHASE_APPLY_MIN_CELLS) {
      offset = first.offsetCells;
    } else if (tieBroken != null && Math.abs(tieBroken) >= PHASE_APPLY_MIN_CELLS) {
      offset = tieBroken;
      source = "sprite-tiebreak";
    } else if (first.samples < PHASE_BLIND_MAX_SAMPLES && prefetch.rois.length >= PHASE_BLIND_MIN_ROIS) {
      const probe = await prefetchFor(dexGridRows(gridBottomAnchor, gridColumns, PHASE_PROBE_OFFSET_CELLS, layout));
      if (probe.samples.length >= PHASE_PROBE_MIN_SAMPLES) {
        const rel = estimateGridPhase(probe.samples);
        let total = PHASE_PROBE_OFFSET_CELLS + (rel.ambiguous ? 0 : rel.offsetCells);
        if (total > 0.5) total -= 1;
        offset = total;
        source = "probe";
        samples = probe.samples.length;
      }
    }
    if (offset !== 0) {
      gridRows = dexGridRows(gridBottomAnchor, gridColumns, offset, layout);
      prefetch = await prefetchFor(gridRows);
      appliedPhase = { offsetCells: Number(offset.toFixed(3)), source, samples };
    }
  }
  // eslint-disable-next-line no-console
  if (appliedPhase) console.log("[dex-scan] grid phase applied", appliedPhase);
  // One line per frame, always on (2026-09-08): what the registration saw and
  // asked for. Cheap, and the only way a device log can say why a frame
  // read nothing.
  // eslint-disable-next-line no-console
  console.log("[dex-scan] phase diag", {
    frame: frameRgba ? `${frameRgba.width}x${frameRgba.height}` : "no-rgba",
    anchor: Number(gridBottomAnchor.toFixed(3)),
    rowH: Number(rowHeightFrac.toFixed(4)),
    rows: gridRows.length,
    edge: edgeDiag,
    rois: prefetch.rois.length,
    reads: prefetch.reads.size,
    samples: prefetch.samples.length,
    cacheCleared: cacheClearedAfterBlind,
    cacheSize: cellContentCache.size,
  });
  const prefetchedReads = prefetch.reads;
  // Frame-level label consistency: the ids on one frame span a few rows, so
  // a read far from the others is a misread, and a label made only of
  // 0/1/6/8/9 whose 180-degree twin is ALSO a zero-padded label ("0060" /
  // "0900", "0090" / "0600": Vision read #060 as 900 and #090 as 600 on the
  // 2026-09-24 Purified / Perfect passes) is settled by the frame's other reads.
  const frameLabels = buildFrameLabelContext([...prefetchedReads.values()].map((r) => r.text));
  const bandHashByCell = prefetch.hashes;

  for (const rowSpec of gridRows) {
    const row = rowSpec.row;
    for (let col = 0; col < gridColumns; col++) {
      cellIdx += 1;

      // TOP GRID ROW — NOT SCANNED (2026-07-23). The Nexus PiP card floats over
      // the top of the captured screen, so row 0's sprite band is the one part of
      // the grid we cover ourselves. Two labelled device passes against the same
      // 169-catch shiny ground truth say the row is not worth reading either way:
      //
      //   guarded (evidence build): row 0 committed 1 cell out of 149, while
      //     absorbing 1351 of the run's 1607 overlay skips — ~0.7% of the value
      //     for the large majority of the wasted classification work;
      //   unguarded (single-frame build): row 0 was the single largest source of
      //     false writes, 6 of 11, at 73.9% precision against 100% for rows 2-4.
      //
      // So the row is either useless or dangerous depending on whether the
      // overlay guard is armed, and nothing real is lost by skipping it: the
      // Pokédex scrolls, so every species in row 0 arrives in rows 1-3 moments
      // later and commits there. That is why row-0 recall barely moved between
      // the two passes even though its precision differed by 26 points.
      //
      // Bail HERE, before the number ROI, cache hash, Vision OCR and pixel
      // classifier, so the row costs almost nothing (`cellRectFrac` below is pure
      // arithmetic — `gateCellNumberRoi` returns "top_row" before touching a
      // pixel). `cellIdx` is advanced first so slot numbering is unchanged and
      // telemetry stays comparable with the earlier passes
      // (row = slotIndex / gridColumns).
      const cell = cellRectFrac(rowSpec, col, gridColumns, layout);
      // The five geometric skips (top row, bottom-UI band, ROI rejection, number
      // -ROI overlay overlap, sprite-body overlay overlap) now live in one pure
      // helper so the number-ROI PREFETCH above selects exactly the cells this
      // loop goes on to read. Counters + logs stay here, unchanged.
      const geom = gateCellNumberRoi({
        topRowClearY: layout.topRowClearY,
        layout,
        row, cell, zones, bottomUiBand, nexusOverlayRect, pipRect, scrollPillRect,
      });

      if (geom.reason === "top_row") {
        topRowSkippedCells += 1;
        continue;
      }

      // BOTTOM-UI BAND (see bottomUiBand above). A cell whose sprite body sits
      // under Pokémon GO's fixed bottom controls reads flat button pixels as a
      // caught sprite. Bail HERE — before the number ROI, the exclusion-zone
      // checks, the cache hash, the Vision OCR and the pixel classifier — so a
      // below-the-line cell costs almost nothing and never clogs OCR. cellIdx is
      // already advanced (kept stable for the content cache); the cell re-reads
      // cleanly once it scrolls up out of the band. Counter only, no per-cell log
      // (it fires every frame for the bottom row — would bloat the telemetry).
      if (geom.reason === "bottom_ui_band") {
        bottomUiObscuredCells += 1;
        continue;
      }

      if (geom.reason === "roi_rejected") {
        if (geom.roiDecision?.reason === "pip_obscured") pipObscuredCells += 1;
        continue;
      }

      // Nexus overlay guard (geometry): when our own green live-scan card sits
      // over this cell's number ROI, the digits there are HUD text, not grid
      // evidence. Skip the cell (prefer a false negative — the user can hold
      // steady / scroll for an unobscured frame) and log WHY so the contamination
      // is visible in telemetry. Done before the cache + OCR so an obscured cell
      // never caches or applies.
      if (geom.reason === "number_overlay_overlap") {
        nexusOverlayObscuredCells += 1;
        // eslint-disable-next-line no-console
        if (DEX_SCAN_VERBOSE) console.log("[dex-scan] cell classify", {
          cellIdx,
          dexNumberRead: null,
          numberPosition: "no-canvas",
          classificationSkipReason: "nexus_overlay_overlap" as ClassificationSkipReason,
          spriteState: "none",
          variant: frameVariant,
          finalClassification: "skipped",
          pillDetected: framePill !== null,
          cacheHit: false,
        });
        continue;
      }

      // Nexus PiP / overlay guard (SPRITE BODY). The two guards above only
      // protect the NUMBER ROI (the digit strip low in the cell). A cell
      // scrolling under the top PiP has its number BELOW the PiP — readable —
      // but its SPRITE BODY under the PiP, so the caught/silhouette classifier
      // samples PiP pixels and false-reports "caught". This is the reported
      // device bug: uncaught/silhouette cells hitting the PiP at the top get
      // written as caught. The sprite body is the top ~62% of the cell (the
      // number lives in the bottom third — see numberRoiFrac). If that body
      // materially overlaps the PiP slab OR the green status card, SKIP the
      // whole cell: emit NO observation at all — not caught (no false write)
      // and not missing (so it can't become a false silhouette or a sticky
      // not-caught either) — and let a lower, unobscured frame classify it.
      //
      // THRESHOLD (2026-07-22, labelled shiny pass). This test used the shared
      // MAX_TOLERATED_OVERLAY_OVERLAP (0.18), and that tolerance is why the guard
      // fired ONCE in a 222-frame session while ten false writes went through it.
      // Measured from the device trace + screenshot IMG_3795: the HUD card sits at
      // y 0.122..0.217, the top lattice row's sprite band at 0.205..0.296, so the
      // card covers 13% of that band's height. Horizontally the card starts at
      // x 0.409 — no overlap with column 1, ~39% of column 2, all of columns 3-4.
      // Area overlap therefore lands at 0%, 5% and 13%: every one of them UNDER
      // 0.18, so nothing was ever skipped. The false writes that session fell in
      // columns 0/1/5/4 — the exact ordering that geometry predicts.
      //
      // 0.18 is the right tolerance for a NUMBER ROI (small centred digits; a
      // clipped rounded corner still reads). It is the wrong tolerance for a
      // sprite body, where the verdict is a statistic over the WHOLE region: 13%
      // of the band filled with our own saturated card reads as chroma 0.52-0.56
      // at ~7,700 foreground pixels, far outside the real-catch distribution
      // (median 0.127). There is no amount of our own opaque UI that belongs in a
      // sprite ROI, so the sprite body gets its own near-zero bar.
      //
      // Replayed over that trace: removes all 10 false writes, costs 5 real
      // catches, and 5 more at-risk cells survive because they were also read in
      // an uncontaminated slot. Precision 91.7% -> 100%.
      // Both rects are PADDED for this test (see OVERLAY_RECT_SAFETY_PAD): the
      // detectors measure where the HUD is opaque, and an anti-aliased edge,
      // rounded corner or drop shadow lies outside that while still polluting the
      // ROI. At a 0.02 bar a few pixels decide the outcome, so the comparison
      // uses the conservative rect. Detection geometry itself is unchanged, and
      // the number-ROI guards above still use the raw rects.
      const { spriteOverPip, spriteOverCard } = geom;
      // Non-null from here down: every reason that leaves `roi` null has already
      // `continue`d above, and the accept path always carries one.
      const roi = geom.roi!;
      if (geom.reason === "sprite_overlay_overlap") {
        nexusOverlayObscuredCells += 1;
        // eslint-disable-next-line no-console
        if (DEX_SCAN_VERBOSE) console.log("[dex-scan] cell classify", {
          cellIdx,
          dexNumberRead: null,
          numberPosition: "no-canvas",
          classificationSkipReason: "nexus_overlay_overlap" as ClassificationSkipReason,
          spriteState: "none",
          variant: frameVariant,
          finalClassification: "skipped",
          pillDetected: framePill !== null,
          cacheHit: false,
          spriteObscuredBy: spriteOverPip >= spriteOverCard ? "pip" : "card",
          spriteOverlap: Number(Math.max(spriteOverPip, spriteOverCard).toFixed(3)),
        });
        continue;
      }

      // Y6 perf: hash the cell's number band and look for a cache hit before
      // doing any heavy work. A hit replays the cached classification + OCR read
      // and skips Tesseract + the pixel classifier entirely. We force a full
      // re-classify every FORCE_REFRESH_INTERVAL frames so a stuck cache can't go
      // stale, and treat a rect change at the same cellIdx as a remap (drop the
      // entry, re-classify). When pixel data is unavailable (SSR / no canvas),
      // bandHash is null and we always run the original path.
      // Reuse the hash the prefetch already computed for this cell (it gated the
      // same cell on the same rect), falling back to a fresh hash if the prefetch
      // somehow did not reach it.
      const bandHash = bandHashByCell.has(cellIdx)
        ? bandHashByCell.get(cellIdx)!
        : numberBandHash(img, cell.x, cell.y, cell.w, cell.h);
      const forceRefresh = frameNumber % FORCE_REFRESH_INTERVAL === 0;
      const cacheEntry = bandHash != null ? cellContentCache.get(cellIdx) : undefined;
      const rectMatches = cacheEntry != null
        && cacheEntry.lastResult.rect.x === cell.x
        && cacheEntry.lastResult.rect.y === cell.y
        && cacheEntry.lastResult.rect.w === cell.w
        && cacheEntry.lastResult.rect.h === cell.h;
      if (cacheEntry != null && !rectMatches) {
        // cellIdx remap: the grid geometry shifted under this index. The cached
        // hash describes a different region now, so drop it and re-classify.
        cellContentCache.delete(cellIdx);
      }
      // A stable cell (locked after STABILITY_THRESHOLD identical-hash frames)
      // hits on the hash test too; we keep re-hashing because the band hash is
      // cheap relative to OCR + classify, but the hitCount gate is what the log
      // surfaces as "stable".
      const cacheHit = cacheEntry != null
        && rectMatches
        && !forceRefresh
        && cacheEntry.hash === bandHash;

      if (cacheHit && cacheEntry != null) {
        cacheEntry.hitCount += 1;
        cacheHits += 1;
        const r = cacheEntry.lastResult;
        // The classify log MUST still fire on a hit (with cacheHit:true) so the
        // device log can confirm the cache is working.
        // eslint-disable-next-line no-console
        if (DEX_SCAN_VERBOSE) console.log("[dex-scan] cell classify", {
          cellIdx,
          dexNumberRead: r.dexNumber,
          numberPosition: r.numberPosition,
          classificationSkipReason: r.classificationSkipReason,
          spriteState: r.spriteState,
          hueVariance: r.hueVariance,
          luminanceVariance: r.luminanceVariance,
          foregroundPixels: r.foregroundPixels,
          chromaFraction: r.chromaFraction,
          variant: frameVariant,
          finalClassification: r.finalClassification,
          pillDetected: framePill !== null,
          cacheHit: true,
          stable: cacheEntry.hitCount >= STABILITY_THRESHOLD,
        });
        cells.push({
          dexNumber: r.dexNumber,
          speciesName: r.speciesName,
          isNew: r.isNew,
          rowIndex: row,
          colIndex: col,
          caught: r.caught,
          seen: r.seen,
          spriteVerdict: r.spriteVerdict,
          spriteConfidence: r.spriteConfidence,
          spriteState: r.spriteState,
          spriteReason: r.spriteReason,
          spriteChromaFraction: r.spriteChromaFraction ?? undefined,
          spriteForegroundPixels: r.foregroundPixels ?? undefined,
          spriteForegroundFraction: r.foregroundFraction ?? undefined,
          spriteHueVariance: r.hueVariance ?? null,
          spriteBodyFrac: r.spriteBodyFrac ?? undefined,
          spriteLumStd: r.spriteLumStd ?? undefined,
          numberPosition: r.numberPosition,
          numberAnchored: r.numberAnchored ?? false,
          // Replayed verdict — same pixels, same answer. Never corroboration.
          classificationCached: true,
        });
        // PR-E6-OCR2: species OCR removed; confidence is dex OCR only.
        confSum += r.dexConf;
        confCount += 1;
        continue;
      }

      cacheMisses += 1;
      if (forceRefresh && cacheEntry != null && cacheEntry.hash === bandHash) {
        forcedRefreshes += 1;
      }

      // Hand the main thread back if this frame has been holding it for a full
      // display frame. Placed HERE — past every cheap `continue` gate and the
      // cache-hit replay, immediately before the per-cell rasterise+classify —
      // so only cells that actually do the expensive work can trigger a yield.
      // See yieldToEventLoop for why the batch made this necessary.
      await yieldIfBusy();

      // b17o step 1-4: NUMBER-ANCHOR-FIRST classification over the whole cell.
      // The classifier reads the dex number position first; only a bottom-pinned
      // number triggers the sprite (hue-variance silhouette) test. A centred
      // number is NEVER-SEEN; an absent/ambiguous number skips the cell entirely.
      const { result: classification, crop: cellCrop } = classifyCellAt(
        img, cell.x, cell.y, cell.w, cell.h, frameVariant, framePill,
      );

      // "New" badge: top-right of the cell, pixel sample for gold fill.
      const badgeLum = avgLuminance(
        img,
        cell.x + cell.w * 0.70,
        cell.y + cell.h * 0.04,
        cell.w * 0.26,
        cell.h * 0.18,
      );

      // Dex number: bottom-centre ROI (where PoGo prints "#0093").
      // PR-E6-OCR2: the per-cell species-name OCR was removed. The cheap
      // dex number read is the only OCR cost per cell now; the sprite
      // chroma matcher (below) replaces species-name as the species check.
      // scan-speed: read the ROI straight off the frame — see runCellNumberOcr
      // for why the old crop-encode-decode-encode round trip is gone.
      // Served from the one-call batch above. The `??` keeps a lone fallback read
      // for the case the prefetch did not cover this cell (a cache/geometry
      // divergence, or a batch that returned short) — correctness never depends
      // on the prefetch having been right, only speed does.
      const dexRes = prefetchedReads.get(cellIdx) ?? (await runCellNumberOcr(img, roi));
      if (isStorageBoxCellText(dexRes.rawText)) cpLabelledCells += 1;
      // VISION NUMBER-BOX ANCHOR: when the engine returned a digit bounding box
      // (Apple Vision path), its geometric vertical position is the TRUSTWORTHY
      // center-vs-bottom signal — it replaces the fragile pixel-centroid below.
      // Null on Tesseract/ML-Kit (no per-glyph geometry) → pixel-centroid path
      // is used unchanged.
      const visionNumberPosition = numberPositionFromBox(dexRes.box);
      // GRID PHASE sample. The same box, kept as a GEOMETRIC measurement rather
      // than reduced to center/bottom: the frame-level fit in dexGridPhase needs
      // where the number sits, not which side of the split it fell on. Collected
      // for every cell that produced a box, including ones that go on to be
      // skipped — a skipped cell's number still measures the lattice.
      const phaseSample = phaseSampleFromBox(dexRes.box, roi, cell, row);
      if (phaseSample) phaseSamples.push(phaseSample);
      // Pinned to null in the public PokedexCellData per PR-E6-OCR2; the
      // adapter and apply gate no longer consume a per-cell species-name.
      const speciesName: string | null = null;

      // Nexus overlay guard (hard text backstop): even if the green-card rect
      // detector missed (card parked where its tint didn't register, or a
      // partial corner under this ROI), any OCR text carrying a Nexus-HUD phrase
      // ("Added:", "Scanning", "dex entries", "NEXUS DEX", "Hold steady") is OUR
      // overlay, never a grid number. Reject it outright so #077/#025/… from the
      // "Added:" row can never become a dex observation. The digit whitelist
      // above usually strips the letters, but a partial read can still leak a
      // marker fragment, so we guard the per-cell read text here as a backstop.
      if (containsNexusOverlayMarker(dexRes.text)) {
        nexusOverlayObscuredCells += 1;
        // eslint-disable-next-line no-console
        if (DEX_SCAN_VERBOSE) console.log("[dex-scan] cell classify", {
          cellIdx,
          dexNumberRead: null,
          numberPosition: "no-canvas",
          classificationSkipReason: "nexus_overlay_overlap" as ClassificationSkipReason,
          spriteState: "none",
          variant: frameVariant,
          finalClassification: "skipped",
          pillDetected: framePill !== null,
          cacheHit: false,
        });
        continue;
      }

      // Scan overhaul v2 (2026-07-09): the Vision-only truncation width-veto
      // (box.w < 0.55 → reject) is DELETED. It was a blunt Tesseract-era-style
      // prop that discarded perfectly good Vision reads: the device log showed
      // text:"0156" at confidence 1.0 (dex #156) killed purely on box width.
      // Its real target — mid-scroll partial glyph captures — is now covered by
      // principled guards that don't throw away clean reads: the scroll-stability
      // gate skips smeared mid-scroll frames before OCR, and on a stable frame a
      // truncated digit necessarily breaks the strictly-ascending grid run
      // (a "47" wedged between #146/#148) so the ascending-neighbour sanity pass
      // (sanitizeFrameObservations) repairs or rejects it, with the region
      // cross-check dropping any surviving out-of-region id (e.g. a "941").
      // A dex label is always four digits ("0045"). A shorter digit run is a
      // label the number ROI cut through ("UU45" -> 45 for #046, "004/" -> 4
      // for #047, "UU07" -> 7 for #037, 2026-09-24 Perfect sweep): a WRONG
      // dex id, which both writes the wrong species and feeds the blank-card
      // auto-correct. The upside-down fallback still sees four digits.
      const dexNumber = FOUR_DIGIT_LABEL_RE.test(dexRes.text) ? resolveFrameDexNumber(dexRes.text, frameLabels) : null;

      // PR-E6-OCR2: per-cell sprite chroma match. When the cell pixel
      // buffer is available (browser path) and the dex number was read,
      // compare the cell's sprite area against the bundled fingerprint.
      // The matcher's three-state verdict (caught/missing/uncertain) is
      // the authoritative caught signal for the cell, replacing the prior
      // hue-variance silhouette classifier when both are available.
      //
      // The sprite occupies roughly the top 62% of the cell rect (the
      // bottom band is the dex number + species name in PoGo's render).
      // We slice the upper portion of cellCrop to avoid feeding the
      // number-band hue noise into the chroma matcher.
      let spriteVerdict: "caught" | "missing" | "uncertain" | undefined;
      let anchoredSilhouette = false;
      let spriteConfidence: number | undefined;
      let spriteChromaFraction: number | null = null;
      let spriteReason: string | null = null;
      // Scan overhaul v2 decision signals, surfaced in the cell-classify log so a
      // device scan validates the thresholds (caught lumStd >0.07, silhouette
      // <0.04; empty bodyFrac ~0 after number masking).
      let spriteLumStd: number | null = null;
      let spriteBodyFrac: number | null = null;
      /**
       * The anchored band's three-state read, when the band ran. It IS the
       * cell's sprite state: the legacy hue-variance state (classifyCellSprite
       * over the lattice ROI) called Purified #024's grey silhouette "empty"
       * at 4 of 5 lattices, and that legacy state is what the HUD colours
       * (missing + silhouette = orange) and the session's vote tiers read.
       */
      let anchoredState: SpriteState | null = null;
      /** The number mask actually handed to the classifier (null = none). */
      let numberBoxApplied: { x: number; y: number; w: number; h: number } | null = null;
      // True when the caught/blank verdict came from the card band above the
      // number Vision found (lattice-independent; the auto-correct's evidence).
      let numberAnchored = false;
      if (cellCrop && dexNumber != null) {
        // Scan overhaul v2 (Phase 2): the hue-histogram cosine-similarity
        // fingerprint matcher (matchCellToSprite) is REPLACED by the pure
        // presence-density classifier (classifyCellPresence). Vision reads the
        // dex NUMBER at ~100% confidence so we already know the species; the
        // classifier's only job is caught vs silhouette vs empty from a chroma
        // density + grey-foreground + shadow/purified aura signal. No
        // fingerprint database, no species-specific hue match, no page-tint
        // flood class. The sprite occupies the top ~62% of the cell (the bottom
        // band is the dex number + species name), so we slice the upper crop.
        const spriteH = Math.max(1, Math.floor(cellCrop.height * 0.62));
        const spriteBytes = cellCrop.data.subarray(
          0,
          spriteH * cellCrop.width * 4,
        );
        // Scan overhaul v2 (Layer A — number masking). An EMPTY cell shows ONLY
        // its dex number, and PoGo prints that number in the vertical CENTRE of a
        // never-seen card (a caught/silhouette cell has a sprite body that pushes
        // the number to the card BOTTOM, below this top-62% sprite ROI). When the
        // centred number sits INSIDE this ROI it would otherwise be counted as
        // sprite body → a false "caught". We hand the classifier a numberBox
        // covering the lower band of the ROI so it masks that number out and reads
        // the blank card as empty.
        //
        // #3 follow-up (2026-07, extended to ALL pages 2026-08-30): mask
        // UNCONDITIONALLY rather than only when visionNumberPosition === "center". That position read is
        // UNRELIABLE — the device-trace empty false-writes (#91, #153, #154) were
        // centred numbers MIS-READ as "bottom", so no mask fired, the number's
        // pixels inflated bodyFrac past the 0.012 empty floor, and cellPresence
        // returned "caught". This is exactly the "signal that does NOT depend on
        // numberPosition" the reverted 1.7.0(1) override called for (see below).
        // Masking only ever removes the ROI's LOWER band; a real catch's sprite
        // body sits higher and clears the 0.012 body floor with wide margin, so
        // masking unconditionally cannot un-catch a genuine sprite on these pages.
        //
        // Binary pages (lucky/perfect/xxs/xxl) now mask UNCONDITIONALLY too
        // (inflated-Perfect heal, 2026-08-30). They used to keep the position
        // gate for fear the mask could drop a borderline real catch under the
        // 0.13 substantial-body floor. Calibration over the real labelled
        // corpus (fixtures IMG_1200/1202 perfect, IMG_1204 xxl, IMG_1207 xxs)
        // showed the OPPOSITE: masking shrinks the bodyFrac denominator more
        // than the numerator (the sprite sits above the masked band), lifting
        // real catches OVER the floor — masked classification recovered 5/9
        // XXL and 6/11 XXS labelled catches the conditional path missed — while
        // an unmasked centred number both feeds lumStd and, with a poisoned
        // background estimate, inflates bodyFrac on EMPTY cards. The position
        // read the old gate keyed on is documented above as unreliable.
        const numberBoxMask = { x: 0.08, y: 0.6, w: 0.84, h: 0.4 };
        // Binary pages: mask where Vision SAW the number, not the fixed
        // centred band. The band (sprite-ROI y 0.6-1.0 = cell y 0.37-0.62)
        // hides the lower half of every caught sprite, so a small pale
        // catch (#056 Mankey, 2026-09-24) kept bodyFrac 0.011 < 0.012 and
        // chroma 0.0006 and never read caught. A blank card's centred
        // number is still masked, by its own box.
        let numberBox = numberBoxMask;
        if (isBinaryVariant(frameVariant) && dexRes.box) {
          const spriteH = cell.h * 0.62;
          const bx = roi.x + dexRes.box.x * roi.w;
          const by = roi.y + dexRes.box.y * roi.h;
          numberBox = {
            x: (bx - cell.x) / cell.w - 0.03,
            y: (by - cell.y) / spriteH - 0.05,
            w: (dexRes.box.w * roi.w) / cell.w + 0.06,
            h: (dexRes.box.h * roi.h) / spriteH + 0.1,
          };
        }
        // Hoisted so the classify telemetry can report the mask that was
        // ACTUALLY applied rather than re-deriving (and mis-stating) it.
        numberBoxApplied = numberBox ?? null;
        let presence = classifyCellPresence({
          spriteRgba: spriteBytes,
          width: cellCrop.width,
          height: spriteH,
          variant: frameVariant,
          numberBox,
        });
        // Binary pages: judge the band of the CARD directly above the number
        // Vision found, not the top 62% of the lattice cell (2026-09-24
        // Perfect sweep). The lattice drifts up to half a row off the cards,
        // and then the top-of-cell ROI holds the card ABOVE (its sprite and
        // number: #162 false caught at +0.4) or misses a small low sprite
        // entirely (#050 Diglett, #052 Meowth, #102, #104, #167). The number
        // is pinned to the card whatever the lattice does: a caught card's
        // number sits at its bottom, so the band above is the sprite; a blank
        // card's number is centred, so the band above is its own empty upper
        // half. Only a whole four-digit read anchors (a clipped label's box
        // is not where the label is).
        const anchoredRoi =
          dexRes.box && frameRgba && FOUR_DIGIT_LABEL_RE.test(dexRes.text)
            ? cardBandAboveNumber(frameRgba, cell, roi, dexRes.box)
            : null;
        const bandUnderPill =
          anchoredRoi != null && scrollPillRect != null &&
          overlayOverlapFraction({ x: cell.x, y: anchoredRoi.y0Frac, w: cell.w, h: anchoredRoi.hFrac }, padOverlayRect(scrollPillRect)) > 0;
        if (anchoredRoi && !bandUnderPill) {
          numberAnchored = true;
          presence = classifyCellPresence({
            spriteRgba: anchoredRoi.data,
            width: anchoredRoi.width,
            height: anchoredRoi.height,
            variant: frameVariant,
            numberAnchored: true,
          });
        }
        // REVERTED (2026-07-09): the 1.7.0(1) "centered-number-not-caught"
        // override forced every cell whose Vision number read "center" to
        // not-caught. Device telemetry proved that signal is UNRELIABLE for
        // center-vs-bottom — it fired 771×, and 728 of those were REAL caught
        // sprites (structured body) that it wrongly rejected, crashing capture
        // from 177 to 69 applied and making the scan slow/hot from re-scanning.
        // Back to trusting the body-structure classifier directly (the fast,
        // accurate pre-1.7.0(1) behaviour). The residual empty false-writes
        // (#153/#154) need a signal that does NOT depend on numberPosition — a
        // follow-up, never this override.
        //
        // Map the presence state onto the legacy three-state verdict the rest
        // of pokedexOcr + the adapter consume: caught -> "caught"; silhouette /
        // empty -> "missing" (seen-only / never-seen never write present).
        spriteVerdict = presence.state === "caught" ? "caught" : "missing";
        // A card the pill lies over is neither caught nor blank this frame.
        if (bandUnderPill) spriteVerdict = "uncertain";
        if (numberAnchored) anchoredState = presence.state;
        // A number-anchored silhouette is a SEEN card (2026-09-25 Galaxy
        // frames: every silhouette classified "silhouette" here and was then
        // folded into "missing", so seen:false reached the adapter and the
        // HUD never showed an orange chip). Carried to the seen flag below.
        anchoredSilhouette = numberAnchored && !bandUnderPill && presence.state === "silhouette";
        spriteConfidence = presence.confidence;
        spriteChromaFraction = presence.chromaDensity;
        spriteReason = presence.reason;
        spriteLumStd = presence.lumStd;
        spriteBodyFrac = presence.bodyFrac;
      }

      // b17p A2: split the old single "no-canvas" bucket. classifyCellAt returns
      // either a successful classification, or a { skipReason } marker telling us
      // WHY it produced no signal: "pos-absent" (no number anchor) vs "ctx-null"
      // (a 2D canvas context could not be created). The genuinely no-canvas
      // (non-DOM/upload) path still surfaces as classificationSkipReason: null
      // with numberPosition "no-canvas", so the three cases are distinguishable
      // in the log instead of all reading "no-canvas".
      let cls: DexCellClassification | null = isCellSkip(classification)
        ? null
        : classification;
      let classificationSkipReason: ClassificationSkipReason | null =
        isCellSkip(classification) ? classification.skipReason : null;

      // b18-pill: compute cell-local pill mask and overlap fraction for telemetry.
      const { w: iw2, h: ih2 } = imgDims(img);
      const cellPxX = Math.floor(iw2 * cell.x);
      const cellPxY = Math.floor(ih2 * cell.y);
      const cellPxW = Math.max(1, Math.floor(iw2 * cell.w));
      const cellPxH = Math.max(1, Math.floor(ih2 * cell.h));
      const cellPillMask = framePill
        ? framePillToCellPill(framePill, cellPxX, cellPxY, cellPxW, cellPxH)
        : null;
      const pillOverlap = computePillOverlapFraction(cellPillMask, cellPxW, cellPxH);

      // VISION NUMBER-BOX ANCHOR override. When Vision gave a trustworthy digit
      // box AND we read a valid dex id, the box geometry is authoritative for
      // center-vs-bottom — it supersedes the pixel-centroid (which mis-read
      // caught cells as "center" and applied 0 on-device). We overwrite the
      // existing classification's numberPosition, or synthesise a minimal
      // classification when the pixel anchor was absent/skipped, so the read
      // flows into the caught/seen decision via the box and the legacy
      // promote/recover heuristics below become no-ops on the Vision path.
      if (visionNumberPosition != null && dexNumber != null) {
        if (cls != null) {
          cls = { ...cls, numberPosition: visionNumberPosition };
        } else if (cellCrop) {
          // Anchor was absent but Vision read both the digits and their box:
          // build the same classification the bottom/center paths produce so the
          // cell is no longer silently dropped.
          // Both branches now run the real pixel classifier so foreground/hue
          // are MEASURED, not zeroed. The Stage-1 binary empty-below cross-check
          // needs a truthful foregroundPixels even on a "center" read: a centred
          // number over a non-empty cell is a CONTRADICTION (likely a misread
          // bottom), and we can only detect that if we kept the real pixel data.
          // We pass the Vision position as the anchor so numberPosition is
          // authoritative, but the sprite/foreground signals come from pixels.
          cls = classifyCellSprite(
            cellCrop.data, cellCrop.width, cellCrop.height, frameVariant,
            visionNumberPosition, cellPillMask,
          );
          classificationSkipReason = null;
        }
      }

      // b17v Fix 2: a centroid "center" verdict means never-seen, but if the
      // digit OCR read a valid dex id, a Pokémon entry demonstrably occupies the
      // slot — promote it to at least SEEN and run the sprite test to decide
      // caught, rather than emitting the blank never-seen cell.
      if (cls && cls.numberPosition === "center" && dexNumber != null && cellCrop) {
        const promotion = promoteCenterCellOnRead(
          cls, dexNumber, cellCrop.data, cellCrop.width, cellCrop.height, frameVariant, cellPillMask,
        );
        if (promotion) {
          cls = promotion.classification;
          // eslint-disable-next-line no-console
          if (DEX_SCAN_VERBOSE) console.log("[dex-scan] cell promoted by ocr read", {
            cellIdx,
            dexNumber,
            spriteCaught: promotion.spriteCaught,
            prevPosition: "center",
          });
        }
      }

      // Live-scan write-back fix: the pixel number-anchor scored ABSENT
      // (classificationSkipReason "pos-absent", cls == null) but the tight-ROI
      // OCR positively read a valid dex id and we still hold the cell crop. On
      // the live broadcast/PiP path this is the dominant case — ReplayKit
      // downscaling desaturates the "#xxxx" ink below the glyph predicate so the
      // anchor mislabels a real entry as absent, even though OCR read it. Recover
      // the classification by running the SAME sprite test the bottom-anchor path
      // uses over the crop, so numberPosition resolves to bottom (not no-canvas)
      // and the read flows into the caught/seen decision below instead of being
      // silently downgraded to "missing" and dropped before write-back.
      // True when this cell's classification came from the anchor-absent
      // recovery (cls was null until classifyReadCellWhenAnchorAbsent ran). The
      // recovery re-measures the number band; when it resolves CENTRED that is
      // the silhouette/uncaught signature, so a caught matcher verdict from a
      // bare chroma/presence match (XXL/binary silhouette false positives) must
      // NOT be promoted — we re-apply the bug-A discriminator below.
      let recoveredCentredUncaught = false;
      if (cls == null && dexNumber != null && cellCrop) {
        const recovery = classifyReadCellWhenAnchorAbsent(
          dexNumber, cellCrop.data, cellCrop.width, cellCrop.height, frameVariant, cellPillMask,
        );
        if (recovery) {
          cls = recovery.classification;
          recoveredCentredUncaught = recovery.classification.numberPosition === "center";
          // The anchor was recovered from the OCR read; clear the skip reason so
          // the telemetry shows numberPosition "bottom" / skipReason null instead
          // of the old "no-canvas" / "pos-absent" pair on a frame that did read.
          classificationSkipReason = null;
          // eslint-disable-next-line no-console
          if (DEX_SCAN_VERBOSE) console.log("[dex-scan] cell anchor recovered by ocr read", {
            cellIdx,
            dexNumber,
            spriteCaught: recovery.spriteCaught,
            prevPosition: "absent",
            recoveredNumberPosition: recovery.classification.numberPosition,
          });
        }
      }

      // step 2: an absent/ambiguous number anchor is no signal — skip the cell.
      // In a non-DOM context there is no canvas; fall back to the OCR read so the
      // upload/test path still emits cells. After the live-scan anchor recovery
      // above, a cell that had a read + crop now carries a real numberPosition.
      const numberPosition: NumberVerticalPosition | "no-canvas" =
        cls ? cls.numberPosition : "no-canvas";

      // Diagnostic (#479): the G-Max top row (#003 Venusaur / #006 Charizard /
      // #009 Blastoise) is reported as never applied. Device telemetry shows
      // those cells come back BLANK — foregroundPixels 0, spriteState "none",
      // dexNumberRead null — so OCR never even runs on them. That points
      // upstream to the row-0 crop geometry (the expanded G-Max header occluding
      // the top row, or a row offset), NOT to any write gate. Emit the exact
      // crop geometry whenever a top-row G-Max cell yields no signal so the next
      // capture pinpoints where the row-0 ROI actually landed relative to the
      // detected grid bottom and the header floor. Pure telemetry — no behaviour
      // change. Bounded to row 0 + gmax so it never spams other pages.
      if (
        frameVariant === "gmax" &&
        row === 0 &&
        dexNumber == null &&
        (cls == null || cls.spriteState === "none")
      ) {
        // eslint-disable-next-line no-console
        if (DEX_SCAN_VERBOSE) console.log("[dex-scan] gmax top-row blank crop", {
          cellIdx,
          row,
          col,
          cellFrac: {
            x: Number(cell.x.toFixed(4)),
            y: Number(cell.y.toFixed(4)),
            w: Number(cell.w.toFixed(4)),
            h: Number(cell.h.toFixed(4)),
          },
          cellPx: { x: cellPxX, y: cellPxY, w: cellPxW, h: cellPxH },
          rowTopFrac: Number(rowSpec.top.toFixed(4)),
          rowBottomFrac: Number(rowSpec.bottom.toFixed(4)),
          gridBottomAnchor: Number(gridBottomAnchor.toFixed(4)),
          headerFloor: layout.headerMaxBottom,
          foregroundPixels: cls ? cls.foregroundPixels : 0,
          numberPosition,
        });
      }

      let finalClassification: "caught" | "not_caught" | "skipped";
      let caught: boolean;
      let seen: boolean;
      // PR-E6-OCR2: the sprite chroma matcher is now the authoritative
      // caught signal when a verdict was produced (cellCrop present +
      // dex number read). Mapping:
      //   caught    -> caught:true, seen:true
      //   missing   -> caught:false, seen:false (no sprite, no silhouette)
      //   uncertain -> three-state variants fall back to cls (silhouette =
      //                seen). BINARY variants (lucky, perfect, xxs, xxl)
      //                have no silhouette state, so an uncertain matcher
      //                verdict resolves directly to not-caught; we never
      //                promote to seen via cls for these dexes.
      const binaryVariant = isBinaryVariant(frameVariant);
      // b18 Bug-A gate: in PoGo a CENTRED dex number (no sprite above it) means
      // the entry is registered-but-not-caught; a BOTTOM number under a sprite
      // means caught. An empty/never-seen cell shows the number centred. The
      // chroma matcher can still emit "caught" on such a cell by matching the
      // flat cell background (histSim noise on blue-ish empties). When the pixel
      // classifier positively says the number is CENTRED (numberPosition
      // "center"), that is authoritative evidence of NOT-caught, so a bare
      // chroma-match must NOT override it. We only let a "caught" sprite verdict
      // win when the number geometry is consistent with a caught cell (bottom,
      // no-canvas/upload path, or the matcher cleared on real sprite histogram).
      const numberSaysUncaught =
        cls != null && cls.numberPosition === "center";
      // Recovery false-positive gate: on the anchor-absent recovery path a
      // CENTRED number is the silhouette/uncaught signature, so a caught matcher
      // verdict (chroma-match OR local-presence/aura against a silhouette shape,
      // the XXL/binary case) must be rejected — the number geometry wins. This
      // closes the #439 residual: the recovery used to force a "bottom" anchor,
      // making the bug-A gate above a no-op for recovered cells.
      const recoveryCaughtRejected =
        recoveredCentredUncaught && spriteVerdict === "caught";
      // Scan overhaul v2 (2026-07-08) — number geometry is the SOLE caught veto.
      // A CENTRED dex number (numberPosition "center", or the anchor-absent
      // recovery equivalent) is the registered-but-uncaught silhouette signature
      // and is the one genuinely Vision-derived never-seen signal, so it
      // authoritatively blocks a caught verdict. The legacy spriteState /
      // histSim veto arms (silhouetteStateRejectsCaught, localPresenceZeroHistSim,
      // histPresenceFloorDemotes, and bug-A's spriteStateEmpty branch) were
      // removed: the cellPresence chroma-density verdict is now authoritative for
      // caught, guarded downstream by the two-frame same-slot confirmation.
      // Geometry-only signal (center read alone) — kept for telemetry so we can
      // see when Option C's foreground corroboration SPARED a real catch.
      if (anchoredState != null && cls) {
        cls = { ...cls, spriteState: anchoredState };
      }
      const bugAGeometryVeto = bugAGateRejectsCaught({
        spriteVerdict,
        numberSaysUncaught,
        recoveredCentredUncaught,
      });
      // Option C (2026-07-08): the non-binary center-veto now ALSO requires
      // foreground to corroborate an EMPTY cell (foregroundPixels <=
      // CENTER_VETO_FG_FLOOR). A caught cell whose number misreads as "center"
      // during scroll carries a real sprite (high foreground) and survives; an
      // unknown foreground defers to the matcher (recall-safe). This applies to
      // BOTH the direct center read and the recovery-rejected path (both fold
      // into geometryVeto inside the gate).
      const bugAReject = bugAGateRejectsCaught({
        spriteVerdict,
        numberSaysUncaught,
        recoveredCentredUncaught,
        requireLowForeground: true,
        foregroundPixels: cls?.foregroundPixels,
        foregroundFraction: cls?.foregroundFraction,
      });
      // BINARY-VARIANT VETO BYPASS (Apple Vision simplification). For binary
      // variants (lucky/perfect/xxs/xxl) there is no silhouette and the
      // caught/empty split is cleanly carried by the matcher’s structural
      // presence gate (chromaForegroundPixels > 0). The histSim/number-centroid
      // vetoes below are noise on these dexes (telemetry: real XXL catches at
      // histSim 0.08–0.31 were all wrongly demoted), so we skip ALL THREE for
      // binaryVariant and trust the matcher verdict directly.
      if (!binaryVariant && bugAReject) {
        // Number centred (uncaught) + a caught sprite verdict -> reject the false
        // positive, trust the number geometry. A centred number is the
        // registered-but-uncaught silhouette: not caught AND not seen, never
        // CAUGHT. This is the sole classification-layer caught veto now.
        caught = false;
        seen = false; // centred number = registered-but-uncaught silhouette = not-seen, for both the recovery-rejected and bug-A center cases
        finalClassification = "not_caught";
        // eslint-disable-next-line no-console
        if (DEX_SCAN_VERBOSE) console.log("[dex-scan] bug-A gate: caught rejected (number centred)", {
          cellIdx, dexNumber, spriteReason,
          recovered: recoveryCaughtRejected,
        });
      } else if (spriteVerdict === "caught") {
        // The chroma-density verdict is AUTHORITATIVE for caught; the session's
        // two-frame same-slot confirmation is the false-write guard (this is the
        // 2026-07-08 scan-overhaul-v2 contract — see the comment above bugAGeometry).
        //
        // FLAT-CELL VETO REMOVED (2026-07): a hard-veto added 2026-07-18 rejected
        // a chroma-caught cell whenever its hue AND luminance variance read ~0, on
        // the theory that "no sprite can be flat". Device trace
        // 20260718T121520 disproved that — real catches #13/#19/#53/#56/#72 read
        // hueVariance 0 / luminanceVariance 0 at spriteConfidence 1.0 and were
        // dropped into scan gaps. The reason: hueVariance is 0 for ANY achromatic
        // (grey/dark) sprite, not just empty cells — ocrPixelOps leaves it at its
        // 0 initial value whenever the foreground carries no chromatic pixels
        // (chroma === 0), which its own comment documents ("a near-pure grey
        // silhouette … variance 0"). So the flat signal cannot distinguish a grey
        // sprite from an empty cell, and must not override a confident chroma
        // verdict. We keep the catch and let corroboration reject a genuinely-
        // empty #91 (which won't read caught across two consecutive frames),
        // logging the flat case so device runs can still quantify any false-write
        // it lets through.
        if (DEX_SCAN_VERBOSE && isFlatEmptyCell(cls)) {
          // eslint-disable-next-line no-console
          console.log("[dex-scan] flat-cell allowed (chroma-caught, corroboration guards)", {
            cellIdx,
            dexNumber,
            spriteReason,
            spriteConfidence,
            hueVariance: cls ? Number(cls.hueVariance.toFixed(4)) : null,
            luminanceVariance: cls ? Number(cls.luminanceVariance.toFixed(5)) : null,
            numberPosition,
          });
        }
        // Option C: if the number read centred (geometry veto WOULD have fired)
        // but foreground corroborated a real sprite, we intentionally spared the
        // catch. Trace it so device runs can confirm the mitigation is working.
        // eslint-disable-next-line no-console
        if (DEX_SCAN_VERBOSE && !binaryVariant && bugAGeometryVeto && !bugAReject) {
          console.log("[dex-scan] bug-A gate: veto skipped (foreground corroborates sprite)", {
            cellIdx,
            dexNumber,
            foregroundPixels: cls?.foregroundPixels,
          });
        }
        caught = true;
        seen = true;
        finalClassification = "caught";
      } else if (spriteVerdict === "missing") {
        caught = false;
        // Silhouette on a three-state page = registered, not caught.
        seen = anchoredSilhouette && !binaryVariant;
        finalClassification = "not_caught";
      } else if (binaryVariant) {
        // Binary variant + uncertain matcher: no silhouette state exists,
        // so anything that did not clear the chroma+histogram bar is just
        // not-caught for this dex.
        caught = false;
        seen = false;
        finalClassification = "not_caught";
      } else if (cls) {
        // Three-state variant: trust the legacy pixel classifier for the
        // silhouette/seen-only case.
        caught = cls.caught;
        seen = cls.seen;
        finalClassification = caught ? "caught" : "not_caught";
      } else {
        // No matcher verdict, no classifier signal: emit a conservative
        // seen-only read so existing consumers are unaffected. Matches the
        // legacy upload-path stance.
        caught = false;
        seen = true;
        finalClassification = "not_caught";
      }

      // b17o step 1: when the pixel anchor says the number is absent/ambiguous
      // AND OCR also read nothing, there is no cell to emit.
      if (cls == null && !dexNumber && !speciesName) continue;
      if (cls && cls.numberPosition === "bottom" &&
          !dexNumber && !speciesName) {
        // A bottom number that OCR could not read still carries no id to apply.
        continue;
      }

      // b17o per-cell debug log. The user reads these in paste.txt during device
      // testing; one structured line per classified cell.
      // PR-E6-OCR2: log the sprite matcher fields alongside the legacy
      // pixel-classifier fields so device traces can confirm both signals.
      // eslint-disable-next-line no-console
      if (DEX_SCAN_VERBOSE) console.log("[dex-scan] cell classify", {
        cellIdx,
        dexNumberRead: dexNumber,
        numberPosition,
        classificationSkipReason,
        spriteState: cls ? cls.spriteState : "none",
        hueVariance: cls ? Number(cls.hueVariance.toFixed(3)) : null,
        luminanceVariance: cls
          ? Number(cls.luminanceVariance.toFixed(4)) : null,
        foregroundPixels: cls ? cls.foregroundPixels : null,
        foregroundFraction: cls ? cls.foregroundFraction : null,
        chromaFraction: cls && cls.chromaFraction != null
          ? Number(cls.chromaFraction.toFixed(4)) : null,
        variant: frameVariant,
        finalClassification,
        pillDetected: framePill !== null,
        pillOverlapFraction: Number(pillOverlap.toFixed(4)),
        cacheHit: false,
        spriteVerdict: spriteVerdict ?? null,
        spriteConfidence: spriteConfidence != null
          ? Number(spriteConfidence.toFixed(3)) : null,
        spriteChromaFraction: spriteChromaFraction != null
          ? Number(spriteChromaFraction.toFixed(4)) : null,
        spriteReason,
        spriteLumStd: spriteLumStd != null ? Number(spriteLumStd.toFixed(4)) : null,
        spriteBodyFrac: spriteBodyFrac != null ? Number(spriteBodyFrac.toFixed(4)) : null,
        // TELEMETRY TRUTH (2026-07-20). `numberMasked` used to be
        // `visionNumberPosition === "center"`, which is NOT what it says: on a
        // non-binary page the mask is applied UNCONDITIONALLY, so the field read
        // false on every shiny cell while masking had in fact happened. Reading
        // a device log, that inverts the diagnosis of any false write — it cost
        // real analysis time on the #90/#128 traces. Report the mask that was
        // actually handed to the classifier, and log the position test under its
        // own name so both signals stay available and neither impersonates the
        // other.
        numberMasked: numberBoxApplied != null,
        numberMaskBand: numberBoxApplied
          ? `${numberBoxApplied.y.toFixed(2)}..${(numberBoxApplied.y + numberBoxApplied.h).toFixed(2)}`
          : null,
        visionNumberPosition: visionNumberPosition ?? null,
        // Which path decided the position: Vision's digit box (trustworthy) or
        // the pixel-centroid heuristic (known fragile). A false write traced to
        // the heuristic is a different bug from one traced to Vision.
        numberPositionSource: visionNumberPosition != null ? "vision-box" : "pixel-centroid",
      });

      const isNew = badgeLum > 180;
      cells.push({
        dexNumber,
        speciesName,
        isNew,
        rowIndex: row,
        colIndex: col,
        caught,
        seen,
        spriteVerdict,
        spriteConfidence,
        spriteState: cls ? cls.spriteState : "none",
        spriteReason,
        spriteChromaFraction: spriteChromaFraction ?? undefined,
        spriteForegroundPixels: cls ? cls.foregroundPixels : undefined,
        spriteForegroundFraction: cls ? cls.foregroundFraction : undefined,
        spriteHueVariance: cls ? cls.hueVariance : null,
        spriteBodyFrac: spriteBodyFrac ?? undefined,
        spriteLumStd: spriteLumStd ?? undefined,
        pillOverlapFraction: pillOverlap,
        classificationCached: false,
        numberPosition,
        numberAnchored,
      });
      confSum += dexRes.conf;
      confCount += 1;

      // Y6 perf: store the fresh result so the next frame can replay it when the
      // number band is unchanged. A miss resets hitCount to 0; a forced refresh
      // that found the same hash keeps the cell counting toward stable. We round
      // the logged numerics here so a hit replays identical log values.
      if (bandHash != null) {
        const prev = cellContentCache.get(cellIdx);
        const sameHash = prev != null && prev.hash === bandHash && rectMatches;
        cellContentCache.set(cellIdx, {
          hash: bandHash,
          hitCount: sameHash ? prev.hitCount : 0,
          lastResult: {
            dexNumber,
            speciesName,
            classification: cls,
            classificationSkipReason,
            numberPosition,
            numberAnchored,
            spriteState: cls ? cls.spriteState : "none",
            hueVariance: cls ? Number(cls.hueVariance.toFixed(3)) : null,
            luminanceVariance: cls ? Number(cls.luminanceVariance.toFixed(4)) : null,
            foregroundPixels: cls ? cls.foregroundPixels : null,
            foregroundFraction: cls ? cls.foregroundFraction : null,
            chromaFraction: cls && cls.chromaFraction != null
              ? Number(cls.chromaFraction.toFixed(4)) : null,
            finalClassification,
            caught,
            seen,
            isNew,
            dexConf: dexRes.conf,
            // PR-E6-OCR2: species OCR removed; field kept at 0 for cache
            // back-compat. The matcher's spriteConfidence is the new signal.
            speciesConf: 0,
            spriteVerdict,
            spriteConfidence,
            spriteReason,
            spriteChromaFraction: spriteChromaFraction != null
              ? Number(spriteChromaFraction.toFixed(4)) : null,
            spriteBodyFrac: spriteBodyFrac != null
              ? Number(spriteBodyFrac.toFixed(4)) : null,
            spriteLumStd: spriteLumStd != null
              ? Number(spriteLumStd.toFixed(4)) : null,
            rect: { x: cell.x, y: cell.y, w: cell.w, h: cell.h },
          },
        });
      }
    }
  }

  // Y6 perf: per-frame cache summary so the device log can confirm the cache is
  // doing real work (hit rate should climb above ~70% within a few frames of a
  // steady scroll). totalCells counts only ROIs that cleared acceptCellNumberRoi
  // (where a hit or miss was actually decided), matching hits + misses.
  const totalCells = cacheHits + cacheMisses;
  // eslint-disable-next-line no-console
  if (DEX_SCAN_VERBOSE) console.log("[dex-scan] frame perf", {
    totalCells,
    cacheHits,
    cacheMisses,
    hitRate: totalCells > 0 ? Number((cacheHits / totalCells).toFixed(3)) : 0,
    forcedRefreshes,
  });

  // GRID PHASE — the registration measurement. Logged unconditionally (not behind
  // DEX_SCAN_VERBOSE) because it is ONE line per frame and it is the question the
  // next device pass exists to answer: is the lattice actually off, by how much,
  // and does the offset track grid row 1 the way the labelled pass implies?
  //
  // `perRow` is the important column. A whole-grid offset (every row equally off)
  // is a scroll-phase error and is correctable by shifting the lattice. A row-1
  // offset with rows 2-4 near zero is instead a header-clip/pitch error, and the
  // fix is the row bounds, not the phase. The two have different repairs, so the
  // data has to say which before either is written.
  const gridPhase = estimateGridPhase(phaseSamples);
  const perRow: Record<string, number> = {};
  for (const r of new Set(phaseSamples.map((s) => s.row))) {
    const ys = phaseSamples.filter((s) => s.row === r).map((s) => s.cellRelY).sort((a, b) => a - b);
    perRow[`r${r}`] = Number(ys[Math.floor(ys.length / 2)].toFixed(3));
  }
  // eslint-disable-next-line no-console
  console.log("[dex-scan] grid phase", {
    offsetCells: Number(gridPhase.offsetCells.toFixed(3)),
    // The correction the lattice ran under this frame (null = unshifted);
    // with it applied the residual estimate above should read near zero.
    applied: appliedPhase,
    ambiguous: gridPhase.ambiguous,
    samples: gridPhase.samples,
    residual: gridPhase.residual,
    explained: gridPhase.explained,
    // Median measured number position per lattice row. Aligned + caught reads
    // ~0.85, aligned + uncaught ~0.50; anything between is the lattice error.
    perRow,
    gridBottom: Number(gridBottomAnchor.toFixed(3)),
  });

  const confidence = confCount > 0 ? Math.round(confSum / confCount) : 0;
  if (confidence < 50) warnings.push("low_confidence");

  // Region authority = trusted dex numbers (device 2026-06-28). The header
  // chrome resolution above (classifyRegion -> tab -> column -> top panel) is
  // noisy and the Nexus overlay can garble it to "unknown" even on a clean page.
  // The cell dex numbers we just read are the 100pct-confidence signal, so a
  // GATED majority vote over them (regionFromReadIds) takes authority: it
  // overrides a chrome read ONLY when the numbers form a confident,
  // false-positive-guarded consensus. When the numbers are sparse / torn /
  // scattered the vote returns "unknown" and the chrome region stands. This
  // removes the two-resolver drift (chrome here vs dexIdToRegion in
  // dexPipelineRunner) that left flawless reads stuck at region:"unknown".
  // G-Max exemption: the G-Max roster is cross-region by design (Kanto #3/#6/#9,
  // Alola #770, Johto #212, etc.), so a majority vote over its cell numbers
  // returns an inconsistent / wrong single region and would then drive the
  // cross-check to drop the whole page. Skip the vote entirely on G-Max frames.
  if (frameVariant === "gmax") {
    // eslint-disable-next-line no-console
    if (DEX_SCAN_VERBOSE) console.log("[dex-scan] region-from-numbers skipped (gmax cross-region)", {
      activeRegion,
      regionReason,
    });
  } else {
    const numberRegion = regionFromReadIds(cells.map((c) => c.dexNumber));
    if (numberRegion !== "unknown" && numberRegion !== activeRegion) {
      // eslint-disable-next-line no-console
      if (DEX_SCAN_VERBOSE) console.log("[dex-scan] region from numbers", {
        chromeRegion: activeRegion,
        chromeReason: regionReason,
        numberRegion,
        votes: cells.filter((c) => c.dexNumber != null).length,
      });
      activeRegion = numberRegion;
      regionReason = "dex_numbers";
      const idx = warnings.indexOf("active_region_unknown");
      if (idx >= 0) warnings.splice(idx, 1);
    }
  }

  if (cells.length === 0 && progressTotal === null) {
    warnings.push("not_pokedex_screenshot");
  }
  // THE STORAGE BOX (2026-09-18 owner report): a search-filtered box wrote
  // #034 / #221 / #190 into the Purified dex. Its "CP 282" labels sit where a
  // dex cell prints "#0282", and the digit whitelist made them dex numbers.
  // Three or more cells still carrying the "CP" in their unfiltered text is
  // the box; nothing on it is a dex observation, and the frame gate drops a
  // frame flagged not_pokedex_screenshot before anything applies.
  if (cpLabelledCells >= STORAGE_BOX_MIN_CELLS) {
    cells.length = 0;
    if (!warnings.includes("not_pokedex_screenshot")) warnings.push("not_pokedex_screenshot");
    warnings.push("storage_box");
  }

  // [dex-scan] probe: per-cell OCR finished for this frame. b13c.4 corrects the
  // fields that were previously misleading:
  //   - `cells` = ROIs that produced a number or species (out of 16 swept).
  //   - `withNumber` = cells that yielded a parsed dex number (the old `caught`
  //      field was THIS count mislabelled, which is why it never matched the
  //      user's actual caught total).
  //   - `caught` = cells whose sprite area reads as a colourful (caught) sprite,
  //      the real caught estimate.
  // If `cells` logs ≪ 16 with the geometry log showing 16 totalCells, the ROIs
  // are landing off the numbers (geometry), not a loop-bound bug.
  // eslint-disable-next-line no-console
  extractLastNumbersRead = cells.filter((c) => c.dexNumber != null).length;
  if (DEX_SCAN_VERBOSE) console.log("[dex-scan] cell ocr", {
    cells: cells.length,
    withNumber: cells.filter((c) => c.dexNumber != null).length,
    caught: cells.filter((c) => c.caught).length,
    region: activeRegion,
    confidence,
    nexusOverlayObscured: nexusOverlayObscuredCells,
    topRowSkipped: topRowSkippedCells,
  });

  return {
    activeRegion,
    activeFilter,
    progressSeen,
    progressTotal,
    cells,
    confidence,
    warnings,
    regionReason,
    pipObscuredCells,
    nexusOverlayObscuredCells,
    bottomUiObscuredCells,
    topRowSkippedCells,
    numberRoiBatchSize: prefetch.rois.length,
    cellLoopYieldMs: slicer.yieldedMs(),
    topPanel,
    variantChip,
    colourNarrowedVariant,
    pageColourFamily,
    gridColumns,
    // Run-3: the global header/region/filter OCR text, so the write chokepoint
    // can veto a frame whose OCR sampled the Nexus overlay/status HUD even when
    // no cell ROI geometrically overlapped the card.
    globalOcrText: [
      labelText.text,
      tabText.text,
      progressText.text,
      variantRowText.text,
      filterText.text,
    ]
      .filter((t): t is string => typeof t === "string" && t.length > 0)
      .join(" "),
  };
}

/**
 * Merge a sequence of `PokedexGridFrame`s (the user scrolled and captured
 * multiple screenshots) into a single deduped frame, per spec §6.8. Cells
 * are keyed by dexNumber; when a dex number appears in multiple frames we
 * keep the cell with the higher OCR confidence (approximated by the
 * frame's average confidence since we do not score per-cell).
 */
/**
 * Push 4 (v1.4.1 b8): explicit barrier guard.
 *
 * The Pokédex OCR payload must NEVER become an inventory/instance record.
 * A Pokédex cell carries only Dex-state ({ dexNumber, speciesName, isNew })
 * — no CP, no IV, no nickname, no slot id. Inventory records carry
 * per-instance fields (CP, IV%, instanceId, flags). If a Pokédex frame
 * somehow inherits any of those, this guard returns `false` and the live-
 * scan path refuses to forward it. Pure helper; safe to call from tests
 * and production.
 */
export function isPokedexPayloadSafe(payload: unknown): boolean {
  if (!payload || typeof payload !== "object") return false;
  const p = payload as Record<string, unknown>;
  if (!Array.isArray(p.cells)) return false;
  // Reject if any inventory-shaped field exists at the top level.
  const inventoryFields = [
    "cp", "ivPercent", "instanceId", "speciesId", "flags",
    "nickname", "captureMethod", "tradePartner", "iv",
  ];
  for (const f of inventoryFields) {
    if (f in p && p[f] !== null && p[f] !== undefined) return false;
  }
  // And reject if any cell carries inventory-shaped fields.
  for (const cell of p.cells as unknown[]) {
    if (!cell || typeof cell !== "object") return false;
    const c = cell as Record<string, unknown>;
    for (const f of inventoryFields) {
      if (f in c && c[f] !== null && c[f] !== undefined) return false;
    }
  }
  return true;
}

/**
 * Push 4 (v1.4.1 b8): strip a Pokédex frame down to its Dex-state-only
 * surface. Defensive helper for cases where a future change accidentally
 * threads extra fields onto a frame; the live-scan path can call this
 * to enforce the barrier without rejecting otherwise-good data.
 */
export function strictPokedexFrame(frame: PokedexGridFrame): PokedexGridFrame {
  return {
    activeRegion: frame.activeRegion,
    activeFilter: frame.activeFilter,
    progressSeen: frame.progressSeen,
    progressTotal: frame.progressTotal,
    cells: frame.cells.map((c) => ({
      dexNumber: c.dexNumber,
      speciesName: c.speciesName,
      isNew: !!c.isNew,
      rowIndex: c.rowIndex,
      colIndex: c.colIndex,
      caught: !!c.caught,
    })),
    confidence: frame.confidence,
    warnings: [...frame.warnings],
  };
}

export function mergePokedexFrames(frames: PokedexGridFrame[]): PokedexGridFrame {
  if (frames.length === 0) {
    return {
      activeRegion: "unknown",
      activeFilter: "seen",
      progressSeen: null, progressTotal: null,
      cells: [], confidence: 0, warnings: [],
    };
  }
  const seen = new Map<number, { cell: PokedexCellData; frameConf: number }>();
  const sansDex: PokedexCellData[] = [];
  for (const frame of frames) {
    for (const cell of frame.cells) {
      if (cell.dexNumber == null) { sansDex.push(cell); continue; }
      const prev = seen.get(cell.dexNumber);
      if (!prev || frame.confidence > prev.frameConf) {
        seen.set(cell.dexNumber, { cell, frameConf: frame.confidence });
      }
    }
  }
  // Region + filter + progress: use the highest-confidence frame's values
  // as the canonical answer.
  const best = frames.slice().sort((a, b) => b.confidence - a.confidence)[0];
  return {
    activeRegion: best.activeRegion,
    activeFilter: best.activeFilter,
    progressSeen: best.progressSeen,
    progressTotal: best.progressTotal,
    cells: Array.from(seen.values()).map((v) => v.cell).concat(sansDex),
    confidence: Math.round(frames.reduce((s, f) => s + f.confidence, 0) / frames.length),
    warnings: Array.from(new Set(frames.flatMap((f) => f.warnings))),
  };
}
